import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Spinner,
  Table,
  Badge,
  Nav,
  Tab,
  Dropdown,
  Modal,
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { LineChart, BarChart, PieChart, FunnelChart, GaugeChart } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
  TitleComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import budgetService from '../../../services/budgetService';
import { useAuth } from '../../../contexts/AuthContext';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Select, { components as rsComponents } from 'react-select';
import '../../../styles/react-select.css';
import ObjectivesSummaryWidget from '../objectives/ObjectivesSummaryWidget';
import objectivesService from '../../../services/objectivesService';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

// Multi-select de campaña (Overview): muestra a lo sumo N chips en UNA línea y
// colapsa el resto en "+N más", así los chips nunca se desbordan del selector.
const MAX_CAMPAIGN_CHIPS = 2;
const CampaignMultiValue = (props) => {
  const values = props.getValue();
  const idx = values.indexOf(props.data);
  if (idx < MAX_CAMPAIGN_CHIPS) return <rsComponents.MultiValue {...props} />;
  if (idx === MAX_CAMPAIGN_CHIPS) {
    return (
      <div style={{
        backgroundColor: '#e0e7ff', color: '#3730a3', borderRadius: 6,
        fontSize: 11, fontWeight: 600, padding: '2px 8px', margin: 2, whiteSpace: 'nowrap',
      }}>
        +{values.length - MAX_CAMPAIGN_CHIPS} más
      </div>
    );
  }
  return null;
};

/** Fecha local YYYY-MM-DD (evita desfase de toISOString en UTC-5). */
const toLocalDateString = (d) => {
  if (!d) return '';
  const date = d instanceof Date ? d : new Date(d);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// Registrar componentes de ECharts
echarts.use([
  LineChart,
  BarChart,
  PieChart,
  FunnelChart,
  GaugeChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  TitleComponent,
  CanvasRenderer,
]);

const getCreativeProxyUrl = (ad) => {
  if (!ad?.adKey) return null;
  const base = process.env.REACT_APP_API_URL || (
    window.location.hostname.includes('.herrerahavas.')
      ? `${window.location.protocol}//${window.location.hostname}:3001/api`
      : 'http://localhost:3001/api'
  );
  return `${base}/budget/creative-image/${ad.adKey}`;
};

// Formatters
const formatNumber = (num) => {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num?.toLocaleString() || '0';
};

const formatNumberFull = (num) => {
  return (num || 0).toLocaleString('es-MX');
};

const formatCurrency = (num) => {
  return '$' + (num || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatPercent = (num) => {
  return (num || 0).toFixed(2) + '%';
};

// Exporta TODO el tablero a un único Excel con varias hojas
const exportFullDashboard = ({
  dateFrom, dateTo, kpis, kpisByObjective, data, jitData, waData,
  complianceData, clientName, filters = {},
}) => {
  try {
    const wb = XLSX.utils.book_new();
    const fmtDate = (d) => toLocalDateString(d);
    const safe = (v) => (v == null ? '' : v);
    const num = (v) => (v == null || v === '' ? 0 : Number(v));

    const addSheet = (name, rows, headerOrder = null) => {
      if (!rows || rows.length === 0) return;
      const ws = XLSX.utils.json_to_sheet(rows, headerOrder ? { header: headerOrder } : undefined);
      const headers = headerOrder || Object.keys(rows[0] || {});
      ws['!cols'] = headers.map(h => ({ wch: Math.max(String(h).length + 2, 14) }));
      XLSX.utils.book_append_sheet(wb, ws, name.slice(0, 31));
    };

    // Hoja: Resumen general con KPIs principales y filtros aplicados
    addSheet('Resumen', [{
      Cliente: clientName,
      'Fecha desde': fmtDate(dateFrom),
      'Fecha hasta': fmtDate(dateTo),
      'Filtros - Cuenta': filters.accounts || 'Todas',
      'Filtros - Producto': filters.productType || 'Todos',
      'Filtros - Objetivo': filters.campaignObjective || 'Todos',
      'Filtros - Plataforma': filters.platform || 'Todas',
      'Inversión Total': num(kpis?.cost),
      Impresiones: num(kpis?.impressions),
      Reach: num(kpis?.reach),
      Clicks: num(kpis?.clicks),
      'Link Clicks': num(kpis?.linkClicks),
      'Video Views': num(kpis?.videoViews),
      'ThruPlay (Video)': num(kpis?.videoThruPlay),
      Leads: num(kpis?.leads),
      'WhatsApp Conv.': num(kpis?.whatsappConversations),
      CPM: num(kpis?.cpm),
      CPC: num(kpis?.cpc),
      CPL: num(kpis?.cpl),
      CTR: num(kpis?.ctr),
      'Frecuencia': num(kpis?.frequency),
      'Coste/Conversación': num(kpis?.costPerWhatsappConversation),
    }]);

    if (kpisByObjective && Object.keys(kpisByObjective).length > 0) {
      const objRows = Object.entries(kpisByObjective).map(([obj, v]) => ({
        Objetivo: obj,
        Inversión: num(v?.cost),
        Impresiones: num(v?.impressions),
        Clicks: num(v?.clicks),
        Leads: num(v?.leads),
        'WhatsApp Conv.': num(v?.conversations ?? v?.whatsappConversations),
        CPL: num(v?.cpl),
        CPC: num(v?.cpc),
        CPM: num(v?.cpm),
        CTR: num(v?.ctr),
      }));
      addSheet('KPIs por Objetivo', objRows);
    }

    if (complianceData?.objectives?.length) {
      addSheet('Real vs Objetivo', complianceData.objectives.map(o => ({
        Cuenta: o.accountName, Objetivo: o.objective, Producto: o.productType,
        Plataforma: o.platform, 'Presupuesto Plan': num(o.plannedBudget),
        'Inversión Real': num(o.actualSpend), 'Cumplimiento %': num(o.compliancePct),
      })));
    }

    if (data?.evolutive?.length || data?.dailyTrend?.length) {
      const evo = data.evolutive || data.dailyTrend || [];
      addSheet('Evolutivo Diario', evo.map(d => ({
        Fecha: d.dateKey || d.date,
        Impresiones: num(d.impressions),
        Clicks: num(d.clicks),
        'Link Clicks': num(d.linkClicks),
        Leads: num(d.leads),
        Inversión: num(d.cost),
        'WhatsApp Conv.': num(d.whatsappConversations),
        'Coste/Conv.': num(d.costPerWhatsappConversation),
        CPM: num(d.cpm),
        CPC: num(d.cpc),
        CPL: num(d.cpl),
        CTR: num(d.ctr),
      })));
    }

    if (data?.campaigns?.length) {
      const treeRows = buildTreeExportRows(data.campaigns, data.adGroups, data.ads);
      addSheet('Árbol Campañas', treeRows);

      addSheet('Campañas', data.campaigns.map(c => ({
        Cuenta: c.accountName, Campaña: c.campaignName, 'ID Campaña': c.campaignId || c.campaignKey,
        Plataforma: c.sourcePlatform, Producto: c.productType,
        Inversión: num(c.cost), Impresiones: num(c.impressions), Reach: num(c.reach),
        Clicks: num(c.clicks), 'Link Clicks': num(c.linkClicks),
        Leads: num(c.leads), 'WhatsApp Conv.': num(c.whatsappConversations),
        CPM: num(c.cpm), CPC: num(c.cpc), CPL: num(c.cpl), CTR: num(c.ctr),
        'Coste/Conv.': num(c.costPerWhatsapp),
      })));
    }

    if (data?.adGroups?.length) {
      addSheet('Conjuntos de Anuncios', data.adGroups.map(g => ({
        Cuenta: g.accountName, Campaña: g.campaignName, 'Conjunto': g.adGroupName,
        Plataforma: g.sourcePlatform, Inversión: num(g.cost),
        Impresiones: num(g.impressions), Clicks: num(g.clicks),
        Leads: num(g.leads), 'WhatsApp Conv.': num(g.whatsappConversations),
        CPM: num(g.cpm), CPC: num(g.cpc), CPL: num(g.cpl), CTR: num(g.ctr),
      })));
    }

    if (data?.ads?.length) {
      addSheet('Anuncios', data.ads.map(a => ({
        Cuenta: a.accountName, Campaña: a.campaignName, Conjunto: a.adGroupName, Anuncio: a.adName || adDisplayName(a),
        'ID Anuncio': a.adId || a.adKey, Plataforma: a.sourcePlatform,
        Inversión: num(a.cost), Impresiones: num(a.impressions), Clicks: num(a.clicks),
        Leads: num(a.leads), 'WhatsApp Conv.': num(a.whatsappConversations),
        'Coste/Conv.': num(a.costPerWhatsapp),
        CPM: num(a.cpm), CPC: num(a.cpc), CPL: num(a.cpl), CTR: num(a.ctr),
        'Vista previa': a.thumbnailUrl || '',
      })));
    }

    if (data?.byPlatform?.length) {
      addSheet('Por Plataforma', data.byPlatform.map(p => ({
        Plataforma: p.platform, Inversión: num(p.cost),
        Impresiones: num(p.impressions), Clicks: num(p.clicks),
        Leads: num(p.leads), CPM: num(p.cpm), CPC: num(p.cpc), CPL: num(p.cpl), CTR: num(p.ctr),
      })));
    }

    if (data?.byProductType?.length) {
      addSheet('Por Producto', data.byProductType.map(p => ({
        Producto: p.productType, Inversión: num(p.cost),
        Impresiones: num(p.impressions), Clicks: num(p.clicks),
        Leads: num(p.leads), 'WhatsApp Conv.': num(p.whatsappConversations),
        CPM: num(p.cpm), CPC: num(p.cpc), CPL: num(p.cpl), CTR: num(p.ctr),
      })));
    }

    if (data?.byProductTypePlatform?.length) {
      addSheet('Producto x Plataforma', data.byProductTypePlatform.map(p => ({
        Producto: p.productType, Plataforma: p.platform, Inversión: num(p.cost),
        Impresiones: num(p.impressions), Clicks: num(p.clicks), Leads: num(p.leads),
      })));
    }

    if (data?.byRegion?.length) {
      addSheet('Por Región', data.byRegion.map(r => ({
        Región: r.region, Inversión: num(r.cost),
        Impresiones: num(r.impressions), Clicks: num(r.clicks), Leads: num(r.leads),
      })));
    }

    if (data?.byAge?.length) {
      addSheet('Por Edad', data.byAge.map(a => ({
        Rango: a.ageRange, Inversión: num(a.cost),
        Impresiones: num(a.impressions), Clicks: num(a.clicks), Leads: num(a.leads),
      })));
    }

    if (jitData?.kpis) {
      addSheet('Funnel JIT', [{
        'Leads Digitales': num(kpis?.leads || kpisByObjective?.LEADS?.leads || 0),
        'No Contactados': num(jitData.kpis.totalNoContactados),
        Contactados: num(jitData.kpis.totalContactados),
        'Inician Proceso': num((parseInt(jitData.kpis.totalAcepta) || 0) + (parseInt(jitData.kpis.totalAgendado) || 0)),
        Acepta: num(jitData.kpis.totalAcepta),
      }]);
      if (jitData?.daily?.length) {
        addSheet('JIT Diario', jitData.daily.map(d => ({
          Fecha: d.fecha, Registros: num(d.registros),
          Contactados: num(d.contactados), Acepta: num(d.acepta),
          Agendado: num(d.agendado),
        })));
      }
    }

    if (waData?.kpis) {
      addSheet('Funnel WhatsApp SAC', [{
        Recibidos: num(waData.kpis.totalRecibidos),
        Atendidos: num(waData.kpis.totalAtendidos),
        'No Atendidos': num(waData.kpis.totalNoAtendidos),
        Acepta: num(waData.kpis.totalAcepta),
        Conversiones: num(waData.kpis.totalConversiones),
      }]);
    }

    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const fname = `tablero_${(clientName || 'cliente').toLowerCase().replace(/\s+/g, '_')}_${fmtDate(dateFrom)}_${fmtDate(dateTo)}.xlsx`;
    saveAs(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), fname);
    toast.success(`Tablero descargado · ${wb.SheetNames.length} hojas`);
  } catch (e) {
    console.error('exportFullDashboard error:', e);
    toast.error('Error al descargar el tablero');
  }
};

// Construye filas planas con estructura de árbol para exportar campañas con sus ad groups y ads
const buildTreeExportRows = (campaigns, adGroups, ads) => {
  const agByCampaign = {};
  (adGroups || []).forEach(ag => {
    if (!agByCampaign[ag.campaignKey]) agByCampaign[ag.campaignKey] = [];
    agByCampaign[ag.campaignKey].push(ag);
  });
  Object.values(agByCampaign).forEach(arr => arr.sort((a, b) => (b.cost || 0) - (a.cost || 0)));

  const adsByAdGroup = {};
  (ads || []).forEach(ad => {
    if (!adsByAdGroup[ad.adGroupKey]) adsByAdGroup[ad.adGroupKey] = [];
    adsByAdGroup[ad.adGroupKey].push(ad);
  });
  Object.values(adsByAdGroup).forEach(arr => arr.sort((a, b) => (b.cost || 0) - (a.cost || 0)));

  const n = (v) => (v == null || v === '' ? 0 : Number(v));
  const rows = [];

  (campaigns || []).forEach(c => {
    const campAgs = agByCampaign[c.campaignKey] || [];
    rows.push({
      Nivel: 'Campaña',
      Nombre: c.campaignName,
      Cuenta: c.accountName || '',
      Plataforma: c.sourcePlatform || c.platform || '',
      Producto: c.productType || '',
      Impresiones: n(c.impressions),
      Alcance: n(c.reach),
      Clics: n(c.clicks),
      CTR: n(c.ctr),
      CPM: n(c.cpm),
      CPC: n(c.cpc),
      Leads: n(c.leads),
      CPL: c.leads > 0 ? n(c.cost / c.leads) : 0,
      'WA Conv.': n(c.whatsappConversations),
      Inversión: n(c.cost),
    });

    campAgs.forEach(ag => {
      const agAds = adsByAdGroup[ag.adGroupKey] || [];
      rows.push({
        Nivel: '  Ad Group',
        Nombre: ag.adGroupName,
        Cuenta: '',
        Plataforma: '',
        Producto: '',
        Impresiones: n(ag.impressions),
        Alcance: n(ag.reach),
        Clics: n(ag.clicks),
        CTR: n(ag.ctr),
        CPM: n(ag.cpm),
        CPC: n(ag.cpc),
        Leads: n(ag.leads),
        CPL: n(ag.cpl),
        'WA Conv.': n(ag.whatsappConversations),
        Inversión: n(ag.cost),
      });

      agAds.forEach(ad => {
        rows.push({
          Nivel: '    Anuncio',
          Nombre: ad.adName || ad.adKey || '',
          Cuenta: '',
          Plataforma: '',
          Producto: '',
          Impresiones: n(ad.impressions),
          Alcance: n(ad.reach),
          Clics: n(ad.clicks),
          CTR: n(ad.ctr),
          CPM: n(ad.cpm),
          CPC: n(ad.cpc),
          Leads: n(ad.leads),
          CPL: n(ad.cpl),
          'WA Conv.': n(ad.whatsappConversations),
          Inversión: n(ad.cost),
        });
      });
    });
  });

  return rows;
};

// Componente de exportación para gráficas - CSV/Excel/Imagen
const ChartExportMenu = ({ data, columns, filename = 'datos', chartRef, treeData }) => {
  if (!data || data.length === 0) return null;

  const doExport = (type) => {
    try {
      if (type === 'image') {
        if (chartRef?.current) {
          const instance = chartRef.current.getEchartsInstance?.();
          if (instance) {
            const url = instance.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#fff' });
            const link = document.createElement('a');
            link.download = `${filename}.png`;
            link.href = url;
            link.click();
            toast.success('Imagen descargada');
            return;
          }
        }
        toast.info('No disponible para este gráfico');
        return;
      }

      const exportRows = treeData || data.map(row => {
        const r = {};
        columns.forEach(c => { r[c.header] = row[c.key] ?? ''; });
        return r;
      });
      const exportHeaders = treeData
        ? Object.keys(exportRows[0] || {})
        : columns.map(c => c.header);

      if (type === 'csv') {
        const headers = exportHeaders.map(h => `"${h}"`).join(',');
        const csvRows = exportRows.map(r =>
          exportHeaders.map(h => `"${String(r[h] ?? '').replace(/"/g, '""')}"`).join(',')
        );
        const blob = new Blob(['\uFEFF' + [headers, ...csvRows].join('\n')], { type: 'text/csv;charset=utf-8' });
        saveAs(blob, `${filename}.csv`);
        toast.success('CSV descargado');
      } else {
        const ws = XLSX.utils.json_to_sheet(exportRows);
        ws['!cols'] = exportHeaders.map(h => ({ wch: Math.max(String(h).length + 2, 14) }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Datos');
        const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        saveAs(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `${filename}.xlsx`);
        toast.success('Excel descargado');
      }
    } catch (e) {
      console.error('Export error:', e);
      toast.error('Error al exportar');
    }
  };

  return (
    <Dropdown align="end">
      <Dropdown.Toggle as="span" style={{ cursor: 'pointer', opacity: 0.5, transition: 'opacity 0.2s' }}
        onMouseEnter={e => e.target.style.opacity = 1} onMouseLeave={e => e.target.style.opacity = 0.5}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
          <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"/>
          <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z"/>
        </svg>
      </Dropdown.Toggle>
      <Dropdown.Menu style={{ minWidth: '160px', fontSize: '12px', zIndex: 1050 }}
        popperConfig={{ strategy: 'fixed' }} renderOnMount>
        <Dropdown.Header style={{ fontSize: '10px' }}>Exportar datos</Dropdown.Header>
        <Dropdown.Item onClick={() => doExport('csv')}>CSV (.csv)</Dropdown.Item>
        <Dropdown.Item onClick={() => doExport('xlsx')}>Excel (.xlsx)</Dropdown.Item>
        <Dropdown.Divider />
        <Dropdown.Header style={{ fontSize: '10px' }}>Exportar gráfico</Dropdown.Header>
        <Dropdown.Item onClick={() => doExport('image')}>Imagen (.png)</Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );
};

// Métricas disponibles para el gráfico evolutivo
const METRIC_OPTIONS = [
  { key: 'cost', label: 'Inversión', isCurrency: true },
  { key: 'impressions', label: 'Impresiones', isCurrency: false },
  { key: 'clicks', label: 'Clics', isCurrency: false },
  { key: 'linkClicks', label: 'Link Clics', isCurrency: false },
  { key: 'leads', label: 'Leads', isCurrency: false },
  { key: 'whatsappConversations', label: 'Conversaciones WA', isCurrency: false },
  { key: 'cpm', label: 'CPM', isCurrency: true },
  { key: 'cpc', label: 'CPC', isCurrency: true },
  { key: 'cpl', label: 'CPL', isCurrency: true },
  { key: 'costPerWhatsappConversation', label: 'Coste/Conv.', isCurrency: true },
  { key: 'ctr', label: 'CTR %', isCurrency: false, isPercent: true },
];

// Gráfico Evolutivo Principal con selectores de barras (izq) y línea (der)
const EvolutiveChart = ({ data }) => {
  const [selectedMetric, setSelectedMetric] = useState('impressions');
  const [selectedKPI, setSelectedKPI] = useState('cost');

  const metricConfig = METRIC_OPTIONS.find(m => m.key === selectedMetric) || METRIC_OPTIONS[1];
  const kpiConfig = METRIC_OPTIONS.find(m => m.key === selectedKPI) || METRIC_OPTIONS[0];

  const fmtVal = (v, cfg) => cfg.isCurrency ? '$' + formatNumber(v) : cfg.isPercent ? formatPercent(v) : formatNumber(v);

  const options = useMemo(() => {
    if (!data || data.length === 0) return {};
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross' },
        formatter: (params) => {
          let result = `<strong>${params[0].axisValue}</strong><br/>`;
          params.forEach(p => {
            const config = p.seriesIndex === 0 ? metricConfig : kpiConfig;
            result += `${p.marker} ${p.seriesName}: <strong>${fmtVal(p.value, config)}</strong><br/>`;
          });
          return result;
        }
      },
      legend: { data: [metricConfig.label, kpiConfig.label], top: 0, itemGap: 20 },
      grid: { left: 60, right: 60, bottom: 50, top: 40 },
      xAxis: {
        type: 'category',
        data: data.map(d => { const s = String(d.dateKey); return `${s.slice(4,6)}/${s.slice(6,8)}`; }),
        axisLabel: { fontSize: 10, rotate: 45 },
      },
      yAxis: [
        { type: 'value', name: metricConfig.label, nameTextStyle: { fontSize: 10 },
          axisLabel: { formatter: v => fmtVal(v, metricConfig), fontSize: 10 },
          splitLine: { show: true, lineStyle: { type: 'dashed', color: '#eee' } } },
        { type: 'value', name: kpiConfig.label, nameTextStyle: { fontSize: 10 }, position: 'right',
          axisLabel: { formatter: v => fmtVal(v, kpiConfig), fontSize: 10 },
          splitLine: { show: false } },
      ],
      series: [
        { name: metricConfig.label, type: 'bar', data: data.map(d => d[selectedMetric] || 0),
          itemStyle: { color: '#f5803e' }, barMaxWidth: 40,
          label: { show: data.length <= 20, position: 'top',
            formatter: p => fmtVal(p.value, metricConfig), fontSize: 8, color: '#333' } },
        { name: kpiConfig.label, type: 'line', yAxisIndex: 1, data: data.map(d => d[selectedKPI] || 0),
          lineStyle: { color: '#333', width: 2 }, itemStyle: { color: '#333' }, symbol: 'circle', symbolSize: 5,
          label: { show: data.length <= 15, position: 'top',
            formatter: p => fmtVal(p.value, kpiConfig), fontSize: 8, color: '#333' } },
      ],
    };
  }, [data, selectedMetric, selectedKPI, metricConfig, kpiConfig]); // eslint-disable-line

  if (!data || data.length === 0) return <div className="text-center text-muted py-4">Sin datos</div>;

  return (
    <Card className="shadow-sm border-0 h-100">
      <Card.Header className="bg-white border-bottom py-2 d-flex justify-content-between align-items-center">
        <h6 className="mb-0">EVOLUTIVO</h6>
        <ChartExportMenu data={data} filename="evolutivo" columns={[
          { key: 'dateKey', header: 'Fecha' }, { key: 'impressions', header: 'Impresiones' },
          { key: 'clicks', header: 'Clicks' }, { key: 'linkClicks', header: 'Link Clics' },
          { key: 'leads', header: 'Leads' }, { key: 'cost', header: 'Inversión' },
          { key: 'cpm', header: 'CPM' }, { key: 'ctr', header: 'CTR' }, { key: 'cpl', header: 'CPL' },
        ]} />
      </Card.Header>
      <Card.Body>
        <Row className="g-0">
          <Col xs={2} className="pe-2">
            <div className="mb-2 pb-1 border-bottom"><small className="text-muted fw-bold">Parámetro Métricas</small></div>
            {METRIC_OPTIONS.map(metric => (
              <button key={metric.key} className="btn btn-sm w-100 mb-1 text-start"
                style={{ backgroundColor: selectedMetric === metric.key ? '#f5803e' : '#f8f9fa',
                  color: selectedMetric === metric.key ? 'white' : '#333', border: 'none', fontSize: '11px', padding: '5px 8px' }}
                onClick={() => setSelectedMetric(metric.key)}>
                {metric.label}
              </button>
            ))}
          </Col>
          <Col xs={8} className="px-2">
            <div className="text-center mb-1">
              <small>
                <span style={{ color: '#f5803e' }}>● {metricConfig.label}</span>{' '}
                <span style={{ color: '#333' }}>● {kpiConfig.label}</span>
              </small>
            </div>
            <ReactEChartsCore echarts={echarts} option={options} style={{ height: 320 }} />
          </Col>
          <Col xs={2} className="ps-2">
            <div className="mb-2 pb-1 border-bottom"><small className="text-muted fw-bold">KPI (líneas)</small></div>
            {METRIC_OPTIONS.map(metric => (
              <button key={metric.key} className="btn btn-sm w-100 mb-1 text-start"
                style={{ backgroundColor: selectedKPI === metric.key ? '#333' : '#f8f9fa',
                  color: selectedKPI === metric.key ? 'white' : '#333', border: 'none', fontSize: '11px', padding: '5px 8px' }}
                onClick={() => setSelectedKPI(metric.key)}>
                {metric.label}
              </button>
            ))}
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

// Funnel de Leads (datos JIT) - diseño visual mejorado
const LeadsFunnelChart = ({ jitData, digitalLeads }) => {
  const funnelOpt = useMemo(() => {
    const k = jitData?.kpis || {};
    const leadsBrutos = parseInt(digitalLeads) || 0;
    const noContactados = parseInt(k.totalNoContactados) || 0;
    const contactados = parseInt(k.totalContactados) || 0;
    const acepta = parseInt(k.totalAcepta) || 0;
    const agendado = parseInt(k.totalAgendado) || 0;
    const inicianProceso = acepta + agendado;

    const steps = [
      { name: 'Leads Digitales', val: leadsBrutos, color: '#B8D4E3' },
      { name: 'No Contactados', val: noContactados, color: '#FFC107' },
      { name: 'Contactados', val: contactados, color: '#6BAED6' },
      { name: 'Inician Proceso', val: inicianProceso, color: '#2171B5' },
      { name: 'Acepta', val: acepta, color: '#28a745' },
    ];

    const maxVal = Math.max(leadsBrutos, 1);
    const norm = (v) => Math.max(Math.round((v / maxVal) * 100), 5);

    return {
      tooltip: {
        trigger: 'item',
        backgroundColor: 'rgba(255,255,255,0.95)',
        borderColor: '#ddd',
        borderWidth: 1,
        textStyle: { color: '#333', fontSize: 13 },
        formatter: (p) => {
          const s = steps[p.dataIndex];
          return `<strong style="font-size:14px">${s.name}</strong><br/><span style="font-size:18px;font-weight:bold">${formatNumber(s.val)}</span>`;
        },
      },
      series: [{
        type: 'funnel',
        left: '10%',
        width: '80%',
        top: 20,
        bottom: 20,
        gap: 3,
        sort: 'descending',
        minSize: '8%',
        label: {
          show: true,
          position: 'inside',
          fontSize: 14,
          fontWeight: '600',
          color: '#fff',
          textShadowColor: 'rgba(0,0,0,0.3)',
          textShadowBlur: 4,
          formatter: (p) => `${p.name}\n${formatNumber(p.data?.realValue)}`,
          lineHeight: 20,
        },
        labelLine: { show: false },
        emphasis: {
          label: { fontSize: 16 },
          itemStyle: { shadowBlur: 10, shadowColor: 'rgba(0,0,0,0.2)' },
        },
        itemStyle: {
          borderColor: '#fff',
          borderWidth: 2,
          shadowBlur: 4,
          shadowColor: 'rgba(0,0,0,0.08)',
        },
        data: steps.map(s => ({
          name: s.name,
          value: norm(s.val),
          realValue: s.val,
          itemStyle: { color: s.color },
        })),
      }],
    };
  }, [jitData, digitalLeads]);

  if (jitData?.notApplicable) {
    return (
      <Card className="shadow-sm border-0 h-100">
        <Card.Header className="bg-white border-bottom py-2">
          <h6 className="mb-0">Funnel Leads</h6>
        </Card.Header>
        <Card.Body className="d-flex align-items-center justify-content-center py-5">
          <div className="text-center text-muted">
            <span className="d-block mb-2 fw-semibold">No aplica</span>
            <small>{jitData.notApplicableReason || 'Este segmento no genera leads en JIT'}</small>
          </div>
        </Card.Body>
      </Card>
    );
  }

  const leadsBrutos = parseInt(digitalLeads) || 0;
  if (!jitData?.kpis && leadsBrutos === 0) {
    return (
      <Card className="shadow-sm border-0 h-100">
        <Card.Body className="d-flex align-items-center justify-content-center py-5">
          <div className="text-center text-muted">
            <div style={{ fontSize: '32px', marginBottom: '8px' }}>📊</div>
            <span>Sin datos disponibles</span>
          </div>
        </Card.Body>
      </Card>
    );
  }

  const k = jitData?.kpis || {};
  const contactados = parseInt(k.totalContactados) || 0;
  const acepta = parseInt(k.totalAcepta) || 0;
  const tasaCierre = contactados > 0 ? ((acepta / contactados) * 100).toFixed(1) : '0';
  const tasaContacto = leadsBrutos > 0 ? ((contactados / leadsBrutos) * 100).toFixed(1) : '0';

  return (
    <Card className="shadow-sm border-0 h-100">
      <Card.Header className="bg-white border-bottom py-2 d-flex justify-content-between align-items-center">
        <div>
          <h6 className="mb-0">Funnel Leads</h6>
          <small className="text-muted" style={{ fontSize: '10px' }}>
            {jitData?.dateRange ? `${jitData.dateRange.from} → ${jitData.dateRange.to}` : 'Leads digitales → JIT (Pauta + Vertical)'}
          </small>
        </div>
        <ChartExportMenu data={[{
          leadsDigitales: leadsBrutos, contactados, noContactados: parseInt(k.totalNoContactados) || 0,
          acepta, agendado: parseInt(k.totalAgendado) || 0, tasaContacto: tasaContacto + '%', tasaCierre: tasaCierre + '%',
        }]} filename="funnel_leads" columns={[
          { key: 'leadsDigitales', header: 'Leads Digitales' }, { key: 'contactados', header: 'Contactados' },
          { key: 'noContactados', header: 'No Contactados' }, { key: 'acepta', header: 'Acepta' },
          { key: 'agendado', header: 'Agendado' }, { key: 'tasaContacto', header: 'Tasa Contacto' },
          { key: 'tasaCierre', header: 'Tasa Cierre' },
        ]} />
      </Card.Header>
      <Card.Body className="d-flex flex-column">
        <div className="flex-grow-1" style={{ minHeight: 340 }}>
          <ReactEChartsCore echarts={echarts} option={funnelOpt} style={{ height: '100%', minHeight: 340 }} />
        </div>
        <Row className="g-2 mt-2">
          <Col xs={4}>
            <div className="text-center rounded py-2" style={{ background: 'linear-gradient(135deg, #e8f4fd 0%, #d0e8f7 100%)' }}>
              <div className="fw-bold" style={{ fontSize: '18px', color: '#2171B5' }}>{formatNumber(leadsBrutos)}</div>
              <small className="text-muted" style={{ fontSize: '10px' }}>Leads Digitales</small>
            </div>
          </Col>
          <Col xs={4}>
            <div className="text-center rounded py-2" style={{ background: 'linear-gradient(135deg, #fff8e1 0%, #ffecb3 100%)' }}>
              <div className="fw-bold" style={{ fontSize: '18px', color: '#e65100' }}>{tasaContacto}%</div>
              <small className="text-muted" style={{ fontSize: '10px' }}>Tasa Contacto</small>
            </div>
          </Col>
          <Col xs={4}>
            <div className="text-center rounded py-2" style={{ background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)' }}>
              <div className="fw-bold" style={{ fontSize: '18px', color: '#2e7d32' }}>{tasaCierre}%</div>
              <small className="text-muted" style={{ fontSize: '10px' }}>Tasa Cierre</small>
            </div>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

// Gráfico de Impresiones vs CPM (doble eje)
// Top Anuncios por métrica - muestra ranking de anuncios para cada etapa del funnel
const TOP_AD_METRICS = [
  { key: 'impressions', label: 'Impresiones', fmt: formatNumber, color: '#2c7be5' },
  { key: 'clicks', label: 'Clicks', fmt: formatNumber, color: '#00d97e' },
  { key: 'ctr', label: 'CTR %', fmt: v => formatPercent(v), color: '#f5803e' },
  { key: 'leads', label: 'Leads', fmt: formatNumber, color: '#6b5eae' },
  { key: 'cpl', label: 'CPL', fmt: v => formatCurrency(v), color: '#e63757', sortAsc: true },
  { key: 'whatsappConversations', label: 'Conversaciones', fmt: formatNumber, color: '#25d366' },
  { key: 'costPerWhatsapp', label: 'Coste/Conv.', fmt: v => formatCurrency(v), color: '#128c7e', sortAsc: true },
  { key: 'cost', label: 'Inversión', fmt: v => formatCurrency(v), color: '#27bcfd' },
];

const PLATFORM_BADGE_COLORS = { META: '#1877f2', GOOGLE: '#34a853', TIKTOK: '#010101', LINKEDIN: '#0a66c2', ILLUMIN: '#7B61FF', X: '#14171a' };

const adDisplayName = (ad) => {
  if (ad.adName && ad.adName.trim()) return ad.adName;
  if (ad.creativeTitle && ad.creativeTitle.trim()) return ad.creativeTitle;
  const platform = (ad.sourcePlatform || '').toUpperCase();
  if (platform === 'GOOGLE') return `Google Ad #${ad.adId || ad.adKey}`;
  if (platform === 'TIKTOK') return `TikTok Ad #${ad.adId || ad.adKey}`;
  if (platform === 'ILLUMIN') return `Illumin Creative #${ad.adId || ad.adKey}`;
  return `Ad #${ad.adId || ad.adKey}`;
};

const PlatformBadge = ({ platform, style }) => {
  const p = (platform || '').toUpperCase();
  const color = PLATFORM_BADGE_COLORS[p] || '#748194';
  return (
    <Badge bg="" style={{ backgroundColor: color, fontSize: 8, fontWeight: 500, verticalAlign: 'middle', ...style }}>
      {p || '?'}
    </Badge>
  );
};

const CreativePreviewModal = ({ ad, show, onHide }) => {
  if (!ad) return null;
  const proxyUrl = ad?.adKey ? `${getCreativeProxyUrl(ad)}?size=full` : null;
  const hasImage = !!(ad.imageUrl || ad.thumbnailUrl || proxyUrl);
  const previews = [
    { label: 'Instagram', url: ad.previewUrlInstagram },
    { label: 'Mobile Feed', url: ad.previewUrlMobileFeed },
  ].filter(p => p.url);

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: '16px' }}>
          {adDisplayName(ad)}
          <PlatformBadge platform={ad.sourcePlatform} style={{ marginLeft: 8 }} />
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="g-3">
          <Col md={6} className="text-center">
            {hasImage ? (
              <img
                src={proxyUrl || ad.imageUrl || ad.thumbnailUrl}
                alt={ad.adName}
                style={{ maxWidth: '100%', maxHeight: 420, borderRadius: 8, border: '1px solid #e5e7eb' }}
                onError={(e) => { e.target.onerror = null; e.target.src = proxyUrl ? '' : ''; e.target.style.display = 'none'; }}
              />
            ) : (
              <div className="d-flex flex-column align-items-center justify-content-center text-muted bg-light"
                style={{ height: 300, borderRadius: 8 }}>
                <div style={{ fontSize: 48 }}>🖼️</div>
                <small className="mt-2">Sin imagen disponible</small>
                <small className="text-muted mt-1" style={{ fontSize: 10 }}>
                  (Espera al próximo procesamiento de Supermetrics)
                </small>
              </div>
            )}
          </Col>
          <Col md={6}>
            <div className="mb-2">
              <small className="text-muted">Ad ID</small>
              <div className="fw-semibold" style={{ fontSize: 13 }}>{ad.adId || ad.adKey}</div>
            </div>
            {ad.creativeTitle && (
              <div className="mb-2">
                <small className="text-muted">Título</small>
                <div className="fw-semibold">{ad.creativeTitle}</div>
              </div>
            )}
            {ad.creativeBody && (
              <div className="mb-2">
                <small className="text-muted">Cuerpo</small>
                <div style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{ad.creativeBody.slice(0, 600)}</div>
              </div>
            )}
            {ad.creativeObjectType && (
              <div className="mb-2">
                <small className="text-muted">Tipo</small>
                <div><Badge bg="info">{ad.creativeObjectType}</Badge></div>
              </div>
            )}
            {ad.creativeCallToAction && (
              <div className="mb-2">
                <small className="text-muted">Call to Action</small>
                <div><Badge bg="warning">{ad.creativeCallToAction}</Badge></div>
              </div>
            )}
            {previews.length > 0 && (
              <div className="mb-2">
                <small className="text-muted">Vista previa en placement</small>
                <div className="d-flex gap-2 flex-wrap mt-1">
                  {previews.map(p => (
                    <a key={p.label} href={p.url} target="_blank" rel="noopener noreferrer"
                      className="btn btn-sm btn-outline-primary" style={{ fontSize: 11 }}>
                      {p.label} ↗
                    </a>
                  ))}
                </div>
              </div>
            )}
            <hr />
            <div className="row g-2" style={{ fontSize: 12 }}>
              <div className="col-6"><strong>Impresiones:</strong> {formatNumberFull(ad.impressions || 0)}</div>
              <div className="col-6"><strong>Clicks:</strong> {formatNumberFull(ad.clicks || 0)}</div>
              <div className="col-6"><strong>Inversión:</strong> {formatCurrency(ad.cost || 0)}</div>
              <div className="col-6"><strong>Leads:</strong> {formatNumberFull(ad.leads || 0)}</div>
              <div className="col-6"><strong>WhatsApp:</strong> {formatNumberFull(ad.whatsappConversations || 0)}</div>
              <div className="col-6"><strong>CTR:</strong> {formatPercent(ad.ctr || 0)}</div>
            </div>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Cerrar</Button>
      </Modal.Footer>
    </Modal>
  );
};

const TopAdsSection = ({ ads, campaigns }) => {
  const [activeMetric, setActiveMetric] = useState('impressions');
  const [previewAd, setPreviewAd] = useState(null);

  const metricCfg = TOP_AD_METRICS.find(m => m.key === activeMetric) || TOP_AD_METRICS[0];

  const topAds = useMemo(() => {
    if (!ads || ads.length === 0) return [];
    const filtered = ads.filter(a => {
      const v = parseFloat(a[activeMetric]) || 0;
      return metricCfg.sortAsc ? v > 0 : true;
    });
    const sorted = [...filtered].sort((a, b) => {
      const aVal = parseFloat(a[activeMetric]) || 0;
      const bVal = parseFloat(b[activeMetric]) || 0;
      return metricCfg.sortAsc ? aVal - bVal : bVal - aVal;
    });
    return sorted.slice(0, 10);
  }, [ads, activeMetric, metricCfg.sortAsc]);

  const campaignName = (ad) => {
    const c = campaigns?.find(c => c.campaignKey === ad.campaignKey);
    return c?.campaignName || '';
  };

  const maxVal = topAds.length > 0 ? Math.max(...topAds.map(a => Math.abs(parseFloat(a[activeMetric]) || 0)), 1) : 1;

  if (!ads || ads.length === 0) return null;

  const hasAnyThumb = ads.some(a => a.thumbnailUrl || a.imageUrl);

  return (
    <>
      <Card className="shadow-sm border-0">
        <Card.Header className="bg-white border-bottom py-2 d-flex justify-content-between align-items-center">
          <h6 className="mb-0">
            Top Anuncios Activos
            {!hasAnyThumb && (
              <small className="text-muted ms-2" style={{ fontSize: 11 }}>
                · Las miniaturas aparecerán tras el próximo procesamiento de Supermetrics
              </small>
            )}
          </h6>
          <div className="d-flex align-items-center gap-2">
            <ChartExportMenu data={topAds} filename="top_anuncios" columns={[
              { key: 'adName', header: 'Anuncio' }, { key: 'impressions', header: 'Impresiones' },
              { key: 'clicks', header: 'Clicks' }, { key: 'ctr', header: 'CTR' },
              { key: 'leads', header: 'Leads' }, { key: 'cpl', header: 'CPL' },
              { key: 'whatsappConversations', header: 'Conversaciones' },
              { key: 'costPerWhatsapp', header: 'Coste/Conv.' },
              { key: 'cost', header: 'Inversión' },
              { key: 'thumbnailUrl', header: 'Thumbnail URL' },
            ]} />
          </div>
        </Card.Header>
        <Card.Body>
          <div className="d-flex gap-1 mb-3 flex-wrap">
            {TOP_AD_METRICS.map(m => (
              <Badge key={m.key} pill
                bg={activeMetric === m.key ? undefined : 'light'}
                text={activeMetric === m.key ? 'white' : 'dark'}
                style={{
                  cursor: 'pointer', fontSize: '11px', padding: '6px 12px',
                  backgroundColor: activeMetric === m.key ? m.color : undefined,
                }}
                onClick={() => setActiveMetric(m.key)}>
                {m.label}
              </Badge>
            ))}
          </div>
          <div className="table-responsive">
            <Table hover size="sm" className="mb-0" style={{ fontSize: '12px' }}>
              <thead className="bg-light">
                <tr>
                  <th style={{ width: 30 }}>#</th>
                  <th style={{ width: 60 }}></th>
                  <th>Anuncio</th>
                  <th>Campaña</th>
                  <th className="text-end" style={{ color: metricCfg.color, fontWeight: 'bold', width: 100 }}>{metricCfg.label}</th>
                  <th style={{ width: '30%' }}>Distribución</th>
                </tr>
              </thead>
              <tbody>
                {topAds.map((ad, idx) => {
                  const val = parseFloat(ad[activeMetric]) || 0;
                  const barW = maxVal > 0 ? (Math.abs(val) / maxVal) * 100 : 0;
                  const proxyUrl = getCreativeProxyUrl(ad);
                  const adLabel = adDisplayName(ad) || `Ad ${ad.adKey}`;
                  return (
                    <tr key={ad.adKey || idx} style={{ cursor: 'pointer' }}
                      onClick={() => setPreviewAd(ad)} title={`${adLabel}\n${campaignName(ad)}\n${metricCfg.label}: ${metricCfg.fmt(val)}\nClick para vista previa`}>
                      <td>
                        <Badge bg={idx < 3 ? 'primary' : 'secondary'} style={{ fontSize: '10px' }}>{idx + 1}</Badge>
                      </td>
                      <td>
                        {proxyUrl ? (
                          <img src={proxyUrl} alt={ad.adName}
                            style={{ width: 42, height: 42, objectFit: 'cover', borderRadius: 4, border: '1px solid #e5e7eb' }}
                            onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }} />
                        ) : (
                          <div style={{
                            width: 42, height: 42, background: '#f3f4f6', borderRadius: 4,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, color: '#9ca3af',
                          }}>{ad.creativeObjectType === 'VIDEO' ? '🎬' : '🖼️'}</div>
                        )}
                      </td>
                      <td>
                        <div className="fw-semibold text-truncate" style={{ maxWidth: 220 }} title={ad.adName || adLabel}>
                          {adLabel}
                        </div>
                        <div className="d-flex align-items-center gap-1 mt-1">
                          <PlatformBadge platform={ad.sourcePlatform} />
                          {(!ad.adName || !ad.adName.trim()) && (
                            <Badge bg="light" text="muted" style={{ fontSize: 8, border: '1px solid #e5e7eb' }}>Sin nombre origen</Badge>
                          )}
                        </div>
                        {ad.creativeTitle && ad.creativeTitle !== adLabel && (
                          <small className="text-muted text-truncate d-block" style={{ maxWidth: 220, fontSize: 10 }}>
                            {ad.creativeTitle}
                          </small>
                        )}
                      </td>
                      <td>
                        <small className="text-muted text-truncate d-block" style={{ maxWidth: 180 }} title={campaignName(ad)}>
                          {campaignName(ad).slice(0, 35)}
                        </small>
                      </td>
                      <td className="text-end fw-bold" style={{ color: metricCfg.color }}>
                        {metricCfg.fmt(val)}
                      </td>
                      <td>
                        <div
                          style={{
                            height: 22,
                            background: '#f1f5f9',
                            borderRadius: 4,
                            position: 'relative',
                            overflow: 'hidden',
                          }}
                          title={`${adLabel} — ${metricCfg.fmt(val)}`}
                        >
                          <div
                            style={{
                              width: `${barW}%`,
                              height: '100%',
                              backgroundColor: metricCfg.color,
                              borderRadius: 4,
                              transition: 'width 0.3s',
                            }}
                          />
                          <span
                            style={{
                              position: 'absolute',
                              left: 8,
                              top: '50%',
                              transform: 'translateY(-50%)',
                              fontSize: 10,
                              fontWeight: 600,
                              color: barW > 35 ? '#fff' : '#1f2937',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: '95%',
                              pointerEvents: 'none',
                            }}
                          >
                            {adLabel.length > 38 ? adLabel.slice(0, 38) + '…' : adLabel}
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
          <div className="text-end mt-2">
            <small className="text-muted">
              {ads.length} anuncios totales · Top 10 por {metricCfg.label}{metricCfg.sortAsc ? ' (menor es mejor)' : ''}
              {' · '}<span style={{ color: '#3b82f6' }}>Click en una fila para vista previa</span>
            </small>
          </div>
        </Card.Body>
      </Card>
      <CreativePreviewModal ad={previewAd} show={!!previewAd} onHide={() => setPreviewAd(null)} />
    </>
  );
};

const ImpressionsVsCpmChart = ({ data }) => {
  const options = useMemo(() => {
    if (!data || data.length === 0) return {};
    
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross' },
      },
      legend: { data: ['Impresiones', 'CPM'], top: 5 },
      grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
      xAxis: {
        type: 'category',
        data: data.map(d => {
          const dateStr = String(d.dateKey);
          return `${dateStr.slice(4,6)}/${dateStr.slice(6,8)}`;
        }),
        axisLabel: { fontSize: 10, rotate: 45 },
      },
      yAxis: [
        { type: 'value', name: 'Impresiones', axisLabel: { formatter: v => formatNumber(v) }, splitLine: { show: false } },
        { type: 'value', name: 'CPM', position: 'right', axisLabel: { formatter: v => '$' + v.toFixed(2) } },
      ],
      series: [
        { name: 'Impresiones', type: 'line', data: data.map(d => d.impressions), smooth: true, areaStyle: { opacity: 0.3 }, itemStyle: { color: '#2c7be5' } },
        { name: 'CPM', type: 'line', yAxisIndex: 1, data: data.map(d => d.cpm), smooth: true, itemStyle: { color: '#f5803e' } },
      ],
    };
  }, [data]);

  return <ReactEChartsCore echarts={echarts} option={options} style={{ height: 280 }} />;
};

// Gráfico de Clicks vs CTR
const ClicksVsCtrChart = ({ data }) => {
  const options = useMemo(() => {
    if (!data || data.length === 0) return {};
    
    return {
      tooltip: { trigger: 'axis', axisPointer: { type: 'cross' } },
      legend: { data: ['Clicks', 'CTR'], top: 5 },
      grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
      xAxis: {
        type: 'category',
        data: data.map(d => {
          const dateStr = String(d.dateKey);
          return `${dateStr.slice(4,6)}/${dateStr.slice(6,8)}`;
        }),
        axisLabel: { fontSize: 10, rotate: 45 },
      },
      yAxis: [
        { type: 'value', name: 'Clicks', axisLabel: { formatter: v => formatNumber(v) }, splitLine: { show: false } },
        { type: 'value', name: 'CTR %', position: 'right', axisLabel: { formatter: v => v.toFixed(2) + '%' } },
      ],
      series: [
        { name: 'Clicks', type: 'line', data: data.map(d => d.clicks), smooth: true, areaStyle: { opacity: 0.3 }, itemStyle: { color: '#00d97e' } },
        { name: 'CTR', type: 'line', yAxisIndex: 1, data: data.map(d => d.ctr), smooth: true, itemStyle: { color: '#f5803e' } },
      ],
    };
  }, [data]);

  return <ReactEChartsCore echarts={echarts} option={options} style={{ height: 280 }} />;
};

// Gráfico de Leads vs CPL
const LeadsVsCplChart = ({ data }) => {
  const options = useMemo(() => {
    if (!data || data.length === 0) return {};
    
    return {
      tooltip: { trigger: 'axis', axisPointer: { type: 'cross' } },
      legend: { data: ['Leads', 'CPL'], top: 5 },
      grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
      xAxis: {
        type: 'category',
        data: data.map(d => {
          const dateStr = String(d.dateKey);
          return `${dateStr.slice(4,6)}/${dateStr.slice(6,8)}`;
        }),
        axisLabel: { fontSize: 10, rotate: 45 },
      },
      yAxis: [
        { type: 'value', name: 'Leads', splitLine: { show: false } },
        { type: 'value', name: 'CPL', position: 'right', axisLabel: { formatter: v => '$' + v.toFixed(2) } },
      ],
      series: [
        { name: 'Leads', type: 'bar', data: data.map(d => d.leads || 0), itemStyle: { color: '#6b5eae' } },
        { name: 'CPL', type: 'line', yAxisIndex: 1, data: data.map(d => d.cpl || 0), smooth: true, itemStyle: { color: '#f5803e' } },
      ],
    };
  }, [data]);

  return <ReactEChartsCore echarts={echarts} option={options} style={{ height: 280 }} />;
};

// Gráfico de Costo por Día
const CostByDayChart = ({ data }) => {
  const options = useMemo(() => {
    if (!data || data.length === 0) return {};
    
    return {
      tooltip: { trigger: 'axis', formatter: params => `${params[0].name}<br/>Gasto: ${formatCurrency(params[0].value)}` },
      grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
      xAxis: {
        type: 'category',
        data: data.map(d => {
          const dateStr = String(d.dateKey);
          return `${dateStr.slice(4,6)}/${dateStr.slice(6,8)}`;
        }),
        axisLabel: { fontSize: 10, rotate: 45 },
      },
      yAxis: { type: 'value', axisLabel: { formatter: v => formatCurrency(v) } },
      series: [{
        type: 'line',
        data: data.map(d => d.cost),
        smooth: true,
        areaStyle: { color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: 'rgba(39,188,253,0.5)' }, { offset: 1, color: 'rgba(39,188,253,0.05)' }] } },
        itemStyle: { color: '#27bcfd' },
        label: { show: true, position: 'top', formatter: p => formatCurrency(p.value), fontSize: 9 },
      }],
    };
  }, [data]);

  return <ReactEChartsCore echarts={echarts} option={options} style={{ height: 250 }} />;
};

// Mapa de regiones con selector de métrica
const RegionMapChart = ({ data, selectedMetric = 'impressions' }) => {
  const metricConfig = {
    impressions: { label: 'Impresiones', color: '#2c7be5', formatter: formatNumber },
    clicks: { label: 'Clicks', color: '#00d97e', formatter: formatNumber },
    cost: { label: 'Inversión', color: '#e63757', formatter: formatCurrency },
    leads: { label: 'Leads', color: '#6b5eae', formatter: formatNumber },
  };
  
  const config = metricConfig[selectedMetric] || metricConfig.impressions;
  
  const options = useMemo(() => {
    if (!data || data.length === 0) return {};
    
    const sortedData = [...data].sort((a, b) => (b[selectedMetric] || 0) - (a[selectedMetric] || 0)).slice(0, 15);
    const maxValue = sortedData[0]?.[selectedMetric] || 1;
    
    return {
      tooltip: { 
        trigger: 'axis', 
        axisPointer: { type: 'shadow' },
        formatter: params => {
          const val = params[0].value;
          return `<strong>${params[0].name}</strong><br/>${config.label}: ${config.formatter(val)}`;
        }
      },
      grid: { left: '3%', right: '12%', bottom: '3%', containLabel: true },
      xAxis: { type: 'value', axisLabel: { formatter: v => config.formatter(v) } },
      yAxis: { 
        type: 'category', 
        data: sortedData.map(d => d.region || 'Desconocido').reverse(), 
        axisLabel: { fontSize: 11, width: 120, overflow: 'truncate' } 
      },
      series: [{
        type: 'bar',
        data: sortedData.map(d => ({
          value: d[selectedMetric] || 0,
          itemStyle: {
            color: {
              type: 'linear', x: 0, y: 0, x2: 1, y2: 0,
              colorStops: [
                { offset: 0, color: config.color },
                { offset: 1, color: config.color + '80' }
              ]
            }
          }
        })).reverse(),
        label: { 
          show: true, 
          position: 'right', 
          formatter: p => config.formatter(p.value), 
          fontSize: 10,
          color: '#333'
        },
        barMaxWidth: 25,
      }],
    };
  }, [data, selectedMetric, config]);

  return <ReactEChartsCore echarts={echarts} option={options} style={{ height: 450 }} />;
};

// KPI Card Component - Estilo Looker Studio
const KPICard = ({ title, value, subtitle, color = '#2c7be5', tooltip }) => (
  <Card className="h-100 border-0 shadow-sm text-center" style={{ borderTop: `3px solid ${color}` }} title={tooltip}>
    <Card.Body className="py-3">
      <p className="text-muted small mb-1 text-uppercase">
        {title}{tooltip && <span style={{ marginLeft: 4, opacity: 0.6, cursor: 'help' }}>ⓘ</span>}
      </p>
      <h3 className="mb-0 fw-bold" style={{ color }}>{value}</h3>
      {subtitle && <small className="text-muted">{subtitle}</small>}
    </Card.Body>
  </Card>
);

// KPI Card Illumin con mini-gráfica de tendencia (estilo Falcon)
const IlluminKPICard = ({ title, value, subtitle, color = '#7B61FF', trendData, trendKey, tooltip, formatSpark = (v) => v }) => {
  const sparkOption = useMemo(() => {
    if (!trendData?.length) return null;
    const values = trendData.map((d) => d[trendKey] ?? 0);
    const dates = trendData.map((d) => d.date?.slice(5) || '');
    return {
      tooltip: {
        trigger: 'axis',
        confine: true,
        padding: [4, 8],
        textStyle: { fontSize: 11 },
        formatter: (params) => {
          const p = params[0];
          return `${p.axisValue}<br/>${title}: ${formatSpark(p.value)}`;
        },
      },
      xAxis: {
        type: 'category',
        data: dates,
        show: false,
        boundaryGap: false,
      },
      yAxis: { type: 'value', show: false },
      series: [{
        type: 'line',
        data: values,
        smooth: true,
        symbol: 'circle',
        symbolSize: 4,
        showSymbol: values.length <= 7,
        lineStyle: { color, width: 2 },
        itemStyle: { color, borderColor: color },
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: color + '33' },
            { offset: 1, color: color + '00' },
          ]),
        },
      }],
      grid: { left: 0, right: 0, top: 4, bottom: 0 },
    };
  }, [trendData, trendKey, color, title, formatSpark]);

  return (
    <Card className="h-100 border-0 shadow-sm" style={{ borderTop: `3px solid ${color}` }} title={tooltip}>
      <Card.Body className="py-2 px-3">
        <div className="d-flex align-items-end justify-content-between gap-2">
          <div className="flex-grow-1 min-w-0">
            <p className="text-muted small mb-1 text-uppercase" style={{ fontSize: '10px' }}>
              {title}{tooltip && <span style={{ marginLeft: 4, opacity: 0.6, cursor: 'help' }}>ⓘ</span>}
            </p>
            <h4 className="mb-0 fw-bold" style={{ color, fontSize: '1.15rem' }}>{value}</h4>
            {subtitle && <small className="text-muted" style={{ fontSize: '10px' }}>{subtitle}</small>}
          </div>
          {sparkOption && (
            <ReactEChartsCore
              echarts={echarts}
              option={sparkOption}
              style={{ width: 88, height: 48, flexShrink: 0 }}
              opts={{ renderer: 'canvas' }}
            />
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

// Gráfico de tendencia con múltiples métricas
const TrendChart = ({ data }) => {
  const options = useMemo(() => {
    if (!data || data.length === 0) return {};

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross' },
      },
      legend: {
        data: ['Impresiones', 'Clicks', 'Gasto'],
        bottom: 0,
      },
      grid: { left: '3%', right: '4%', bottom: '15%', top: '10%', containLabel: true },
      xAxis: {
        type: 'category',
        data: data.map((d) => d.date?.slice(5) || ''),
        axisLabel: { fontSize: 10 },
      },
      yAxis: [
        { type: 'value', name: 'Impresiones/Clicks', axisLabel: { formatter: (v) => formatNumber(v) } },
        { type: 'value', name: 'Gasto ($)', axisLabel: { formatter: (v) => '$' + formatNumber(v) } },
      ],
      series: [
        {
          name: 'Impresiones',
          type: 'line',
          data: data.map((d) => d.impressions || 0),
          smooth: true,
          areaStyle: { opacity: 0.1 },
          lineStyle: { color: '#2c7be5' },
          itemStyle: { color: '#2c7be5' },
        },
        {
          name: 'Clicks',
          type: 'line',
          data: data.map((d) => d.clicks || 0),
          smooth: true,
          lineStyle: { color: '#00d97e' },
          itemStyle: { color: '#00d97e' },
        },
        {
          name: 'Gasto',
          type: 'bar',
          yAxisIndex: 1,
          data: data.map((d) => d.cost || 0),
          itemStyle: { color: '#f5803e', opacity: 0.8 },
          barMaxWidth: 20,
        },
      ],
    };
  }, [data]);

  if (!data || data.length === 0) return <div className="text-center py-5 text-muted">Sin datos</div>;
  return <ReactEChartsCore echarts={echarts} option={options} style={{ height: '300px' }} />;
};

// Gráfico por Ubicación/Región
const LocationChart = ({ data }) => {
  const options = useMemo(() => {
    if (!data || data.length === 0) return {};

    return {
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      grid: { left: '3%', right: '4%', bottom: '3%', top: '10%', containLabel: true },
      xAxis: { type: 'value', axisLabel: { formatter: (v) => formatNumber(v) } },
      yAxis: {
        type: 'category',
        data: data.map((d) => d.region?.slice(0, 20) || 'N/A'),
        axisLabel: { fontSize: 10 },
      },
      series: [
        {
          type: 'bar',
          data: data.map((d) => d.impressions),
          itemStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
              { offset: 0, color: '#667eea' },
              { offset: 1, color: '#764ba2' },
            ]),
          },
          barMaxWidth: 25,
        },
      ],
    };
  }, [data]);

  if (!data || data.length === 0) return <div className="text-center py-5 text-muted">Sin datos de ubicación</div>;
  return <ReactEChartsCore echarts={echarts} option={options} style={{ height: '300px' }} />;
};

// Gráfico por Edad — donut con % + rango dentro de las rebanadas + tooltip completo
const AgeChart = ({ data }) => {
  const options = useMemo(() => {
    if (!data || data.length === 0) return {};

    const colors = ['#2c7be5', '#00d97e', '#f5803e', '#e63757', '#27bcfd', '#6b5eae', '#a78bfa'];
    const totalImp = data.reduce((s, d) => s + (d.impressions || 0), 0);

    return {
      tooltip: {
        trigger: 'item',
        formatter: (p) => {
          const item = data.find((d) => (d.ageRange || 'N/A') === p.name) || {};
          return `<strong>${p.name}</strong> — <strong>${p.percent.toFixed(1)}%</strong><br/>` +
            `Impresiones: ${formatNumber(p.value)}<br/>` +
            `Clicks: ${formatNumber(item.clicks || 0)}<br/>` +
            `Leads: ${formatNumber(item.leads || 0)}<br/>` +
            `Inversión: ${formatCurrency(item.cost || 0)}`;
        },
      },
      legend: {
        orient: 'vertical',
        right: 5,
        top: 'center',
        textStyle: { fontSize: 10 },
        formatter: (name) => {
          const item = data.find((d) => (d.ageRange || 'N/A') === name);
          if (!item || totalImp === 0) return name;
          const pct = ((item.impressions / totalImp) * 100).toFixed(1);
          return `${name}  ${pct}%`;
        },
      },
      graphic: totalImp > 0 ? [{
        type: 'text',
        left: '26%',
        top: '44%',
        style: { text: formatNumber(totalImp), fontSize: 16, fontWeight: 'bold', fill: '#1f2937', textAlign: 'center' },
      }, {
        type: 'text',
        left: '26%',
        top: '54%',
        style: { text: 'Impresiones', fontSize: 9, fill: '#6b7280', textAlign: 'center' },
      }] : [],
      series: [
        {
          type: 'pie',
          radius: ['40%', '72%'],
          center: ['30%', '50%'],
          avoidLabelOverlap: true,
          itemStyle: { borderRadius: 5, borderColor: '#fff', borderWidth: 2 },
          label: {
            show: true,
            position: 'outside',
            fontSize: 11,
            fontWeight: 600,
            formatter: (p) => p.percent >= 5 ? `{bold|${p.name}}\n{pct|${p.percent.toFixed(1)}%}` : p.percent >= 2 ? `{pct|${p.percent.toFixed(1)}%}` : '',
            rich: {
              bold: { fontSize: 11, fontWeight: 700, color: '#1f2937', lineHeight: 16 },
              pct: { fontSize: 12, fontWeight: 600, color: '#4b5563', lineHeight: 16 },
            },
          },
          labelLine: { show: true, length: 10, length2: 8, smooth: true },
          emphasis: {
            label: { show: true, fontSize: 14, fontWeight: 'bold' },
            scaleSize: 8,
          },
          data: data.map((d, i) => ({
            name: d.ageRange || 'N/A',
            value: d.impressions,
            itemStyle: { color: colors[i % colors.length] },
          })),
        },
      ],
    };
  }, [data]);

  if (!data || data.length === 0) return <div className="text-center py-5 text-muted">Sin datos de edad</div>;
  return <ReactEChartsCore echarts={echarts} option={options} style={{ height: '300px' }} />;
};

// Gráfico por Plataforma
const PlatformChart = ({ data }) => {
  const options = useMemo(() => {
    if (!data || data.length === 0) return {};

    // Agrupar por plataforma
    const grouped = {};
    data.forEach((d) => {
      const key = d.platform || 'Otro';
      if (!grouped[key]) grouped[key] = { impressions: 0, clicks: 0, cost: 0 };
      grouped[key].impressions += d.impressions;
      grouped[key].clicks += d.clicks;
      grouped[key].cost += d.cost;
    });

    const platforms = Object.keys(grouped);
    const colors = { facebook: '#1877f2', instagram: '#e4405f', messenger: '#0084ff', audience_network: '#0668e1' };

    return {
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      legend: { bottom: 0 },
      grid: { left: '3%', right: '4%', bottom: '15%', top: '10%', containLabel: true },
      xAxis: { type: 'category', data: platforms },
      yAxis: [
        { type: 'value', name: 'Impresiones', axisLabel: { formatter: (v) => formatNumber(v) } },
        { type: 'value', name: 'Gasto', axisLabel: { formatter: (v) => '$' + v } },
      ],
      series: [
        {
          name: 'Impresiones',
          type: 'bar',
          data: platforms.map((p) => ({
            value: grouped[p].impressions,
            itemStyle: { color: colors[p.toLowerCase()] || '#6b5eae' },
          })),
          barMaxWidth: 40,
        },
        {
          name: 'Gasto',
          type: 'line',
          yAxisIndex: 1,
          data: platforms.map((p) => grouped[p].cost),
          lineStyle: { color: '#e63757', width: 2 },
          itemStyle: { color: '#e63757' },
        },
      ],
    };
  }, [data]);

  if (!data || data.length === 0) return <div className="text-center py-5 text-muted">Sin datos de plataforma</div>;
  return <ReactEChartsCore echarts={echarts} option={options} style={{ height: '280px' }} />;
};

// Gráfico Impresiones vs CPM por Campaña
const ImpressionsCPMChart = ({ campaigns }) => {
  const options = useMemo(() => {
    if (!campaigns || campaigns.length === 0) return {};

    const top10 = campaigns.slice(0, 10);

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params) => {
          const name = top10[params[0].dataIndex]?.campaignName?.slice(0, 40) || '';
          let html = `<strong>${name}</strong><br/>`;
          params.forEach((p) => {
            html += `${p.marker} ${p.seriesName}: ${p.seriesName === 'CPM' ? '$' + p.value : formatNumber(p.value)}<br/>`;
          });
          return html;
        },
      },
      legend: { bottom: 0 },
      grid: { left: '3%', right: '4%', bottom: '15%', top: '10%', containLabel: true },
      xAxis: {
        type: 'category',
        data: top10.map((c) => c.campaignName?.slice(0, 15) + '...'),
        axisLabel: { rotate: 45, fontSize: 9 },
      },
      yAxis: [
        { type: 'value', name: 'Impresiones', axisLabel: { formatter: (v) => formatNumber(v) } },
        { type: 'value', name: 'CPM ($)', min: 0 },
      ],
      series: [
        {
          name: 'Impresiones',
          type: 'bar',
          data: top10.map((c) => c.impressions),
          itemStyle: { color: '#2c7be5' },
          barMaxWidth: 30,
        },
        {
          name: 'CPM',
          type: 'line',
          yAxisIndex: 1,
          data: top10.map((c) => c.cpm),
          lineStyle: { color: '#f5803e', width: 2 },
          itemStyle: { color: '#f5803e' },
        },
      ],
    };
  }, [campaigns]);

  if (!campaigns || campaigns.length === 0) return <div className="text-center py-5 text-muted">Sin datos</div>;
  return <ReactEChartsCore echarts={echarts} option={options} style={{ height: '300px' }} />;
};

// Colores por tipo de producto
const productTypeColors = {
  BRAND: '#2c7be5',
  CORPORATIVO: '#00d97e',
  EQUIPOS: '#f5803e',
  HOGAR: '#6b5eae',
  POSTPAGO: '#e63757',
  PREPAGO: '#27bcfd',
  OTRO: '#748194',
};

// Colores por plataforma
const platformColors = {
  META: '#1877f2',
  GOOGLE: '#34a853',
  TIKTOK: '#ff0050',
  LINKEDIN: '#0A66C2',
  ILLUMIN: '#7B61FF',
  X: '#14171a',
  UNKNOWN: '#748194',
};

// Gráfico de barras agrupadas por tipo de producto
const ProductTypeChart = ({ data }) => {
  const options = useMemo(() => {
    if (!data || data.length === 0) return {};

    // Agrupar por tipo de producto
    const productTypes = [...new Set(data.map(d => d.productType))].filter(p => p !== 'OTRO');
    
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params) => {
          let html = `<strong>${params[0].axisValue}</strong><br/>`;
          params.forEach(p => {
            html += `${p.marker} ${p.seriesName}: ${p.seriesName === 'Inversión' ? formatCurrency(p.value) : formatNumber(p.value)}<br/>`;
          });
          return html;
        },
      },
      legend: { bottom: 0, textStyle: { fontSize: 11 } },
      grid: { left: '3%', right: '4%', bottom: '15%', top: '10%', containLabel: true },
      xAxis: {
        type: 'category',
        data: productTypes,
        axisLabel: { fontSize: 11, fontWeight: 'bold' },
      },
      yAxis: [
        { type: 'value', name: 'Impresiones', axisLabel: { formatter: v => formatNumber(v) }, splitLine: { show: false } },
        { type: 'value', name: 'Inversión ($)', axisLabel: { formatter: v => '$' + formatNumber(v) } },
      ],
      series: [
        {
          name: 'Impresiones',
          type: 'bar',
          data: productTypes.map(pt => {
            const item = data.find(d => d.productType === pt);
            return {
              value: item?.impressions || 0,
              itemStyle: { color: productTypeColors[pt] || '#748194' },
            };
          }),
          barMaxWidth: 40,
        },
        {
          name: 'Inversión',
          type: 'line',
          yAxisIndex: 1,
          data: productTypes.map(pt => {
            const item = data.find(d => d.productType === pt);
            return item?.cost || 0;
          }),
          lineStyle: { color: '#e63757', width: 3 },
          itemStyle: { color: '#e63757' },
          symbol: 'circle',
          symbolSize: 8,
        },
      ],
    };
  }, [data]);

  if (!data || data.length === 0) return <div className="text-center py-5 text-muted">Sin datos por tipo de producto</div>;
  return <ReactEChartsCore echarts={echarts} option={options} style={{ height: '300px' }} />;
};

// Gráfico de donas para distribución Meta vs Google por tipo de producto
const PlatformDistributionChart = ({ data, metric = 'cost' }) => {
  const options = useMemo(() => {
    if (!data || data.length === 0) return {};

    // Agrupar por plataforma
    const byPlatform = {};
    data.forEach(d => {
      const platform = d.sourcePlatform || 'UNKNOWN';
      if (!byPlatform[platform]) byPlatform[platform] = 0;
      byPlatform[platform] += d[metric] || 0;
    });

    const pieData = Object.entries(byPlatform).map(([name, value]) => ({
      name: name === 'META' ? 'Meta Ads' : name === 'GOOGLE' ? 'Google Ads' : name === 'TIKTOK' ? 'TikTok Ads' : name === 'LINKEDIN' ? 'LinkedIn Ads' : name === 'X' ? 'X Ads' : name,
      value: Math.round(value * 100) / 100,
      itemStyle: { color: platformColors[name] || '#748194' },
    }));

    return {
      tooltip: {
        trigger: 'item',
        formatter: p => `${p.name}<br/>${metric === 'cost' ? formatCurrency(p.value) : formatNumber(p.value)} (${p.percent.toFixed(1)}%)`,
      },
      legend: { orient: 'vertical', right: 10, top: 'center', textStyle: { fontSize: 11 } },
      series: [{
        type: 'pie',
        radius: ['45%', '75%'],
        center: ['35%', '50%'],
        avoidLabelOverlap: false,
        itemStyle: { borderRadius: 8, borderColor: '#fff', borderWidth: 3 },
        label: { show: false },
        emphasis: { label: { show: true, fontSize: 14, fontWeight: 'bold' } },
        data: pieData,
      }],
    };
  }, [data, metric]);

  if (!data || data.length === 0) return <div className="text-center py-5 text-muted">Sin datos</div>;
  return <ReactEChartsCore echarts={echarts} option={options} style={{ height: '250px' }} />;
};

// Tabla de métricas por tipo de producto (agrupada) con ordenación y filtros
// Desempeño de Ad Groups / Conjuntos de anuncio AGRUPADOS por plataforma.
// Reutiliza data.adGroups (ya trae sourcePlatform, campaignName y métricas) — sin backend nuevo.
const AdGroupsByPlatformTable = ({ adGroups = [] }) => {
  const [q, setQ] = useState('');
  const [sortBy, setSortBy] = useState({ field: 'cost', direction: 'desc' });
  const [collapsed, setCollapsed] = useState({});
  const onSort = (field) =>
    setSortBy((s) => ({ field, direction: s.field === field && s.direction === 'desc' ? 'asc' : 'desc' }));

  const groups = useMemo(() => {
    const ql = q.trim().toLowerCase();
    const filtered = (adGroups || []).filter(
      (g) =>
        !ql ||
        (g.adGroupName || '').toLowerCase().includes(ql) ||
        (g.campaignName || '').toLowerCase().includes(ql),
    );
    const m = {};
    for (const g of filtered) {
      const p = (g.sourcePlatform || g.platform || 'OTRO').toUpperCase();
      (m[p] = m[p] || []).push(g);
    }
    const order = ['META', 'GOOGLE', 'TIKTOK', 'LINKEDIN', 'ILLUMIN', 'X', 'OTRO'];
    const rank = (p) => (order.indexOf(p) < 0 ? 99 : order.indexOf(p));
    return Object.keys(m)
      .sort((a, b) => rank(a) - rank(b))
      .map((p) => {
        const list = [...m[p]].sort((a, b) => {
          const av = a[sortBy.field] ?? 0;
          const bv = b[sortBy.field] ?? 0;
          return sortBy.direction === 'asc' ? av - bv : bv - av;
        });
        const sub = list.reduce(
          (acc, r) => ({
            impressions: acc.impressions + (r.impressions || 0),
            clicks: acc.clicks + (r.clicks || 0),
            cost: acc.cost + (r.cost || 0),
            leads: acc.leads + (r.leads || 0),
            reach: acc.reach + (r.reach || 0),
          }),
          { impressions: 0, clicks: 0, cost: 0, leads: 0, reach: 0 },
        );
        sub.ctr = sub.impressions > 0 ? (sub.clicks / sub.impressions) * 100 : 0;
        sub.cpc = sub.clicks > 0 ? sub.cost / sub.clicks : 0;
        sub.cpl = sub.leads > 0 ? sub.cost / sub.leads : 0;
        return { platform: p, list, sub };
      });
  }, [adGroups, q, sortBy]);

  const grand = useMemo(
    () =>
      groups.reduce(
        (acc, g) => ({
          impressions: acc.impressions + g.sub.impressions,
          clicks: acc.clicks + g.sub.clicks,
          cost: acc.cost + g.sub.cost,
          leads: acc.leads + g.sub.leads,
        }),
        { impressions: 0, clicks: 0, cost: 0, leads: 0 },
      ),
    [groups],
  );

  const totalAg = groups.reduce((n, g) => n + g.list.length, 0);
  if (totalAg === 0) {
    return <div className="text-center py-5 text-muted">Sin ad groups para el filtro actual</div>;
  }

  const SortH = ({ field, label }) => (
    <th style={{ cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => onSort(field)} className="text-end">
      {label}
      {sortBy.field === field && <span className="ms-1 text-primary">{sortBy.direction === 'asc' ? '↑' : '↓'}</span>}
    </th>
  );

  return (
    <div className="table-responsive">
      <div className="p-2">
        <Form.Control
          size="sm"
          placeholder="Filtrar ad group o campaña..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{ fontSize: '12px', maxWidth: 320 }}
        />
      </div>
      <Table hover size="sm" className="mb-0" style={{ fontSize: '12px' }}>
        <thead className="bg-light">
          <tr>
            <th style={{ minWidth: 240 }}>Ad Group / Conjunto</th>
            <SortH field="impressions" label="Impresiones" />
            <SortH field="reach" label="Alcance" />
            <SortH field="clicks" label="Clicks" />
            <SortH field="ctr" label="CTR" />
            <SortH field="cpc" label="CPC" />
            <SortH field="leads" label="Leads" />
            <SortH field="cpl" label="CPL" />
            <SortH field="cost" label="Inversión" />
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => {
            const isCol = collapsed[g.platform];
            return (
              <React.Fragment key={g.platform}>
                <tr
                  className="bg-light fw-bold"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setCollapsed((c) => ({ ...c, [g.platform]: !c[g.platform] }))}
                >
                  <td>
                    <span className="me-2">{isCol ? '▸' : '▾'}</span>
                    <PlatformBadge platform={g.platform} />
                    <span className="ms-2 text-muted">({g.list.length})</span>
                  </td>
                  <td className="text-end">{formatNumber(g.sub.impressions)}</td>
                  <td className="text-end">{formatNumber(g.sub.reach)}</td>
                  <td className="text-end">{formatNumber(g.sub.clicks)}</td>
                  <td className="text-end">{formatPercent(g.sub.ctr)}</td>
                  <td className="text-end">{formatCurrency(g.sub.cpc)}</td>
                  <td className="text-end">{formatNumber(g.sub.leads)}</td>
                  <td className="text-end">{formatCurrency(g.sub.cpl)}</td>
                  <td className="text-end fw-bold">{formatCurrency(g.sub.cost)}</td>
                </tr>
                {!isCol &&
                  g.list.map((ag, i) => (
                    <tr key={`${g.platform}-${i}`}>
                      <td style={{ paddingLeft: 28 }}>
                        <div className="fw-semibold text-truncate" style={{ maxWidth: 340 }} title={ag.adGroupName}>
                          {ag.adGroupName || '(sin nombre)'}
                        </div>
                        <small className="text-muted text-truncate d-block" style={{ maxWidth: 340 }} title={ag.campaignName}>
                          {ag.campaignName}
                        </small>
                      </td>
                      <td className="text-end">{formatNumber(ag.impressions)}</td>
                      <td className="text-end">{formatNumber(ag.reach)}</td>
                      <td className="text-end">{formatNumber(ag.clicks)}</td>
                      <td className="text-end">{formatPercent(ag.ctr)}</td>
                      <td className="text-end">{formatCurrency(ag.cpc)}</td>
                      <td className="text-end">{formatNumber(ag.leads)}</td>
                      <td className="text-end">{formatCurrency(ag.cpl)}</td>
                      <td className="text-end">{formatCurrency(ag.cost)}</td>
                    </tr>
                  ))}
              </React.Fragment>
            );
          })}
        </tbody>
        <tfoot className="bg-light fw-bold">
          <tr>
            <td>TOTAL</td>
            <td className="text-end">{formatNumber(grand.impressions)}</td>
            <td></td>
            <td className="text-end">{formatNumber(grand.clicks)}</td>
            <td className="text-end">{formatPercent(grand.impressions > 0 ? (grand.clicks / grand.impressions) * 100 : 0)}</td>
            <td></td>
            <td className="text-end">{formatNumber(grand.leads)}</td>
            <td className="text-end">{formatCurrency(grand.leads > 0 ? grand.cost / grand.leads : 0)}</td>
            <td className="text-end">{formatCurrency(grand.cost)}</td>
          </tr>
        </tfoot>
      </Table>
    </div>
  );
};

const ProductTypeTable = ({ data, byPlatform, sortBy = { field: 'cost', direction: 'desc' }, onSort, filters = {}, onFilterChange }) => {
  const filtered = useMemo(() => {
    if (!data?.length) return [];
    return data.filter(d => d.productType !== 'OTRO' && (!filters.productType || (d.productType || '').toLowerCase().includes((filters.productType || '').toLowerCase())));
  }, [data, filters.productType]);
  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const aVal = a[sortBy.field] ?? 0;
      const bVal = b[sortBy.field] ?? 0;
      return sortBy.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [filtered, sortBy]);

  if (sorted.length === 0 && (!data || data.length === 0)) {
    return <div className="text-center py-5 text-muted">Sin datos por tipo de producto</div>;
  }

  const totals = sorted.reduce((acc, row) => ({
    impressions: acc.impressions + (row.impressions || 0),
    clicks: acc.clicks + (row.clicks || 0),
    cost: acc.cost + (row.cost || 0),
    reach: acc.reach + (row.reach || 0),
    leads: acc.leads + (row.leads || 0),
    videoViews: acc.videoViews + (row.videoViews || 0),
  }), { impressions: 0, clicks: 0, cost: 0, reach: 0, leads: 0, videoViews: 0 });

  const SortHeaderLocal = ({ field, label }) => (
    <th style={{ cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => onSort?.(field)} className="text-end">
      {label}
      {sortBy.field === field && <span className="ms-1 text-primary">{sortBy.direction === 'asc' ? '↑' : '↓'}</span>}
    </th>
  );

  return (
    <div className="table-responsive">
      <Table hover size="sm" className="mb-0" style={{ fontSize: '12px' }}>
        <thead className="bg-light">
          <tr>
            <th style={{ minWidth: '120px' }}>Tipo Producto</th>
            <th className="text-center">Plataformas</th>
            <SortHeaderLocal field="impressions" label="Impresiones" />
            <SortHeaderLocal field="clicks" label="Clicks" />
            <SortHeaderLocal field="videoViews" label="Views" />
            <SortHeaderLocal field="ctr" label="CTR" />
            <SortHeaderLocal field="cpm" label="CPM" />
            <SortHeaderLocal field="cpc" label="CPC" />
            <SortHeaderLocal field="leads" label="Leads" />
            <SortHeaderLocal field="cpl" label="CPL" />
            <SortHeaderLocal field="cost" label="Inversión" />
            <th className="text-end">% del Total</th>
          </tr>
          <tr className="bg-white border-bottom">
            <th style={{ padding: '4px 6px' }}>
              <Form.Control size="sm" placeholder="Filtrar tipo..." value={filters.productType || ''} onChange={(e) => onFilterChange?.({ ...filters, productType: e.target.value })} style={{ fontSize: '11px' }} />
            </th>
            <th colSpan={11} className="text-muted small" style={{ padding: '4px 8px', verticalAlign: 'middle' }}>Filtros · {sorted.length} resultado(s)</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((row, idx) => {
            const percentOfTotal = totals.cost > 0 ? (row.cost / totals.cost * 100) : 0;
            // Buscar desglose por plataforma
            const platforms = byPlatform?.filter(p => p.productType === row.productType) || [];
            
            return (
              <tr key={idx}>
                <td>
                  <div className="d-flex align-items-center">
                    <div 
                      className="me-2 rounded" 
                      style={{ width: 12, height: 12, backgroundColor: productTypeColors[row.productType] }}
                    />
                    <span className="fw-semibold">{row.productType}</span>
                  </div>
                </td>
                <td className="text-center">
                  {platforms.map((p, i) => { const sp = p.sourcePlatform || p.platform || 'OTRO'; return (
                    <Badge 
                      key={i} 
                      bg={sp === 'META' ? 'primary' : sp === 'GOOGLE' ? 'success' : sp === 'LINKEDIN' ? 'info' : sp === 'TIKTOK' ? 'danger' : 'secondary'}
                      className="me-1"
                      style={{ fontSize: '10px' }}
                    >
                      {sp === 'META' ? 'Meta' : sp === 'GOOGLE' ? 'Google' : sp === 'LINKEDIN' ? 'LinkedIn' : sp === 'TIKTOK' ? 'TikTok' : sp}
                      <span className="ms-1">{formatCurrency(p.cost)}</span>
                    </Badge>
                  ); })}
                </td>
                <td className="text-end">{formatNumber(row.impressions)}</td>
                <td className="text-end">{formatNumber(row.clicks)}</td>
                <td className="text-end">{formatNumber(row.videoViews || 0)}</td>
                <td className="text-end">
                  <Badge bg={row.ctr > 1 ? 'success' : row.ctr > 0.5 ? 'warning' : 'secondary'}>
                    {formatPercent(row.ctr)}
                  </Badge>
                </td>
                <td className="text-end">{formatCurrency(row.cpm)}</td>
                <td className="text-end">{formatCurrency(row.cpc)}</td>
                <td className="text-end">{formatNumber(row.leads)}</td>
                <td className="text-end">{formatCurrency(row.cpl)}</td>
                <td className="text-end fw-bold" style={{ color: productTypeColors[row.productType] }}>
                  {formatCurrency(row.cost)}
                </td>
                <td className="text-end">
                  <div className="d-flex align-items-center justify-content-end">
                    <div 
                      className="progress me-2" 
                      style={{ width: 60, height: 6 }}
                    >
                      <div 
                        className="progress-bar" 
                        style={{ width: `${percentOfTotal}%`, backgroundColor: productTypeColors[row.productType] }}
                      />
                    </div>
                    <span className="text-muted small">{percentOfTotal.toFixed(1)}%</span>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot className="bg-light fw-bold">
          <tr>
            <td>TOTAL</td>
            <td></td>
            <td className="text-end">{formatNumber(totals.impressions)}</td>
            <td className="text-end">{formatNumber(totals.clicks)}</td>
            <td className="text-end">{formatNumber(totals.videoViews)}</td>
            <td className="text-end">
              {formatPercent(totals.impressions > 0 ? (totals.clicks / totals.impressions * 100) : 0)}
            </td>
            <td className="text-end">{formatCurrency(totals.impressions > 0 ? (totals.cost / totals.impressions) * 1000 : 0)}</td>
            <td className="text-end">{formatCurrency(totals.clicks > 0 ? totals.cost / totals.clicks : 0)}</td>
            <td className="text-end">{formatNumber(totals.leads)}</td>
            <td className="text-end">{formatCurrency(totals.leads > 0 ? totals.cost / totals.leads : 0)}</td>
            <td className="text-end text-primary">{formatCurrency(totals.cost)}</td>
            <td className="text-end">100%</td>
          </tr>
        </tfoot>
      </Table>
    </div>
  );
};

// Cabecera ordenable reutilizable
const SortHeader = ({ field, label, sortBy, onSort, className = 'text-end' }) => (
  <th style={{ cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => onSort(field)} className={className}>
    {label}
    {sortBy.field === field && (
      <span className="ms-1 text-primary">{sortBy.direction === 'asc' ? '↑' : '↓'}</span>
    )}
  </th>
);

// Etiqueta y formato de la métrica primaria por objetivo (Claro)
const PRIMARY_METRIC_LABEL = {
  WHATSAPP: { label: 'Conversaciones', color: '#25d366', fmt: formatNumber },
  LEADS: { label: 'Leads', color: '#6b5eae', fmt: formatNumber },
  TRAFICO: { label: 'Link Clics', color: '#27bcfd', fmt: formatNumber },
  AWARENESS: { label: 'Impresiones', color: '#2c7be5', fmt: formatNumber },
  VIDEO: { label: 'ThruPlays', color: '#fd7e14', fmt: formatNumber },
};

// Tabla de Campañas estilo Looker (con filtros y ordenación)
const CampaignTreeTable = ({ campaigns, adGroups, ads, sortBy, onSort, filters = {}, onFilterChange, isClaro = false }) => {
  const [expandedCampaigns, setExpandedCampaigns] = useState({});
  const [expandedAdGroups, setExpandedAdGroups] = useState({});
  const [expandAll, setExpandAll] = useState(false);

  const f = filters || {};
  const filtered = useMemo(() => {
    if (!campaigns?.length) return [];
    return campaigns.filter((c) => {
      if (f.accountName && !(c.accountName || '').toLowerCase().includes((f.accountName || '').toLowerCase())) return false;
      if (f.campaignName && !(c.campaignName || '').toLowerCase().includes((f.campaignName || '').toLowerCase())) return false;
      if (f.productType && (c.productType || '').toLowerCase() !== (f.productType || '').toLowerCase()) return false;
      const cPlat = (c.sourcePlatform || c.platform || '').toLowerCase();
      if (f.platform && cPlat !== (f.platform || '').toLowerCase()) return false;
      return true;
    });
  }, [campaigns, f.accountName, f.campaignName, f.productType, f.platform]);

  const agByCampaign = useMemo(() => {
    const map = {};
    (adGroups || []).forEach(ag => {
      if (!map[ag.campaignKey]) map[ag.campaignKey] = [];
      map[ag.campaignKey].push(ag);
    });
    Object.values(map).forEach(arr => arr.sort((a, b) => (b.cost || 0) - (a.cost || 0)));
    return map;
  }, [adGroups]);

  const adsByAdGroup = useMemo(() => {
    const map = {};
    (ads || []).forEach(ad => {
      if (!map[ad.adGroupKey]) map[ad.adGroupKey] = [];
      map[ad.adGroupKey].push(ad);
    });
    Object.values(map).forEach(arr => arr.sort((a, b) => (b.cost || 0) - (a.cost || 0)));
    return map;
  }, [ads]);

  const toggleCampaign = (key) => {
    setExpandedCampaigns(prev => ({ ...prev, [key]: !prev[key] }));
  };
  const toggleAdGroup = (key) => {
    setExpandedAdGroups(prev => ({ ...prev, [key]: !prev[key] }));
  };
  const toggleAll = () => {
    const next = !expandAll;
    setExpandAll(next);
    if (next) {
      const ec = {};
      const ea = {};
      filtered.forEach(c => {
        ec[c.campaignKey] = true;
        (agByCampaign[c.campaignKey] || []).forEach(ag => { ea[ag.adGroupKey] = true; });
      });
      setExpandedCampaigns(ec);
      setExpandedAdGroups(ea);
    } else {
      setExpandedCampaigns({});
      setExpandedAdGroups({});
    }
  };

  const totalCamps = filtered.length;
  const totalAg = filtered.reduce((s, c) => s + (agByCampaign[c.campaignKey]?.length || 0), 0);
  const totalAds = filtered.reduce((s, c) => {
    return s + (agByCampaign[c.campaignKey] || []).reduce((s2, ag) => s2 + (adsByAdGroup[ag.adGroupKey]?.length || 0), 0);
  }, 0);

  const metricCell = (val, fmt = formatNumber) => <td className="text-end">{val > 0 ? fmt(val) : <span className="text-muted">—</span>}</td>;
  // Celda de Leads con desglose por tipo en tooltip (hover) — funciona hasta nivel ad
  const buildLeadsTitle = (b) => {
    if (!b) return undefined;
    const p = [];
    if (b.quote) p.push(`Cotiz. ${formatNumber(b.quote)}`);
    if (b.sale) p.push(`Ventas ${formatNumber(b.sale)}`);
    if (b.submit) p.push(`Lead forms ${formatNumber(b.submit)}`);
    if (b.onsite) p.push(`Onsite ${formatNumber(b.onsite)}`);
    if (b.webReg) p.push(`Reg. web ${formatNumber(b.webReg)}`);
    if (b.fbPixel) p.push(`FbPixel ${formatNumber(b.fbPixel)}`);
    if (b.purchase) p.push(`Compras ${formatNumber(b.purchase)}`);
    if (b.offsite) p.push(`Offsite ${formatNumber(b.offsite)}`);
    return p.length ? `Desglose de leads — ${p.join(' · ')}` : undefined;
  };
  const leadsCell = (val, breakdown) => {
    const title = buildLeadsTitle(breakdown);
    return <td className="text-end" title={title} style={title ? { cursor: 'help', textDecoration: 'underline dotted' } : undefined}>{val > 0 ? formatNumber(val) : <span className="text-muted">—</span>}</td>;
  };
  const ctrBadge = (val) => (
    <td className="text-end">
      <Badge bg={val > 1 ? 'success' : val > 0.5 ? 'warning' : 'secondary'}>{formatPercent(val)}</Badge>
    </td>
  );

  if (filtered.length === 0 && (!campaigns || campaigns.length === 0)) {
    return <div className="text-center py-5 text-muted">Sin campañas para mostrar</div>;
  }

  const COL_COUNT = isClaro ? 14 : 13;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center px-3 py-2 bg-light border-bottom" style={{ fontSize: 12 }}>
        <div>
          <span className="fw-semibold">{totalCamps}</span> campañas
          {totalAg > 0 && <> · <span className="fw-semibold">{totalAg}</span> ad groups</>}
          {totalAds > 0 && <> · <span className="fw-semibold">{totalAds}</span> anuncios</>}
        </div>
        <Button variant="outline-secondary" size="sm" onClick={toggleAll} style={{ fontSize: 11, padding: '2px 10px' }}>
          {expandAll ? '⊟ Colapsar todo' : '⊞ Expandir todo'}
        </Button>
      </div>
      <div className="table-responsive" style={{ maxHeight: '600px', overflowY: 'auto' }}>
        <Table hover size="sm" className="mb-0" style={{ fontSize: '12px' }}>
          <thead className="bg-light sticky-top">
            <tr>
              <th style={{ minWidth: '300px' }}>Campaña / Ad Group / Anuncio</th>
              <th className="text-center" style={{ width: 70 }}>Plataforma</th>
              {isClaro && (
                <SortHeader field="primaryValue" label={<span title="Resultado principal">Resultado ⓘ</span>} sortBy={sortBy} onSort={onSort} />
              )}
              <SortHeader field="impressions" label="Impresiones" sortBy={sortBy} onSort={onSort} />
              <SortHeader field="reach" label="Alcance" sortBy={sortBy} onSort={onSort} />
              <SortHeader field="clicks" label="Clics" sortBy={sortBy} onSort={onSort} />
              <SortHeader field="ctr" label="CTR" sortBy={sortBy} onSort={onSort} />
              <SortHeader field="cpm" label="CPM" sortBy={sortBy} onSort={onSort} />
              <SortHeader field="cpc" label="CPC" sortBy={sortBy} onSort={onSort} />
              <SortHeader field="leads" label="Leads" sortBy={sortBy} onSort={onSort} />
              <SortHeader field="cpl" label="CPL" sortBy={sortBy} onSort={onSort} />
              <SortHeader field="whatsappConversations" label={<span title="WhatsApp Conv.">WA ⓘ</span>} sortBy={sortBy} onSort={onSort} />
              <SortHeader field="cost" label="Inversión" sortBy={sortBy} onSort={onSort} />
            </tr>
            <tr className="bg-white border-bottom">
              <th style={{ padding: '4px 6px' }}>
                <div className="d-flex gap-1">
                  <Form.Control size="sm" placeholder="Campaña..." value={f.campaignName || ''} onChange={(e) => onFilterChange?.({ ...filters, campaignName: e.target.value })} style={{ fontSize: '10px', flex: 1 }} />
                  <Form.Control size="sm" placeholder="Cuenta..." value={f.accountName || ''} onChange={(e) => onFilterChange?.({ ...filters, accountName: e.target.value })} style={{ fontSize: '10px', flex: 1 }} />
                </div>
              </th>
              <th style={{ padding: '4px 6px' }}>
                <Form.Select size="sm" value={f.platform || ''} onChange={(e) => onFilterChange?.({ ...filters, platform: e.target.value })} style={{ fontSize: '10px' }}>
                  <option value="">Todas</option>
                  <option value="META">Meta</option>
                  <option value="GOOGLE">Google</option>
                  <option value="TIKTOK">TikTok</option>
                  <option value="LINKEDIN">LinkedIn</option>
                  <option value="ILLUMIN">Illumin (CTV)</option>
                  <option value="X">X</option>
                </Form.Select>
              </th>
              <th colSpan={COL_COUNT - 2} className="text-muted small" style={{ padding: '4px 8px' }}>
                {filtered.length} resultado(s)
              </th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => {
              const cKey = c.campaignKey;
              const isExpC = expandedCampaigns[cKey];
              const campAgs = agByCampaign[cKey] || [];
              const sp = c.sourcePlatform || c.platform || 'OTRO';
              return (
                <React.Fragment key={cKey}>
                  {/* Campaign row */}
                  <tr style={{ backgroundColor: '#f8f9fc', cursor: campAgs.length > 0 ? 'pointer' : 'default' }}
                    onClick={() => campAgs.length > 0 && toggleCampaign(cKey)}>
                    <td className="fw-semibold" style={{ paddingLeft: 8 }}>
                      <span style={{ display: 'inline-block', width: 18, textAlign: 'center', fontSize: 11, color: '#6b7280' }}>
                        {campAgs.length > 0 ? (isExpC ? '▼' : '▶') : '●'}
                      </span>
                      <span className="d-inline-block" style={{ maxWidth: 220, verticalAlign: 'middle' }} title={c.campaignName}>
                        {c.campaignName}
                      </span>
                      {c.productType && c.productType !== 'OTRO' && (
                        <Badge className="ms-1" style={{ backgroundColor: productTypeColors[c.productType] || '#748194', fontSize: '8px', verticalAlign: 'middle' }}>
                          {c.productType}
                        </Badge>
                      )}
                      <small className="text-muted ms-1 d-none d-lg-inline" style={{ fontSize: 10 }}>{c.accountName}</small>
                    </td>
                    <td className="text-center">
                      <Badge bg={sp === 'META' ? 'primary' : sp === 'GOOGLE' ? 'success' : sp === 'LINKEDIN' ? 'info' : sp === 'TIKTOK' ? 'danger' : 'secondary'} style={{ fontSize: '9px' }}>
                        {sp === 'META' ? 'Meta' : sp === 'GOOGLE' ? 'Google' : sp === 'LINKEDIN' ? 'LinkedIn' : sp === 'TIKTOK' ? 'TikTok' : sp}
                      </Badge>
                    </td>
                    {isClaro && (() => {
                      const cfg = PRIMARY_METRIC_LABEL[c.campaignObjective];
                      if (!cfg) return <td className="text-end text-muted">—</td>;
                      const val = c.primaryValue ?? 0;
                      return <td className="text-end fw-bold" style={{ color: cfg.color }}>{val > 0 ? cfg.fmt(val) : '—'}</td>;
                    })()}
                    {metricCell(c.impressions)}
                    {metricCell(c.reach)}
                    {metricCell(c.clicks)}
                    {ctrBadge(c.ctr)}
                    {metricCell(c.cpm, formatCurrency)}
                    {metricCell(c.cpc, formatCurrency)}
                    {leadsCell(c.leads, c.leadsBreakdown)}
                    {metricCell(c.leads > 0 ? c.cost / c.leads : 0, formatCurrency)}
                    <td className="text-end" style={{ color: c.whatsappConversations > 0 ? '#25d366' : undefined }}>
                      {c.whatsappConversations > 0 ? formatNumber(c.whatsappConversations) : <span className="text-muted">—</span>}
                    </td>
                    <td className="text-end fw-semibold text-primary">{formatCurrency(c.cost)}</td>
                  </tr>
                  {/* Ad Group children */}
                  {isExpC && campAgs.map((ag) => {
                    const agKey = ag.adGroupKey;
                    const isExpAg = expandedAdGroups[agKey];
                    const agAds = adsByAdGroup[agKey] || [];
                    return (
                      <React.Fragment key={agKey}>
                        <tr style={{ backgroundColor: '#f0f4ff', cursor: agAds.length > 0 ? 'pointer' : 'default' }}
                          onClick={(e) => { e.stopPropagation(); agAds.length > 0 && toggleAdGroup(agKey); }}>
                          <td style={{ paddingLeft: 32 }}>
                            <span style={{ display: 'inline-block', width: 18, textAlign: 'center', fontSize: 10, color: '#6b7280' }}>
                              {agAds.length > 0 ? (isExpAg ? '▼' : '▶') : '○'}
                            </span>
                            <span className="text-truncate d-inline-block" style={{ maxWidth: 200, verticalAlign: 'middle', fontWeight: 500 }} title={ag.adGroupName}>
                              {ag.adGroupName}
                            </span>
                            <small className="text-muted ms-1" style={{ fontSize: 10 }}>[{agAds.length} ads]</small>
                          </td>
                          <td></td>
                          {isClaro && <td></td>}
                          {metricCell(ag.impressions)}
                          {metricCell(ag.reach)}
                          {metricCell(ag.clicks)}
                          {ctrBadge(ag.ctr)}
                          {metricCell(ag.cpm, formatCurrency)}
                          {metricCell(ag.cpc, formatCurrency)}
                          {leadsCell(ag.leads, ag.leadsBreakdown)}
                          {metricCell(ag.cpl, formatCurrency)}
                          <td className="text-end" style={{ color: ag.whatsappConversations > 0 ? '#25d366' : undefined }}>
                            {ag.whatsappConversations > 0 ? formatNumber(ag.whatsappConversations) : <span className="text-muted">—</span>}
                          </td>
                          <td className="text-end fw-semibold" style={{ color: '#6366f1' }}>{formatCurrency(ag.cost)}</td>
                        </tr>
                        {/* Ad children */}
                        {isExpAg && agAds.map((ad) => (
                          <tr key={ad.adKey} style={{ backgroundColor: '#fafbff' }}>
                            <td style={{ paddingLeft: 56 }}>
                              {ad.adKey ? (
                                <img src={getCreativeProxyUrl(ad)} alt="" style={{ width: 24, height: 24, objectFit: 'cover', borderRadius: 3, marginRight: 6, verticalAlign: 'middle' }}
                                  onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }} />
                              ) : (
                                <span style={{ display: 'inline-block', width: 24, textAlign: 'center', fontSize: 12, color: '#9ca3af', marginRight: 6 }}>📄</span>
                              )}
                              <span className="text-truncate d-inline-block" style={{ maxWidth: 190, verticalAlign: 'middle', fontSize: 11 }} title={ad.adName || adDisplayName(ad)}>
                                {adDisplayName(ad)}
                              </span>
                              {(!ad.adName || !ad.adName.trim()) && (
                                <Badge bg="light" text="muted" className="ms-1" style={{ fontSize: 7, border: '1px solid #e5e7eb', verticalAlign: 'middle' }}>?</Badge>
                              )}
                            </td>
                            <td></td>
                            {isClaro && <td></td>}
                            {metricCell(ad.impressions)}
                            {metricCell(ad.reach)}
                            {metricCell(ad.clicks)}
                            {ctrBadge(ad.ctr)}
                            {metricCell(ad.cpm, formatCurrency)}
                            {metricCell(ad.cpc, formatCurrency)}
                            {leadsCell(ad.leads, ad.leadsBreakdown)}
                            {metricCell(ad.cpl, formatCurrency)}
                            <td className="text-end" style={{ color: ad.whatsappConversations > 0 ? '#25d366' : undefined }}>
                              {ad.whatsappConversations > 0 ? formatNumber(ad.whatsappConversations) : <span className="text-muted">—</span>}
                            </td>
                            <td className="text-end" style={{ color: '#818cf8' }}>{formatCurrency(ad.cost)}</td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
          <tfoot className="bg-light fw-bold">
            <tr>
              <td colSpan={2}>Total ({filtered.length} campañas)</td>
              {isClaro && <td></td>}
              <td className="text-end">{formatNumber(filtered.reduce((s, c) => s + c.impressions, 0))}</td>
              <td className="text-end">{formatNumber(filtered.reduce((s, c) => s + c.reach, 0))}</td>
              <td className="text-end">{formatNumber(filtered.reduce((s, c) => s + c.clicks, 0))}</td>
              <td className="text-end">{formatPercent((filtered.reduce((s, c) => s + c.clicks, 0) / (filtered.reduce((s, c) => s + c.impressions, 0) || 1)) * 100)}</td>
              <td className="text-end">{formatCurrency(filtered.reduce((s, c) => s + c.cost, 0) / (filtered.reduce((s, c) => s + c.impressions, 0) / 1000 || 1))}</td>
              <td className="text-end">{(() => { const tc = filtered.reduce((s, c) => s + c.clicks, 0); const tco = filtered.reduce((s, c) => s + c.cost, 0); return tc > 0 ? formatCurrency(tco / tc) : '—'; })()}</td>
              <td className="text-end">{formatNumber(filtered.reduce((s, c) => s + c.leads, 0))}</td>
              <td className="text-end">{(() => { const tl = filtered.reduce((s, c) => s + c.leads, 0); const tco = filtered.reduce((s, c) => s + c.cost, 0); return tl > 0 ? formatCurrency(tco / tl) : '—'; })()}</td>
              <td className="text-end" style={{ color: '#25d366' }}>{formatNumber(filtered.reduce((s, c) => s + (c.whatsappConversations || 0), 0))}</td>
              <td className="text-end text-primary">{formatCurrency(filtered.reduce((s, c) => s + c.cost, 0))}</td>
            </tr>
          </tfoot>
        </Table>
      </div>
    </div>
  );
};

const CampaignTable = ({ campaigns, sortBy, onSort, filters = {}, onFilterChange, isClaro = false }) => {
  const f = filters || {};
  const filtered = useMemo(() => {
    if (!campaigns?.length) return [];
    return campaigns.filter((c) => {
      if (f.accountName && !(c.accountName || '').toLowerCase().includes((f.accountName || '').toLowerCase())) return false;
      if (f.campaignName && !(c.campaignName || '').toLowerCase().includes((f.campaignName || '').toLowerCase())) return false;
      if (f.productType && (c.productType || '').toLowerCase() !== (f.productType || '').toLowerCase()) return false;
      const cPlat = (c.sourcePlatform || c.platform || '').toLowerCase();
      if (f.platform && cPlat !== (f.platform || '').toLowerCase()) return false;
      return true;
    });
  }, [campaigns, f.campaignName, f.productType, f.platform]);

  if (filtered.length === 0 && (!campaigns || campaigns.length === 0)) {
    return <div className="text-center py-5 text-muted">Sin campañas para mostrar</div>;
  }

  return (
    <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
      <Table hover size="sm" className="mb-0" style={{ fontSize: '12px' }}>
        <thead className="bg-light sticky-top">
          <tr>
            <th style={{ minWidth: '140px' }}>Cuenta</th>
            <th style={{ minWidth: '180px' }}>Campaña</th>
            <th className="text-center">Tipo</th>
            <th className="text-center">Plataforma</th>
            {isClaro && (
              <SortHeader field="primaryValue" label={<span title="Métrica principal según objetivo: CPW→Conversaciones, CPL→Leads, CPC→Link Clics, CPM→Impresiones, CPV→ThruPlays">Resultado ⓘ</span>} sortBy={sortBy} onSort={onSort} />
            )}
            <SortHeader field="impressions" label="Impresiones" sortBy={sortBy} onSort={onSort} />
            <SortHeader field="reach" label={<span title="Suma de alcance diario (no es unique users del periodo). Meta/TikTok por anuncio; Google (usuarios únicos) y X por campaña y día.">Alcance ⓘ</span>} sortBy={sortBy} onSort={onSort} />
            <SortHeader field="cpm" label="CPM" sortBy={sortBy} onSort={onSort} />
            <SortHeader field="clicks" label="Clics" sortBy={sortBy} onSort={onSort} />
            <SortHeader field="linkClicks" label="Link Clics" sortBy={sortBy} onSort={onSort} />
            <SortHeader field="ctr" label="CTR" sortBy={sortBy} onSort={onSort} />
            <SortHeader field="cpc" label="CPC" sortBy={sortBy} onSort={onSort} />
            <SortHeader field="linkCpc" label="Link CPC" sortBy={sortBy} onSort={onSort} />
            <SortHeader field="leads" label="Leads" sortBy={sortBy} onSort={onSort} />
            <SortHeader field="cpl" label="CPL" sortBy={sortBy} onSort={onSort} />
            <SortHeader field="whatsappConversations" label={<span title="Conv. iniciadas WhatsApp (Meta · ventana 7 días post-clic). Solo aplica a campañas CPW.">WA Conv. ⓘ</span>} sortBy={sortBy} onSort={onSort} />
            <SortHeader field="cost" label="Inversión" sortBy={sortBy} onSort={onSort} />
          </tr>
          <tr className="bg-white border-bottom">
            <th style={{ padding: '4px 6px' }}>
              <Form.Control
                size="sm"
                placeholder="Cuenta..."
                value={f.accountName || ''}
                onChange={(e) => onFilterChange?.({ ...filters, accountName: e.target.value })}
                style={{ fontSize: '11px', minWidth: '80px' }}
              />
            </th>
            <th style={{ padding: '4px 6px' }}>
              <Form.Control
                size="sm"
                placeholder="Filtrar..."
                value={f.campaignName || ''}
                onChange={(e) => onFilterChange?.({ ...filters, campaignName: e.target.value })}
                style={{ fontSize: '11px', minWidth: '100px' }}
              />
            </th>
            <th style={{ padding: '4px 6px' }}>
              <Form.Control
                size="sm"
                placeholder="Tipo"
                value={f.productType || ''}
                onChange={(e) => onFilterChange?.({ ...filters, productType: e.target.value })}
                style={{ fontSize: '11px', minWidth: '70px' }}
              />
            </th>
            <th style={{ padding: '4px 6px' }}>
              <Form.Select
                size="sm"
                value={f.platform || ''}
                onChange={(e) => onFilterChange?.({ ...filters, platform: e.target.value })}
                style={{ fontSize: '11px', minWidth: '80px' }}
              >
                <option value="">Todas</option>
                <option value="META">Meta</option>
                <option value="GOOGLE">Google</option>
                <option value="TIKTOK">TikTok</option>
                <option value="LINKEDIN">LinkedIn</option>
                <option value="ILLUMIN">Illumin (CTV)</option>
                <option value="X">X</option>
              </Form.Select>
            </th>
            <th colSpan={isClaro ? 12 : 11} className="text-muted small" style={{ padding: '4px 8px', verticalAlign: 'middle' }}>
              Filtros de columna · {filtered.length} resultado(s)
            </th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((c, idx) => (
            <tr key={c.campaignKey || idx}>
              <td className="text-truncate text-muted" style={{ maxWidth: '140px', fontSize: '11px' }} title={c.accountName}>
                {c.accountName}
              </td>
              <td className="fw-medium text-truncate" style={{ maxWidth: '180px' }} title={c.campaignName}>
                {c.campaignName}
              </td>
              <td className="text-center">
                {c.productType && c.productType !== 'OTRO' && (
                  <Badge
                    className="px-2"
                    style={{
                      backgroundColor: productTypeColors[c.productType] || '#748194',
                      fontSize: '9px',
                    }}
                  >
                    {c.productType}
                  </Badge>
                )}
              </td>
              <td className="text-center">
                {(() => { const sp = c.sourcePlatform || c.platform || 'OTRO'; return (
                <Badge
                  bg={sp === 'META' ? 'primary' : sp === 'GOOGLE' ? 'success' : sp === 'LINKEDIN' ? 'info' : sp === 'TIKTOK' ? 'danger' : 'secondary'}
                  style={{ fontSize: '9px' }}
                >
                  {sp === 'META' ? 'Meta' : sp === 'GOOGLE' ? 'Google' : sp === 'LINKEDIN' ? 'LinkedIn' : sp === 'TIKTOK' ? 'TikTok' : sp}
                </Badge>
                ); })()}
              </td>
              {isClaro && (() => {
                const cfg = PRIMARY_METRIC_LABEL[c.campaignObjective];
                if (!cfg) return <td className="text-end text-muted">—</td>;
                const val = c.primaryValue ?? 0;
                return (
                  <td className="text-end fw-bold" style={{ color: cfg.color }} title={cfg.label}>
                    {val > 0 ? cfg.fmt(val) : <span className="text-muted">—</span>}
                  </td>
                );
              })()}
              <td className="text-end">{formatNumber(c.impressions)}</td>
              <td className="text-end">{c.reach > 0 ? formatNumber(c.reach) : <span className="text-muted">—</span>}</td>
              <td className="text-end">{formatCurrency(c.cpm)}</td>
              <td className="text-end">{formatNumber(c.clicks)}</td>
              <td className="text-end">{formatNumber(c.linkClicks || 0)}</td>
              <td className="text-end">
                <Badge bg={c.ctr > 1 ? 'success' : c.ctr > 0.5 ? 'warning' : 'secondary'}>{formatPercent(c.ctr)}</Badge>
              </td>
              <td className="text-end">{formatCurrency(c.cpc || 0)}</td>
              <td className="text-end">{formatCurrency(c.linkCpc || 0)}</td>
              <td className="text-end">{formatNumber(c.leads)}</td>
              <td className="text-end">{formatCurrency(c.leads > 0 ? c.cost / c.leads : 0)}</td>
              <td className="text-end" style={{ color: c.whatsappConversations > 0 ? '#25d366' : undefined }}>
                {c.whatsappConversations > 0 ? formatNumber(c.whatsappConversations) : <span className="text-muted">—</span>}
              </td>
              <td className="text-end fw-semibold text-primary">{formatCurrency(c.cost)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot className="bg-light fw-bold">
          <tr>
            <td colSpan={4}>Total ({filtered.length} campañas)</td>
            {isClaro && (
              <td className="text-end" title="Suma del resultado principal por objetivo (mezcla métricas distintas: conversaciones + leads + clics, etc.)">
                <span className="text-muted small">—</span>
              </td>
            )}
            <td className="text-end">{formatNumber(filtered.reduce((s, c) => s + c.impressions, 0))}</td>
            <td className="text-end">{formatNumber(filtered.reduce((s, c) => s + c.reach, 0))}</td>
            <td className="text-end">
              {formatCurrency(
                filtered.reduce((s, c) => s + c.cost, 0) / (filtered.reduce((s, c) => s + c.impressions, 0) / 1000) || 0
              )}
            </td>
            <td className="text-end">{formatNumber(filtered.reduce((s, c) => s + c.clicks, 0))}</td>
            <td className="text-end">{formatNumber(filtered.reduce((s, c) => s + (c.linkClicks || 0), 0))}</td>
            <td className="text-end">
              {formatPercent(
                (filtered.reduce((s, c) => s + c.clicks, 0) / (filtered.reduce((s, c) => s + c.impressions, 0) || 1)) * 100
              )}
            </td>
            <td className="text-end">
              {(() => {
                const tc = filtered.reduce((s, c) => s + c.clicks, 0);
                const tcost = filtered.reduce((s, c) => s + c.cost, 0);
                return tc > 0 ? formatCurrency(tcost / tc) : '—';
              })()}
            </td>
            <td className="text-end">
              {(() => {
                const tlc = filtered.reduce((s, c) => s + (c.linkClicks || 0), 0);
                const tcost = filtered.reduce((s, c) => s + c.cost, 0);
                return tlc > 0 ? formatCurrency(tcost / tlc) : '—';
              })()}
            </td>
            <td className="text-end">{formatNumber(filtered.reduce((s, c) => s + c.leads, 0))}</td>
            <td className="text-end">
              {(() => {
                const totalLeads = filtered.reduce((s, c) => s + (c.leads || 0), 0);
                const totalCost = filtered.reduce((s, c) => s + c.cost, 0);
                return totalLeads > 0 ? formatCurrency(totalCost / totalLeads) : '—';
              })()}
            </td>
            <td className="text-end" style={{ color: '#25d366' }}>
              {formatNumber(filtered.reduce((s, c) => s + (c.whatsappConversations || 0), 0))}
            </td>
            <td className="text-end text-primary">{formatCurrency(filtered.reduce((s, c) => s + c.cost, 0))}</td>
          </tr>
        </tfoot>
      </Table>
    </div>
  );
};

// ── AI Decision Signals (persisted) ──
const AISignals = ({ kpis, campaigns, clientName, dateRange, clientId, dateFrom, dateTo, filters: appliedFilters }) => {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingSaved, setLoadingSaved] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const dfStr = dateFrom instanceof Date ? toLocalDateString(dateFrom) : dateFrom;
  const dtStr = dateTo instanceof Date ? toLocalDateString(dateTo) : dateTo;

  useEffect(() => {
    if (!clientId || !dfStr || !dtStr) { setLoadingSaved(false); return; }
    const load = async () => {
      try {
        const res = await budgetService.getSavedDashboardInsights({ clientId, dateFrom: dfStr, dateTo: dtStr });
        if (res.success && res.data) { setInsights(res.data); setLastUpdated(res.data.createdAt); }
      } catch {}
      finally { setLoadingSaved(false); }
    };
    load();
  }, [clientId, dfStr, dtStr]);

  const generateInsights = useCallback(async () => {
    if (!kpis || !kpis.impressions) return;
    setLoading(true);
    setError(null);
    try {
      const res = await budgetService.getDashboardInsights({
        kpis, campaigns: campaigns?.slice(0, 10), clientName, dateRange,
        clientId, dateFrom: dfStr, dateTo: dtStr, filters: appliedFilters,
      });
      if (res.success) { setInsights(res.data); setLastUpdated(new Date().toISOString()); }
      else setError(res.error);
    } catch (e) { setError('Error al conectar con IA'); }
    finally { setLoading(false); }
  }, [kpis, campaigns, clientName, dateRange, clientId, dfStr, dtStr, appliedFilters]);

  const signalIcons = { warning: 'exclamation-triangle', success: 'check-circle', info: 'info-circle', action: 'bolt' };
  const signalVariants = { warning: 'warning', success: 'success', info: 'primary', action: 'info' };

  const scoreColor = (s) => s >= 70 ? '#00d97e' : s >= 40 ? '#f5803e' : '#e63757';
  const scoreLabel = (s) => s >= 70 ? 'Saludable' : s >= 40 ? 'Atención' : 'Crítico';

  if (loadingSaved) return null;

  return (
    <Card className="shadow-sm border-0 h-100">
      <Card.Header className="bg-body-tertiary d-flex justify-content-between align-items-center py-2">
        <div className="d-flex align-items-center gap-2">
          <span className="fas fa-brain text-primary"></span>
          <h6 className="mb-0 fw-semibold" style={{ fontSize: 13 }}>Señales de Decisión · IA</h6>
          {insights?.score != null && (
            <Badge pill bg="" style={{ backgroundColor: scoreColor(insights.score), fontSize: 10, padding: '4px 8px' }}>
              {insights.score}/100 · {scoreLabel(insights.score)}
            </Badge>
          )}
        </div>
        <div className="d-flex align-items-center gap-2">
          {lastUpdated && <small className="text-muted" style={{ fontSize: 10 }}>{new Date(lastUpdated).toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' })}</small>}
          <Button size="sm" variant={insights ? 'falcon-default' : 'primary'} className="px-3" onClick={generateInsights} disabled={loading || !kpis?.impressions}>
            {loading ? <Spinner size="sm" animation="border" /> : <><i className={`fas fa-${insights ? 'sync-alt' : 'magic'} me-1`}></i>{insights ? 'Actualizar' : 'Analizar'}</>}
          </Button>
        </div>
      </Card.Header>
      <Card.Body className="py-2">
        {!insights && !loading && !error && (
          <div className="text-center py-4">
            <div className="rounded-circle d-inline-flex align-items-center justify-content-center mb-2" style={{ width: 48, height: 48, backgroundColor: '#e8f0fe' }}>
              <i className="fas fa-brain text-primary fs-4"></i>
            </div>
            <p className="mb-0 text-muted" style={{ fontSize: 12 }}>Genera un análisis inteligente de tus métricas para obtener recomendaciones accionables.</p>
          </div>
        )}
        {error && <div className="alert alert-warning py-2 mb-0" style={{ fontSize: 12 }}><i className="fas fa-exclamation-circle me-1"></i>{error}</div>}
        {loading && (
          <div className="text-center py-4">
            <Spinner animation="border" variant="primary" size="sm" />
            <p className="mt-2 text-muted mb-0" style={{ fontSize: 12 }}>Analizando con IA...</p>
          </div>
        )}
        {insights && !loading && (
          <>
            {insights.summary && (
              <div className="bg-light rounded p-2 mb-3">
                <p className="mb-0 text-800" style={{ fontSize: 12 }}>{insights.summary}</p>
              </div>
            )}
            <div className="d-flex flex-column gap-2">
              {(insights.signals || []).map((signal, i) => (
                <div key={i} className={`border-start border-3 border-${signalVariants[signal.type] || 'secondary'} rounded-end bg-white p-2 shadow-sm`}>
                  <div className="d-flex align-items-start gap-2">
                    <span className={`fas fa-${signalIcons[signal.type] || 'circle'} text-${signalVariants[signal.type] || 'secondary'} mt-1`} style={{ fontSize: 13 }}></span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="d-flex justify-content-between align-items-center">
                        <strong style={{ fontSize: 12 }} className="text-900">{signal.title}</strong>
                        {signal.metric && <Badge bg="light" text="dark" className="fw-normal" style={{ fontSize: 9 }}>{signal.metric}</Badge>}
                      </div>
                      <p className="mb-1 text-700 lh-sm" style={{ fontSize: 11 }}>{signal.description}</p>
                      {signal.recommendation && (
                        <div className="fw-medium text-primary" style={{ fontSize: 11 }}><i className="fas fa-arrow-right me-1" style={{ fontSize: 8 }}></i>{signal.recommendation}</div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </Card.Body>
    </Card>
  );
};

// Main Dashboard Component
const CampaignDashboard = ({ clientId: propClientId, showClientSelector = false, onClientChange }) => {
  const { assignedClients, hasRole, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(propClientId);
  const [clientsLoaded, setClientsLoaded] = useState(false);
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [dateTo, setDateTo] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d;
  });
  const datesInitialized = useRef(false);
  const campaignApplyInit = useRef(false);
  const [campaignApplyNonce, setCampaignApplyNonce] = useState(0);
  const [selectedAccount, setSelectedAccount] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState([]);
  const [selectedAdGroup, setSelectedAdGroup] = useState([]);
  const [selectedAd, setSelectedAd] = useState([]);
  const [selectedPlatform, setSelectedPlatform] = useState([]);
  const [selectedProductType, setSelectedProductType] = useState([]);
  const [selectedCampaignObjective, setSelectedCampaignObjective] = useState([]);
  const [selectedModelo, setSelectedModelo] = useState('');
  const [sortBy, setSortBy] = useState({ field: 'cost', direction: 'desc' });
  const [filterCampaigns, setFilterCampaigns] = useState({ campaignName: '', productType: '', platform: '' });
  const [sortByPlatforms, setSortByPlatforms] = useState({ field: 'cost', direction: 'desc' });
  const [filterPlatforms, setFilterPlatforms] = useState({ platform: '' });
  const [sortByProducts, setSortByProducts] = useState({ field: 'cost', direction: 'desc' });
  const [filterProducts, setFilterProducts] = useState({ productType: '' });
  const [sortByGeo, setSortByGeo] = useState({ field: 'cost', direction: 'desc' });
  const [filterGeo, setFilterGeo] = useState({ region: '' });
  const [sortByDemographics, setSortByDemographics] = useState({ field: 'impressions', direction: 'desc' });
  const [filterDemographics, setFilterDemographics] = useState({ ageRange: '' });
  const [activeTab, setActiveTab] = useState('overview');
  const [geoMetric, setGeoMetric] = useState('impressions');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [illuminSyncing, setIlluminSyncing] = useState(false);
  const [jitData, setJitData] = useState(null);
  const [waData, setWaData] = useState(null);
  const [complianceData, setComplianceData] = useState(null);
  const [treeExpanded, setTreeExpanded] = useState({});
  const [investmentCompliance, setInvestmentCompliance] = useState(null);
  // Universo maestro de cuentas/campañas/adGroups/ads SIN filtros (solo cambia con cliente/fecha)
  const [masterFilters, setMasterFilters] = useState({ accounts: [], campaigns: [], adGroups: [], ads: [] });

  // Verificar si es admin
  const isAdmin = hasRole('admin') || hasRole('super_admin') || hasRole('MANAGER') || user?.isSuperAdmin;

  // Cargar lista de clientes
  useEffect(() => {
    const loadClients = async () => {
      // Para admins: cargar todos los clientes
      // Para usuarios normales: usar los clientes asignados del contexto
      if (isAdmin) {
        const result = await budgetService.getClients();
        // getClients() devuelve { clients }; /budget/clients ya filtra por asignación
        const list = (result.clients || result.data || []).filter(c => c.Active !== false && c.Active !== 0);
        if (result.success && list.length > 0) {
          setClients(list);
          if (!propClientId && !selectedClient) {
            setSelectedClient(list[0].ClientID);
          }
        }
      } else if (assignedClients?.length > 0) {
        // Usar los clientes asignados del contexto
        setClients(assignedClients);
        if (!propClientId && !selectedClient) {
          setSelectedClient(assignedClients[0].ClientID);
        }
      }
      setClientsLoaded(true);
    };
    loadClients();
  }, [isAdmin, assignedClients, propClientId]); // eslint-disable-line

  // Actualizar selectedClient cuando cambia propClientId
  useEffect(() => {
    if (propClientId) {
      setSelectedClient(propClientId);
    }
  }, [propClientId]);

  const clientId = selectedClient || propClientId;

  const loadData = useCallback(async (isInitial = false) => {
    if (!clientId) return;
    
    if (isInitial) {
      setLoading(true);
    } else {
      setIsRefreshing(true);
    }

    try {
      const result = await budgetService.getCampaignDashboardMetrics({
        dateFrom: toLocalDateString(dateFrom),
        dateTo: toLocalDateString(dateTo),
        clientId,
        accountId: selectedAccount.length > 0 ? selectedAccount.map(a => a.value).join(',') : undefined,
        campaignId: selectedCampaign.length > 0 ? selectedCampaign.map(c => c.value).join(',') : undefined,
        platform: selectedPlatform.length > 0 ? selectedPlatform.map(p => p.value).join(',') : undefined,
        productType: selectedProductType.length > 0 ? selectedProductType.map(p => p.value).join(',') : undefined,
        campaignObjective: selectedCampaignObjective.length > 0 ? selectedCampaignObjective.map(o => o.value).join(',') : undefined,
        modelo: selectedModelo || undefined,
      });

      if (result.success) {
        setData(result.data);
      } else {
        toast.error(result.error || 'Error cargando datos');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error cargando el dashboard');
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [dateFrom, dateTo, clientId, selectedAccount, selectedCampaign, selectedPlatform, selectedProductType, selectedCampaignObjective, selectedModelo, selectedAdGroup, selectedAd]);

  const handleIlluminSync = useCallback(async () => {
    if (!clientId || illuminSyncing) return;
    setIlluminSyncing(true);
    try {
      const result = await budgetService.syncIlluminData({ clientId });
      if (result.success) {
        const d = result.data || {};
        toast.success(
          `Illumin actualizado · ${formatNumberFull(d.apiViews || 0)} imp · ${formatNumberFull(d.rowsProcessed || 0)} filas · ${Math.round((d.elapsedMs || 0) / 1000)}s`,
        );
        await loadData(false);
      } else {
        toast.error(result.error || 'Error al sincronizar Illumin');
      }
    } catch (error) {
      console.error('Illumin sync:', error);
      toast.error('Error al sincronizar Illumin');
    } finally {
      setIlluminSyncing(false);
    }
  }, [clientId, illuminSyncing, loadData]);

  // Cargar datos JIT para el funnel de leads (solo Claro = clientId 16)
  const loadJitData = useCallback(async () => {
    if (clientId !== 16) return;
    try {
      const df = toLocalDateString(dateFrom);
      const dt = toLocalDateString(dateTo);
      const productTypes = selectedProductType.length > 0 ? selectedProductType.map(p => p.value) : [];
      const platforms = selectedPlatform.length > 0 ? selectedPlatform.map(p => p.value) : [];
      // Campaña: el JIT matchea por NOMBRE (utmCampaign), así que mandamos los labels.
      const campaignNames = selectedCampaign.length > 0 ? selectedCampaign.map(c => c.label) : [];
      const result = await budgetService.getJitDashboard({
        dateFrom: df,
        dateTo: dt,
        productType: productTypes.length > 0 ? productTypes.join(',') : undefined,
        platform: platforms.length > 0 ? platforms.join(',') : undefined,
        campaign: campaignNames.length > 0 ? campaignNames.join(',') : undefined,
      });
      if (result.success) {
        setJitData(result.data);
      }
    } catch (error) {
      console.error('Error cargando datos JIT:', error);
    }
  }, [clientId, dateFrom, dateTo, selectedProductType, selectedPlatform, selectedCampaign]);

  // Cargar datos WhatsApp SAC (solo Claro) — si el rango del dashboard no tiene data WA, carga sin fechas (rango real)
  const loadWaData = useCallback(async () => {
    if (clientId !== 16) return;
    try {
      const df = toLocalDateString(dateFrom);
      const dt = toLocalDateString(dateTo);
      const productTypes = selectedProductType.length > 0 ? selectedProductType.map(p => p.value) : [];
      const ptParam = productTypes.length > 0 ? productTypes.join(',') : undefined;

      // Respetar SIEMPRE el rango de fechas seleccionado (igual que el Funnel Leads).
      // Antes: si el rango no tenía data de WhatsApp, caía a un fallback SIN fechas que
      // mostraba TODO el histórico (Abr-Ago) con un banner chico -> confundía ("no filtra").
      // Ahora: si el rango está vacío, el render muestra "Sin datos WhatsApp para este período".
      const result = await budgetService.getWhatsappDashboard({
        dateFrom: df,
        dateTo: dt,
        productType: ptParam,
      });
      setWaData(result.success ? result.data : null);
    } catch (error) {
      console.error('Error cargando datos WhatsApp:', error);
    }
  }, [clientId, dateFrom, dateTo, selectedProductType]);

  const loadComplianceData = useCallback(async () => {
    if (!clientId) return;
    try {
      const df = toLocalDateString(dateFrom);
      const dt = toLocalDateString(dateTo);
      const [objResult, invResult] = await Promise.all([
        objectivesService.getDashboardSummary({ clientId, dateFrom: df, dateTo: dt }).catch(() => null),
        budgetService.getInvestmentCompliance({ clientId, dateFrom: df, dateTo: dt }).catch(() => null),
      ]);
      if (objResult && objResult?.success !== false) {
        setComplianceData(objResult?.data || objResult);
      }
      if (invResult && invResult?.success !== false && invResult?.data) {
        setInvestmentCompliance(invResult.data);
      }
    } catch (err) {
      console.error('Error cargando datos de cumplimiento:', err);
    }
  }, [clientId, dateFrom, dateTo]);

  // Cargar universo maestro (sin filtros) — cambia solo con cliente o fechas
  const loadMasterFilters = useCallback(async () => {
    if (!clientId) return;
    try {
      const result = await budgetService.getCampaignDashboardMetrics({
        dateFrom: toLocalDateString(dateFrom),
        dateTo: toLocalDateString(dateTo),
        clientId,
      });
      if (result.success && result.data) {
        setMasterFilters({
          accounts: result.data.filters?.accounts || [],
          campaigns: result.data.filters?.campaigns || result.data.campaigns || [],
          adGroups: result.data.adGroups || [],
          ads: result.data.ads || [],
        });
      }
    } catch (e) {
      console.error('Error cargando filtros maestros:', e);
    }
  }, [clientId, dateFrom, dateTo]);

  useEffect(() => {
    if (clientId && clientsLoaded) {
      loadData(true);
      loadJitData();
      loadWaData();
      loadComplianceData();
      loadMasterFilters();
    }
  }, [clientId, clientsLoaded]); // eslint-disable-line

  useEffect(() => {
    if (clientId && clientsLoaded) {
      loadMasterFilters();
    }
  }, [dateFrom, dateTo, clientId]); // eslint-disable-line

  // Recargar métricas al cambiar fechas (antes había que pulsar Buscar manualmente)
  useEffect(() => {
    if (!clientId || !clientsLoaded) return;
    if (!datesInitialized.current) {
      datesInitialized.current = true;
      return;
    }
    loadData(false);
    if (clientId === 16) { loadJitData(); loadWaData(); }
    loadComplianceData();
  }, [dateFrom, dateTo]); // eslint-disable-line

  const handleApplyFilters = useCallback(() => {
    if (!clientId || !clientsLoaded) return;
    loadData(false);
    if (clientId === 16) { loadJitData(); loadWaData(); }
    loadComplianceData();
  }, [loadData, loadJitData, loadWaData, loadComplianceData, clientId, clientsLoaded]); // eslint-disable-line

  // Barra de campaña del Overview: el usuario selecciona todas las que quiera y
  // recién al pulsar Buscar (o Limpiar) se incrementa este nonce, que dispara UNA
  // sola búsqueda con el estado ya fresco (evita el stale-closure de llamar el
  // handler en el mismo tick que el setState). No dispara en cada pick.
  useEffect(() => {
    if (!clientId || !clientsLoaded) return;
    if (!campaignApplyInit.current) { campaignApplyInit.current = true; return; }
    loadData(false);
    if (clientId === 16) { loadJitData(); loadWaData(); }
    loadComplianceData();
  }, [campaignApplyNonce]); // eslint-disable-line

  const handleSort = (field) => {
    setSortBy((prev) => ({
      field,
      direction: prev.field === field && prev.direction === 'desc' ? 'asc' : 'desc',
    }));
  };

  // Cross-filter: cada dropdown muestra las opciones aplicando los demás filtros,
  // PERO no se filtra a sí mismo (para permitir agregar más selecciones).
  // Usa masterFilters (universo completo) para que las opciones no desaparezcan
  // cuando el backend devuelve datos ya filtrados.
  const crossFilteredOptions = useMemo(() => {
    const campaignsAll = masterFilters.campaigns.length > 0 ? masterFilters.campaigns : (data?.campaigns || []);
    const adGroupsAll = masterFilters.adGroups.length > 0 ? masterFilters.adGroups : (data?.adGroups || []);
    const adsAll = masterFilters.ads.length > 0 ? masterFilters.ads : (data?.ads || []);
    const accountsAll = masterFilters.accounts.length > 0 ? masterFilters.accounts : (data?.filters?.accounts || []);
    const filterCampaignsAll = masterFilters.campaigns.length > 0 ? masterFilters.campaigns : (data?.filters?.campaigns || []);

    const platValues = selectedPlatform.map(p => p.value.toUpperCase());
    const acctIds = selectedAccount.map(a => parseInt(a.value)).filter(n => !isNaN(n));
    const campKeys = selectedCampaign.map(c => c.value);
    const agKeys = selectedAdGroup.map(g => parseInt(g.value)).filter(n => !isNaN(n));
    const adKeys = selectedAd.map(a => parseInt(a.value)).filter(n => !isNaN(n));
    const productValues = selectedProductType.map(p => (p.value || '').toUpperCase());
    const objectiveValues = selectedCampaignObjective.map(o => (o.value || '').toUpperCase());

    const platSet = new Set(platValues);
    const acctIdSet = new Set(acctIds);
    const campKeySet = new Set(campKeys);
    const agKeySet = new Set(agKeys);
    const adKeySet = new Set(adKeys);
    const productSet = new Set(productValues);
    const objectiveSet = new Set(objectiveValues);

    // Helpers para producto y objetivo (clasifican por nombre de cuenta y campaña)
    const matchProductTypeAccount = (accountName) => {
      if (productSet.size === 0) return true;
      const n = (accountName || '').toUpperCase();
      // BRAND y CORPORATIVO se identifican por nombre, los demás por substring
      if (productSet.has('BRAND') && (n.includes('BRAND') || n.includes('MARCA'))) return true;
      if (productSet.has('CORPORATIVO') && n.includes('CORPORATIVO')) return true;
      if (productSet.has('HOGAR') && n.includes('HOGAR')) return true;
      if (productSet.has('POSTPAGO') && (n.includes('POSTPAGO') || n.includes('POSPAGO'))) return true;
      if (productSet.has('PREPAGO') && n.includes('PREPAGO')) return true;
      if (productSet.has('EQUIPOS') && n.includes('EQUIPO')) return true;
      return false;
    };
    const matchProductTypeRow = (row) => {
      if (productSet.size === 0) return true;
      if (row?.productType && productSet.has(String(row.productType).toUpperCase())) return true;
      // Illumin: productType viene del backend (Vertical), no del nombre de cuenta
      if ((row?.sourcePlatform || '').toUpperCase() === 'ILLUMIN' && row?.productType) {
        return productSet.has(String(row.productType).toUpperCase());
      }
      return matchProductTypeAccount(row?.accountName);
    };
    const matchObjectiveCampaign = (campaignName) => {
      if (objectiveSet.size === 0) return true;
      const n = (campaignName || '').toUpperCase();
      // Búsqueda exacta entre underscores: _CPL_, _CPW_, etc.
      return Array.from(objectiveSet).some(obj => n.includes(`_${obj}_`));
    };

    // Bottom-up ancestry from selected ads/adGroups (para conocer ancestros)
    const adObjs = adKeys.map(k => adsAll.find(a => a.adKey === k)).filter(Boolean);
    const agObjs = agKeys.map(k => adGroupsAll.find(g => g.adGroupKey === k)).filter(Boolean);
    const adAncestorCamps = new Set(adObjs.map(a => a.campaignKey));
    const adAncestorAgs = new Set(adObjs.map(a => a.adGroupKey));
    const agAncestorCamps = new Set(agObjs.map(g => g.campaignKey));

    // Helpers: cada matcher acepta un flag para auto-excluir su propio filtro.
    // skipPlatform=true ignora el filtro de plataforma (al construir opciones de plataforma)
    const matchPlatform = (sp, skip) => skip || platSet.size === 0 || platSet.has((sp || '').toUpperCase());
    const matchAcct = (ak, skip) => skip || acctIdSet.size === 0 || acctIdSet.has(ak);
    const matchCampByCampSel = (ck, skip) => skip || campKeySet.size === 0 || campKeySet.has(ck);
    const matchCampByAg = (ck, skip) => skip || agKeySet.size === 0 || agAncestorCamps.has(ck);
    const matchCampByAd = (ck, skip) => skip || adKeySet.size === 0 || adAncestorCamps.has(ck);
    const matchAgByAgSel = (gk, skip) => skip || agKeySet.size === 0 || agKeySet.has(gk);
    const matchAgByAd = (gk, skip) => skip || adKeySet.size === 0 || adAncestorAgs.has(gk);

    // OPCIONES PARA "Plataforma" (skip=plataforma)
    // Plataformas válidas = aquellas con cuentas/campañas que matchean el resto de filtros
    const accountsForPlatform = accountsAll.filter(a => {
      if (!matchProductTypeAccount(a.accountName)) return false;
      const hasCamp = campaignsAll.some(c =>
        c.accountKey === a.accountKey
        && matchAcct(c.accountKey, false)
        && matchCampByCampSel(c.campaignKey, false)
        && matchCampByAg(c.campaignKey, false)
        && matchCampByAd(c.campaignKey, false)
        && matchProductTypeRow(c)
        && matchObjectiveCampaign(c.campaignName)
      );
      return hasCamp || (campaignsAll.length === 0 && matchAcct(a.accountKey, false));
    });
    const platsWithData = new Set(accountsForPlatform.map(a => (a.sourcePlatform || '').toUpperCase()));
    const validPlatforms = ['META', 'GOOGLE', 'TIKTOK', 'LINKEDIN', 'ILLUMIN', 'X'].filter(p => platsWithData.has(p));

    // OPCIONES PARA "Cuenta" (skip=cuenta)
    const validAccounts = accountsAll.filter(a => {
      if (!matchPlatform(a.sourcePlatform, false)) return false;
      if (!matchProductTypeAccount(a.accountName)) return false;
      const hasCamp = campaignsAll.some(c =>
        c.accountKey === a.accountKey
        && matchPlatform(c.sourcePlatform, false)
        && matchCampByCampSel(c.campaignKey, false)
        && matchCampByAg(c.campaignKey, false)
        && matchCampByAd(c.campaignKey, false)
        && matchProductTypeRow(c)
        && matchObjectiveCampaign(c.campaignName)
      );
      return hasCamp || campaignsAll.length === 0;
    });

    // OPCIONES PARA "Campaña" (skip=campaña)
    const validCampaignOptions = filterCampaignsAll.filter(c => {
      if (!matchPlatform(c.sourcePlatform, false)) return false;
      if (!matchAcct(c.accountKey, false)) return false;
      if (!matchCampByAg(c.campaignKey, false)) return false;
      if (!matchCampByAd(c.campaignKey, false)) return false;
      if (!matchProductTypeRow(c)) return false;
      if (!matchObjectiveCampaign(c.campaignName)) return false;
      return true;
    });

    // OPCIONES PARA "Grupo de Anuncios" (skip=adgroup)
    const validAdGroups = adGroupsAll.filter(ag => {
      if (!matchPlatform(ag.sourcePlatform, false)) return false;
      if (!matchAcct(ag.accountKey, false)) return false;
      if (!matchCampByCampSel(ag.campaignKey, false)) return false;
      if (!matchCampByAd(ag.campaignKey, false)) return false;
      if (!matchProductTypeRow(ag)) return false;
      // Heredar objetivo desde campaña padre
      const parentCamp = campaignsAll.find(c => c.campaignKey === ag.campaignKey);
      if (parentCamp && !matchObjectiveCampaign(parentCamp.campaignName)) return false;
      if (adKeySet.size > 0 && !adAncestorAgs.has(ag.adGroupKey)) return false;
      return true;
    });

    // OPCIONES PARA "Anuncios" (skip=ad)
    const validAds = adsAll.filter(a => {
      if (!matchPlatform(a.sourcePlatform, false)) return false;
      if (!matchAcct(a.accountKey, false)) return false;
      if (!matchCampByCampSel(a.campaignKey, false)) return false;
      if (!matchAgByAgSel(a.adGroupKey, false)) return false;
      if (!matchProductTypeRow(a)) return false;
      const parentCamp = campaignsAll.find(c => c.campaignKey === a.campaignKey);
      if (parentCamp && !matchObjectiveCampaign(parentCamp.campaignName)) return false;
      return true;
    });

    // CAMPAÑAS PARA TABLA: usa lo que devolvió el backend (ya filtrado server-side)
    // y aplica los filtros que aún no se mandan al backend (adGroup/ad).
    const dataCampaigns = data?.campaigns || [];
    const campaignsForDisplay = dataCampaigns.filter(c => {
      if (!matchCampByAg(c.campaignKey, false)) return false;
      if (!matchCampByAd(c.campaignKey, false)) return false;
      if (!matchProductTypeRow(c)) return false;
      if (!matchObjectiveCampaign(c.campaignName)) return false;
      return true;
    });

    // AD GROUPS PARA TABLA: usa datos del backend (ya filtrados por productType/cuenta/plataforma)
    // y aplica solo los filtros client-side de campaña y ad seleccionados.
    const dataAdGroups = data?.adGroups || [];
    const adGroupsForDisplay = dataAdGroups.filter(ag => {
      if (!matchCampByCampSel(ag.campaignKey, false)) return false;
      if (adKeySet.size > 0 && !adAncestorAgs.has(ag.adGroupKey)) return false;
      if (!matchProductTypeRow(ag)) return false;
      const parentCamp = (data?.campaigns || []).find(c => c.campaignKey === ag.campaignKey);
      if (parentCamp && !matchObjectiveCampaign(parentCamp.campaignName)) return false;
      return true;
    });

    // ADS PARA TABLA: usa datos del backend (ya filtrados por productType/cuenta/plataforma)
    const dataAds = data?.ads || [];
    const adsForDisplay = dataAds.filter(a => {
      if (!matchCampByCampSel(a.campaignKey, false)) return false;
      if (!matchAgByAgSel(a.adGroupKey, false)) return false;
      if (!matchProductTypeRow(a)) return false;
      const parentCamp = (data?.campaigns || []).find(c => c.campaignKey === a.campaignKey);
      if (parentCamp && !matchObjectiveCampaign(parentCamp.campaignName)) return false;
      return true;
    });

    return {
      platforms: validPlatforms,
      accounts: validAccounts,
      campaigns: validCampaignOptions,
      adGroups: validAdGroups,
      ads: validAds,
      campaignsForDisplay,
      adGroupsForDisplay,
      adsForDisplay,
    };
  }, [data, masterFilters, selectedPlatform, selectedAccount, selectedCampaign, selectedAdGroup, selectedAd, selectedProductType, selectedCampaignObjective]);

  const filteredCampaignsForDisplay = crossFilteredOptions.campaignsForDisplay;
  const filteredAdGroupsForDisplay = crossFilteredOptions.adGroupsForDisplay;
  const filteredAdsForDisplay = crossFilteredOptions.adsForDisplay;

  const sortedCampaigns = useMemo(() => {
    if (!filteredCampaignsForDisplay.length) return [];
    return [...filteredCampaignsForDisplay].sort((a, b) => {
      const aVal = a[sortBy.field] || 0;
      const bVal = b[sortBy.field] || 0;
      return sortBy.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [filteredCampaignsForDisplay, sortBy]);

  const handleDatePreset = (days) => {
    const to = new Date();
    to.setDate(to.getDate() - 1);
    const from = new Date();
    from.setDate(from.getDate() - days);
    setDateFrom(from);
    setDateTo(to);
  };

  const handleMonthToDate = () => {
    const to = new Date();
    to.setDate(to.getDate() - 1);
    setDateFrom(new Date(to.getFullYear(), to.getMonth(), 1));
    setDateTo(to);
  };

  // Si no hay cliente seleccionado y ya terminamos de cargar clientes
  if (!clientId && clientsLoaded) {
    return (
      <Container fluid className="py-5">
        <div className="text-center">
          <div className="mb-3" style={{ fontSize: '48px', color: '#2c7be5' }}>📊</div>
          <h4>Selecciona un cliente</h4>
          <p className="text-muted">No se ha especificado un cliente para mostrar el dashboard.</p>
          {clients.length > 0 && (
            <Form.Select 
              className="w-auto mx-auto mt-3"
              onChange={(e) => setSelectedClient(parseInt(e.target.value))}
              style={{ minWidth: '250px' }}
            >
              <option value="">-- Seleccionar Cliente --</option>
              {clients.map((c) => (
                <option key={c.ClientID} value={c.ClientID}>{c.ClientName}</option>
              ))}
            </Form.Select>
          )}
        </div>
      </Container>
    );
  }

  if (loading || !clientsLoaded) {
    return (
      <Container fluid className="py-5">
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-muted">Cargando dashboard...</p>
        </div>
      </Container>
    );
  }

  const kpis = data?.kpis || {};
  const kpisByObjective = data?.kpisByObjective || null;
  const illuminDailyTrend = data?.illuminDailyTrend || [];
  const isClaro = clientId === 16;

  return (
    <Container fluid className="py-3" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      {/* Header con filtros */}
      {/* Indicador de carga cuando se aplican filtros */}
      {isRefreshing && (
        <div 
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ 
            backgroundColor: 'rgba(255,255,255,0.85)', 
            zIndex: 9999,
            backdropFilter: 'blur(2px)',
          }}
        >
          <div className="text-center">
            <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
            <p className="mt-3 text-muted fw-medium">Aplicando filtros...</p>
          </div>
        </div>
      )}

      <Card className="mb-3 shadow-sm border-0">
        <Card.Body className="py-3">
          {/* Primera fila: Título y fechas (responsive) */}
          <Row className="g-2 align-items-end mb-3">
            <Col xs={12} md={3}>
              <h5 className="mb-0 text-primary">
                📊 Dashboard Campañas
              </h5>
            </Col>
            <Col xs={12} sm={6} md={2}>
              <Form.Label className="small text-muted mb-1">Fecha inicio</Form.Label>
              <DatePicker
                selected={dateFrom}
                onChange={setDateFrom}
                dateFormat="dd/MM/yyyy"
                className="form-control form-control-sm w-100"
                maxDate={dateTo}
                placeholderText="Desde"
              />
            </Col>
            <Col xs={12} sm={6} md={2}>
              <Form.Label className="small text-muted mb-1">Fecha fin</Form.Label>
              <DatePicker
                selected={dateTo}
                onChange={setDateTo}
                dateFormat="dd/MM/yyyy"
                className="form-control form-control-sm w-100"
                minDate={dateFrom}
                maxDate={new Date()}
                placeholderText="Hasta"
              />
            </Col>
            <Col xs={12} sm={12} md="auto">
              <div className="btn-group btn-group-sm">
                <Button variant="outline-secondary" onClick={handleMonthToDate}>Mes</Button>
                <Button variant="outline-secondary" onClick={() => handleDatePreset(7)}>7d</Button>
                <Button variant="outline-secondary" onClick={() => handleDatePreset(30)}>30d</Button>
                <Button variant="outline-secondary" onClick={() => handleDatePreset(90)}>90d</Button>
              </div>
            </Col>
            <Col xs={12} sm={12} md="auto" className="ms-md-auto d-flex gap-2">
              <Button
                variant="primary"
                size="sm"
                onClick={handleApplyFilters}
                disabled={isRefreshing || loading}
                title="Aplicar filtros y buscar"
                style={{ fontWeight: 600 }}
              >
                {isRefreshing ? (
                  <><Spinner animation="border" size="sm" className="me-1" /> Buscando...</>
                ) : (
                  <span style={{ fontSize: 12 }}>🔍 Buscar</span>
                )}
              </Button>
              <Button
                variant="success"
                size="sm"
                onClick={() => exportFullDashboard({
                  dateFrom, dateTo, kpis, kpisByObjective, data, jitData, waData, complianceData,
                  clientName: clients.find(c => c.ClientID === clientId)?.ClientName || 'Cliente',
                  filters: {
                    accounts: selectedAccount.map(a => a.label).join(', ') || 'Todas',
                    productType: selectedProductType.map(p => p.label).join(', ') || 'Todos',
                    campaignObjective: selectedCampaignObjective.map(o => o.label).join(', ') || 'Todos',
                    platform: selectedPlatform.map(p => p.label).join(', ') || 'Todas',
                  },
                })}
                title="Descargar todo el tablero (Excel)"
              >
                <span style={{ fontSize: 12 }}>⬇️ Descargar</span>
              </Button>
            </Col>
          </Row>

          {/* Filtros: 3 por fila en md+, 2 en sm, 1 en xs; todos los selects mismo tamaño */}
          <Row className="g-2 align-items-end">
            {/* Selector de Cliente - Solo si showClientSelector */}
            {showClientSelector && clients.length > 1 && (
              <Col xs={12} sm={6} md={4}>
                <Form.Label className="small text-muted mb-1">Cliente</Form.Label>
                <Select
                  value={selectedClient ? { value: selectedClient, label: clients.find(c => c.ClientID === selectedClient)?.ClientName } : null}
                  onChange={(option) => {
                    setSelectedClient(option ? option.value : '');
                    setSelectedAccount([]);
                    setSelectedCampaign([]);
                    setSelectedAdGroup([]);
                    setSelectedAd([]);
                    setSelectedPlatform([]);
                    setSelectedProductType([]);
                    setSelectedCampaignObjective([]);
                    setSelectedModelo('');
                    // Lleva el cliente a la URL (Tigo tiene su propio dashboard digital)
                    if (option && onClientChange) onClientChange(option.value);
                  }}
                  options={clients.map(c => ({ value: c.ClientID, label: c.ClientName }))}
                  placeholder="Seleccionar..."
                  isClearable
                  isSearchable
                  classNamePrefix="react-select"
                  styles={{
                    control: (base) => ({ ...base, minHeight: 31, fontSize: 13, width: '100%' }),
                    valueContainer: (base) => ({ ...base, padding: '0 8px' }),
                    input: (base) => ({ ...base, margin: 0, padding: 0 }),
                  }}
                />
              </Col>
            )}

            {/* Tipo de Producto - Solo para Claro (ClientID 16) */}
            {clientId === 16 && (
              <Col xs={12} sm={6} md={4}>
                <Form.Label className="small text-muted mb-1">Producto</Form.Label>
                <Select
                  isMulti
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  value={selectedProductType}
                  onChange={(opts) => setSelectedProductType(opts || [])}
                  options={[
                    { value: 'BRAND', label: 'BRAND' },
                    { value: 'CORPORATIVO', label: 'CORPORATIVO' },
                    { value: 'EQUIPOS', label: 'EQUIPOS' },
                    { value: 'HOGAR', label: 'HOGAR' },
                    { value: 'POSTPAGO', label: 'POSTPAGO' },
                    { value: 'PREPAGO', label: 'PREPAGO' },
                  ]}
                  placeholder="Todos (selección múltiple)"
                  isClearable
                  isSearchable
                  classNamePrefix="react-select"
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      minHeight: 31,
                      fontSize: 13,
                      width: '100%',
                      borderColor: state.hasValue ? '#2c7be5' : base.borderColor,
                      backgroundColor: state.hasValue ? '#f0f7ff' : base.backgroundColor,
                    }),
                    valueContainer: (base) => ({ ...base, padding: '0 8px', flexWrap: 'nowrap' }),
                    input: (base) => ({ ...base, margin: 0, padding: 0 }),
                    multiValue: (base) => ({ ...base, fontSize: '11px' }),
                  }}
                />
              </Col>
            )}

            {/* Objetivo de Campaña - Solo para Claro */}
            {clientId === 16 && (
              <Col xs={12} sm={6} md={4}>
                <Form.Label className="small text-muted mb-1">Objetivo Campaña</Form.Label>
                <Select
                  isMulti
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  value={selectedCampaignObjective}
                  onChange={(opts) => setSelectedCampaignObjective(opts || [])}
                  options={[
                    { value: 'CPL', label: 'CPL — Leads' },
                    { value: 'CPW', label: 'CPW — WhatsApp' },
                    { value: 'CPM', label: 'CPM — Awareness' },
                    { value: 'CPC', label: 'CPC — Tráfico' },
                    { value: 'CPV', label: 'CPV — Video' },
                  ]}
                  placeholder="Todos los objetivos"
                  isClearable
                  isSearchable
                  classNamePrefix="react-select"
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      minHeight: 31,
                      fontSize: 13,
                      width: '100%',
                      borderColor: state.hasValue ? '#2c7be5' : base.borderColor,
                      backgroundColor: state.hasValue ? '#f0f7ff' : base.backgroundColor,
                    }),
                    valueContainer: (base) => ({ ...base, padding: '0 8px', flexWrap: 'nowrap' }),
                    input: (base) => ({ ...base, margin: 0, padding: 0 }),
                    multiValue: (base) => ({ ...base, fontSize: '11px' }),
                  }}
                />
              </Col>
            )}

            {/* Plataforma */}
            <Col xs={12} sm={6} md={4}>
              <Form.Label className="small text-muted mb-1">Plataforma</Form.Label>
              <Select
                isMulti
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                value={selectedPlatform}
                onChange={(opts) => {
                  setSelectedPlatform(opts || []);
                  setSelectedAccount([]);
                  setSelectedCampaign([]);
                  setSelectedAdGroup([]);
                  setSelectedAd([]);
                }}
                options={[
                  { value: 'meta', label: 'Meta Ads' },
                  { value: 'google', label: 'Google Ads' },
                  { value: 'tiktok', label: 'TikTok Ads' },
                  { value: 'linkedin', label: 'LinkedIn Ads' },
                  { value: 'x', label: 'X Ads' },
                ]}
                placeholder="Todas (selección múltiple)"
                isClearable
                isSearchable
                classNamePrefix="react-select"
                styles={{
                  control: (base, state) => ({
                    ...base,
                    minHeight: 31,
                    fontSize: 13,
                    width: '100%',
                    borderColor: state.hasValue ? '#00d97e' : base.borderColor,
                    backgroundColor: state.hasValue ? '#f0fff7' : base.backgroundColor,
                  }),
                  valueContainer: (base) => ({ ...base, padding: '0 8px', flexWrap: 'nowrap' }),
                  input: (base) => ({ ...base, margin: 0, padding: 0 }),
                  multiValue: (base) => ({ ...base, fontSize: '11px' }),
                }}
              />
            </Col>

            {/* Modelo - Solo para Ford */}
            {data?.summary?.isFord && data?.filters?.modelos?.length > 0 && (
              <Col xs={12} sm={6} md={4}>
                <Form.Label className="small text-muted mb-1">Modelo</Form.Label>
                <Select
                  value={selectedModelo ? { value: selectedModelo, label: selectedModelo } : null}
                  onChange={(option) => {
                    setSelectedModelo(option ? option.value : '');
                    setSelectedCampaign([]);
                    setSelectedAdGroup([]);
                    setSelectedAd([]);
                  }}
                  options={data.filters.modelos.map(m => ({ value: m, label: m }))}
                  placeholder="Todos"
                  isClearable
                  isSearchable
                  classNamePrefix="react-select"
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      minHeight: 31,
                      fontSize: 13,
                      width: '100%',
                      borderColor: state.hasValue ? '#e63757' : base.borderColor,
                      backgroundColor: state.hasValue ? '#fff0f3' : base.backgroundColor,
                    }),
                    valueContainer: (base) => ({ ...base, padding: '0 8px' }),
                    input: (base) => ({ ...base, margin: 0, padding: 0 }),
                  }}
                />
              </Col>
            )}

            {/* Cuenta - multi-select con búsqueda */}
            <Col xs={12} sm={6} md={4}>
              <Form.Label className="small text-muted mb-1">Cuenta</Form.Label>
              <Select
                isMulti
                value={selectedAccount}
                onChange={(opts) => {
                  setSelectedAccount(opts || []);
                  setSelectedCampaign([]);
                  setSelectedAdGroup([]);
                  setSelectedAd([]);
                }}
                options={crossFilteredOptions.accounts.map(a => ({ value: a.accountKey, label: a.accountName }))}
                placeholder="Todas las cuentas (multi-select)"
                isClearable
                isSearchable
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                classNamePrefix="react-select"
                noOptionsMessage={() => "Sin resultados"}
                styles={{
                  control: (base, state) => ({
                    ...base,
                    minHeight: 31,
                    fontSize: 13,
                    width: '100%',
                    borderColor: state.hasValue ? '#f5803e' : base.borderColor,
                    backgroundColor: state.hasValue ? '#fff8f0' : base.backgroundColor,
                  }),
                  valueContainer: (base) => ({ ...base, padding: '0 8px', flexWrap: 'nowrap' }),
                  input: (base) => ({ ...base, margin: 0, padding: 0 }),
                  multiValue: (base) => ({ ...base, fontSize: '11px' }),
                }}
              />
            </Col>

            {/* Campaña - con búsqueda integrada */}
            <Col xs={12} sm={6} md={4}>
              <Form.Label className="small text-muted mb-1">Campaña</Form.Label>
              <Select
                isMulti
                value={selectedCampaign}
                onChange={(opts) => {
                  setSelectedCampaign(opts || []);
                  setSelectedAdGroup([]);
                  setSelectedAd([]);
                }}
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                options={crossFilteredOptions.campaigns.map(c => ({ value: c.campaignKey, label: c.campaignName }))}
                placeholder="Todas las campañas (multi-select)"
                isClearable
                isSearchable
                classNamePrefix="react-select"
                noOptionsMessage={() => "Sin resultados"}
                styles={{
                  control: (base, state) => ({
                    ...base,
                    minHeight: 31,
                    fontSize: 13,
                    width: '100%',
                    borderColor: state.hasValue ? '#6b5eae' : base.borderColor,
                    backgroundColor: state.hasValue ? '#f8f5ff' : base.backgroundColor,
                  }),
                  valueContainer: (base) => ({ ...base, padding: '0 8px', flexWrap: 'nowrap' }),
                  input: (base) => ({ ...base, margin: 0, padding: 0 }),
                  multiValue: (base) => ({ ...base, fontSize: '11px' }),
                }}
              />
            </Col>

            {/* Grupo de Anuncios - multi-select */}
            <Col xs={12} sm={6} md={4}>
              <Form.Label className="small text-muted mb-1">Grupo de Anuncios</Form.Label>
              <Select
                isMulti
                value={selectedAdGroup}
                onChange={(opts) => {
                  setSelectedAdGroup(opts || []);
                  setSelectedAd([]);
                }}
                options={crossFilteredOptions.adGroups.map(ag => ({ value: ag.adGroupKey, label: (ag.adGroupName || '').slice(0, 50) }))}
                placeholder="Todos los grupos (multi-select)"
                isClearable
                isSearchable
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                classNamePrefix="react-select"
                noOptionsMessage={() => 'Sin resultados'}
                styles={{
                  control: (base, state) => ({
                    ...base,
                    minHeight: 31,
                    fontSize: 13,
                    width: '100%',
                    borderColor: state.hasValue ? '#2c7be5' : base.borderColor,
                    backgroundColor: state.hasValue ? '#f0f7ff' : base.backgroundColor,
                  }),
                  valueContainer: (base) => ({ ...base, padding: '0 8px', flexWrap: 'nowrap' }),
                  input: (base) => ({ ...base, margin: 0, padding: 0 }),
                  multiValue: (base) => ({ ...base, fontSize: '11px' }),
                }}
              />
            </Col>

            {/* Anuncios - multi-select */}
            <Col xs={12} sm={6} md={4}>
              <Form.Label className="small text-muted mb-1">Anuncios</Form.Label>
              <Select
                isMulti
                value={selectedAd}
                onChange={(opts) => setSelectedAd(opts || [])}
                options={crossFilteredOptions.ads.map(a => ({ value: a.adKey, label: (a.adName || a.adKey).toString().slice(0, 50) }))}
                placeholder="Todos los anuncios (multi-select)"
                isClearable
                isSearchable
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                classNamePrefix="react-select"
                noOptionsMessage={() => 'Sin resultados'}
                styles={{
                  control: (base, state) => ({
                    ...base,
                    minHeight: 31,
                    fontSize: 13,
                    width: '100%',
                    borderColor: state.hasValue ? '#00d97e' : base.borderColor,
                    backgroundColor: state.hasValue ? '#f0fff7' : base.backgroundColor,
                  }),
                  valueContainer: (base) => ({ ...base, padding: '0 8px', flexWrap: 'nowrap' }),
                  input: (base) => ({ ...base, margin: 0, padding: 0 }),
                  multiValue: (base) => ({ ...base, fontSize: '11px' }),
                }}
              />
            </Col>
          </Row>

          {/* Indicador de filtros activos */}
          {(selectedProductType.length > 0 || selectedPlatform.length > 0 || selectedAccount.length > 0 || selectedCampaign.length > 0 || selectedAdGroup.length > 0 || selectedAd.length > 0 || selectedModelo) && (
            <div className="mt-2 pt-2 border-top">
              <small className="text-muted me-2">Filtros activos:</small>
              {selectedProductType.length > 0 && (
                <Badge bg="primary" className="me-1" style={{ cursor: 'pointer' }} onClick={() => setSelectedProductType([])}>
                  Producto: {selectedProductType.map(p => p.label).join(', ')} ×
                </Badge>
              )}
              {selectedModelo && (
                <Badge bg="danger" className="me-1" style={{ cursor: 'pointer' }} onClick={() => setSelectedModelo('')}>
                  Modelo: {selectedModelo} ×
                </Badge>
              )}
              {selectedPlatform.length > 0 && (
                <Badge bg="success" className="me-1" style={{ cursor: 'pointer' }} onClick={() => setSelectedPlatform([])}>
                  Plataforma: {selectedPlatform.map(p => p.label).join(', ')} ×
                </Badge>
              )}
              {selectedAccount.length > 0 && (
                <Badge bg="warning" text="dark" className="me-1" style={{ cursor: 'pointer' }} onClick={() => { setSelectedAccount([]); setSelectedCampaign([]); setSelectedAdGroup([]); setSelectedAd([]); }}>
                  {selectedAccount.length} Cuenta(s) ×
                </Badge>
              )}
              {selectedCampaign.length > 0 && (
                <Badge bg="secondary" className="me-1" style={{ cursor: 'pointer' }} onClick={() => { setSelectedCampaign([]); setSelectedAdGroup([]); setSelectedAd([]); }}>
                  {selectedCampaign.length} Campaña(s) ×
                </Badge>
              )}
              {selectedAdGroup.length > 0 && (
                <Badge bg="info" className="me-1" style={{ cursor: 'pointer' }} onClick={() => { setSelectedAdGroup([]); setSelectedAd([]); }}>
                  {selectedAdGroup.length} Grupo(s) ×
                </Badge>
              )}
              {selectedAd.length > 0 && (
                <Badge bg="dark" className="me-1" style={{ cursor: 'pointer' }} onClick={() => setSelectedAd([])}>
                  {selectedAd.length} Anuncio(s) ×
                </Badge>
              )}
              <Button 
                variant="link" 
                size="sm" 
                className="p-0 ms-2 text-danger"
                onClick={() => {
                  setSelectedProductType([]);
                  setSelectedPlatform([]);
                  setSelectedAccount([]);
                  setSelectedCampaign([]);
                  setSelectedAdGroup([]);
                  setSelectedAd([]);
                  setSelectedModelo('');
                }}
              >
                Limpiar todos
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* KPI Cards — dinámicas para Claro, fijas para otros clientes */}
      {(() => {
        const productTypes = (selectedProductType || []).map(p => p.value);
        const activeProduct = productTypes.length === 1 ? productTypes[0] : null;

        // Para Claro: WhatsApp solo de campañas CPW, Leads solo de campañas CPL (_CPL_ en naming)
        // No usar kpis.leads como fallback: incluye conversiones de campañas CPC/CPM
        const waConv = isClaro
          ? (kpisByObjective?.WHATSAPP?.conversations ?? 0)
          : (kpis.whatsappConversations ?? 0);
        const waCp = isClaro
          ? (kpisByObjective?.WHATSAPP?.cpConversation ?? 0)
          : (kpis.costPerWhatsappConversation ?? 0);
        const claroLeads = isClaro
          ? (kpisByObjective?.LEADS?.leads ?? 0)
          : (kpis.leads ?? 0);
        const claroCpl = isClaro
          ? (kpisByObjective?.LEADS?.cpl ?? 0)
          : (kpis.cpl ?? 0);

        // Definición de cada card como elemento reutilizable
        const C = {
          impresiones: (
            <Col xs={6} md key="imp">
              <KPICard
                title="Impresiones"
                value={formatNumberFull(kpis.impressions)}
                subtitle={kpis.illuminExcludedFromMain ? 'Sin CTV/Illumin · ver sección abajo' : undefined}
                color="#2c7be5"
              />
            </Col>
          ),
          reach: (
            <Col xs={6} md key="reach">
              <KPICard
                title="Alcance Estimado"
                value={formatNumberFull(kpis.reach || 0)}
                subtitle={`Frecuencia: ${(kpis.frequency || 0).toFixed(1)} · Aprox. usuarios únicos`}
                color="#17a2b8"
                tooltip="Suma de alcance diario reportado por la plataforma. Es una aproximación al número de personas únicas impactadas en el periodo. Solo Meta y TikTok reportan esta métrica; Google Ads no la expone."
              />
            </Col>
          ),
          reachSinFrecuencia: (
            <Col xs={6} md key="reach">
              <KPICard
                title="Alcance Estimado"
                value={formatNumberFull(kpis.reach || 0)}
                subtitle="Aprox. usuarios únicos"
                color="#17a2b8"
                tooltip="Suma de alcance diario reportado por la plataforma. Es una aproximación al número de personas únicas impactadas en el periodo. Solo Meta y TikTok reportan esta métrica; Google Ads no la expone."
              />
            </Col>
          ),
          clicsTodos: (
            <Col xs={6} md key="clicks">
              <KPICard title="Clics (Todos)" value={formatNumberFull(kpis.clicks)} subtitle={`CTR: ${formatPercent(kpis.ctr)}`} color="#00d97e" />
            </Col>
          ),
          clicsEnlace: (
            <Col xs={6} md key="link">
              <KPICard title="Clics en Enlace" value={formatNumberFull(kpis.linkClicks || 0)} subtitle={`Link CTR: ${formatPercent(kpis.linkCtr || 0)}`} color="#27bcfd" />
            </Col>
          ),
          leads: (() => {
            const b = kpis?.leadsBreakdown;
            const parts = [];
            if (b) {
              if (b.quote) parts.push(`Cotiz. ${formatNumberFull(b.quote)}`);
              if (b.sale) parts.push(`Ventas ${formatNumberFull(b.sale)}`);
              if (b.submit) parts.push(`Lead forms ${formatNumberFull(b.submit)}`);
              if (b.onsite) parts.push(`Onsite ${formatNumberFull(b.onsite)}`);
              if (b.webReg) parts.push(`Reg. web ${formatNumberFull(b.webReg)}`);
              if (b.fbPixel) parts.push(`FbPixel ${formatNumberFull(b.fbPixel)}`);
              if (b.purchase) parts.push(`Compras ${formatNumberFull(b.purchase)}`);
              if (b.offsite) parts.push(`Offsite ${formatNumberFull(b.offsite)}`);
            }
            const desglose = parts.join(' · ');
            return (
              <Col xs={6} md key="leads">
                <KPICard title="Leads" value={formatNumberFull(claroLeads)} color="#6b5eae"
                  subtitle={(
                    <>
                      CPL: {formatCurrency(claroCpl)}
                      {desglose && (
                        <div style={{ fontSize: '10px', lineHeight: 1.3, marginTop: 2, color: '#8a8a8a' }}>{desglose}</div>
                      )}
                    </>
                  )}
                  tooltip={desglose ? `Desglose de leads — ${desglose}` : undefined}
                />
              </Col>
            );
          })(),
          cpm: (
            <Col xs={6} md key="cpm">
              <KPICard title="CPM" value={formatCurrency(kpis.cpm)} subtitle="Costo por mil" color="#f5803e" />
            </Col>
          ),
          cpc: (
            <Col xs={6} md key="cpc">
              <KPICard title="CPC" value={formatCurrency(kpis.cpc)} subtitle="Costo por clic" color="#00d97e" />
            </Col>
          ),
          linkCpc: (
            <Col xs={6} md key="linkcpc">
              <KPICard title="Link CPC" value={formatCurrency(kpis.linkCpc || 0)} subtitle="CPC enlace" color="#27bcfd" />
            </Col>
          ),
          inversion: (
            <Col xs={6} md key="cost">
              <KPICard title="Inversión Total" value={formatCurrency(kpis.cost)} color="#e63757" />
            </Col>
          ),
          whatsapp: (
            <Col xs={6} md key="wa">
              <KPICard
                title="Conv. Iniciadas"
                value={formatNumberFull(waConv)}
                subtitle={waCp > 0 ? `CP-Conv: ${formatCurrency(waCp)}` : 'Campañas CPW · WhatsApp'}
                color="#25d366"
              />
            </Col>
          ),
          ctr: (
            <Col xs={6} md key="ctr">
              <KPICard title="CTR" value={formatPercent(kpis.ctr)} subtitle={`Frecuencia: ${(kpis.frequency || 0).toFixed(1)}`} color="#27bcfd" />
            </Col>
          ),
          ctrSolo: (
            <Col xs={6} md key="ctr">
              <KPICard title="CTR" value={formatPercent(kpis.ctr)} subtitle="Click-through rate" color="#27bcfd" />
            </Col>
          ),
          frecuencia: (
            <Col xs={6} md key="freq">
              <KPICard title="Frecuencia" value={(kpis.frequency || 0).toFixed(2)} subtitle="Promedio por persona" color="#6c757d" />
            </Col>
          ),
          registrosWeb: (
            <Col xs={6} md key="regsWeb">
              <KPICard title="Registros Web" value={formatNumberFull(kpis.leadsWebsiteRegistration || 0)} subtitle="Meta · Registros sitio" color="#9d6bae" />
            </Col>
          ),
          leadsOnFacebook: (
            <Col xs={6} md key="lFb">
              <KPICard title="Leads en Facebook" value={formatNumberFull(kpis.leadsOnFacebook || 0)} subtitle="Meta · Onsite leads" color="#3b5998" />
            </Col>
          ),
          graciasCatalogo: (
            <Col xs={6} md key="gracias">
              <KPICard
                title="Gracias / Compra / Catálogo"
                value={kpis.graciasCatalogo != null ? formatNumberFull(kpis.graciasCatalogo) : '—'}
                subtitle="Conversión custom · Próximamente"
                color="#fd7e14"
              />
            </Col>
          ),
        };

        const illuminSection = kpis.hasIlluminData ? (
          <div className="mb-3">
            <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
              <div className="d-flex align-items-center flex-wrap gap-1">
                <span className="badge me-2" style={{ backgroundColor: '#7B61FF', fontSize: '11px' }}>CTV / Illumin</span>
                <small className="text-muted">
                  Advertiser 37683 · Fuente: API Illumin → FactIlluminAd
                  {data?.summary?.dateRange && (
                    <> · {data.summary.dateRange.from} → {data.summary.dateRange.to}</>
                  )}
                </small>
              </div>
              <Button
                size="sm"
                variant="falcon-default"
                onClick={handleIlluminSync}
                disabled={illuminSyncing || loading}
                title="Trae ayer + hoy desde Illumin API y reprocesa en BD"
              >
                {illuminSyncing ? (
                  <><Spinner animation="border" size="sm" className="me-1" /> Sincronizando...</>
                ) : (
                  <><i className="fas fa-sync-alt me-1" /> Actualizar Illumin</>
                )}
              </Button>
            </div>
            <Row className="g-2 mb-2">
              <Col xs={6} md={4} lg={2}>
                <IlluminKPICard
                  title="Impresiones"
                  value={formatNumberFull(kpis.illuminImpressions || 0)}
                  subtitle="Total del período"
                  color="#2c7be5"
                  trendData={illuminDailyTrend}
                  trendKey="impressions"
                  formatSpark={(v) => formatNumberFull(v)}
                />
              </Col>
              <Col xs={6} md={4} lg={2}>
                <IlluminKPICard
                  title="Clics"
                  value={formatNumberFull(kpis.illuminClicks || 0)}
                  subtitle={`CTR: ${formatPercent(kpis.illuminCtr || 0)}`}
                  color="#00d97e"
                  trendData={illuminDailyTrend}
                  trendKey="clicks"
                  formatSpark={(v) => formatNumberFull(v)}
                />
              </Col>
              <Col xs={6} md={4} lg={2}>
                <IlluminKPICard
                  title="Inversión"
                  value={formatCurrency(kpis.illuminCost || 0)}
                  subtitle="Spend Illumin"
                  color="#e63757"
                  trendData={illuminDailyTrend}
                  trendKey="cost"
                  formatSpark={(v) => formatCurrency(v)}
                />
              </Col>
              <Col xs={6} md={4} lg={2}>
                <IlluminKPICard
                  title="CPM"
                  value={formatCurrency(kpis.illuminCpm || 0)}
                  subtitle="Costo por mil"
                  color="#f5803e"
                  trendData={illuminDailyTrend}
                  trendKey="cpm"
                  formatSpark={(v) => formatCurrency(v)}
                />
              </Col>
              <Col xs={6} md={4} lg={2}>
                <IlluminKPICard
                  title="CPC"
                  value={formatCurrency(kpis.illuminCpc || 0)}
                  subtitle="Costo por clic"
                  color="#27bcfd"
                  trendData={illuminDailyTrend}
                  trendKey="cpc"
                  formatSpark={(v) => formatCurrency(v)}
                />
              </Col>
              <Col xs={6} md={4} lg={2}>
                <IlluminKPICard
                  title="CTR"
                  value={formatPercent(kpis.illuminCtr || 0)}
                  subtitle="Click-through rate"
                  color="#6b5eae"
                  trendData={illuminDailyTrend}
                  trendKey="ctr"
                  formatSpark={(v) => formatPercent(v)}
                />
              </Col>
            </Row>
            <Row className="g-2">
              <Col xs={6} md={3}>
                <IlluminKPICard
                  title="Video Completions"
                  value={formatNumberFull(kpis.videoComplete || 0)}
                  subtitle={`Video Start: ${formatNumberFull(kpis.videoStart || 0)}`}
                  color="#7B61FF"
                  tooltip="Videos completados al 100% en CTV/Illumin"
                  trendData={illuminDailyTrend}
                  trendKey="videoComplete"
                  formatSpark={(v) => formatNumberFull(v)}
                />
              </Col>
              <Col xs={6} md={3}>
                <IlluminKPICard
                  title="VCR"
                  value={`${(kpis.vcr || 0).toFixed(1)}%`}
                  subtitle="Video Completion Rate"
                  color="#6C63FF"
                  tooltip="Tasa de completación = VideoComplete / VideoStart"
                  trendData={illuminDailyTrend}
                  trendKey="vcr"
                  formatSpark={(v) => `${Number(v).toFixed(1)}%`}
                />
              </Col>
              <Col xs={6} md={3}>
                <IlluminKPICard
                  title="CPCV"
                  value={(kpis.cpcv || 0) < 0.01 && (kpis.cpcv || 0) > 0
                    ? '$' + (kpis.cpcv || 0).toLocaleString('es-MX', { minimumFractionDigits: 4, maximumFractionDigits: 4 })
                    : formatCurrency(kpis.cpcv || 0)}
                  subtitle="Cost Per Completed Video"
                  color="#9B59B6"
                  tooltip="Costo por video completado"
                  trendData={illuminDailyTrend}
                  trendKey="cpcv"
                  formatSpark={(v) => (v < 0.01 && v > 0
                    ? '$' + Number(v).toLocaleString('es-MX', { minimumFractionDigits: 4, maximumFractionDigits: 4 })
                    : formatCurrency(v))}
                />
              </Col>
              <Col xs={6} md={3}>
                <IlluminKPICard
                  title="Alcance CTV"
                  value={formatNumberFull(kpis.illuminReach || 0)}
                  subtitle="Usuarios únicos"
                  color="#8E44AD"
                  tooltip="Alcance único en Illumin/CTV"
                  trendData={illuminDailyTrend}
                  trendKey="reach"
                  formatSpark={(v) => formatNumberFull(v)}
                />
              </Col>
            </Row>
          </div>
        ) : null;

        // ----- Layout para clientes NO-Claro -----
        if (!isClaro) {
          return (
            <>
              <Row className="g-2 mb-2">
                {C.impresiones}{C.clicsTodos}{C.clicsEnlace}{C.leads}
              </Row>
              <Row className="g-2 mb-2">
                {C.cpm}{C.cpc}{C.linkCpc}{C.inversion}
              </Row>
              <Row className="g-2 mb-3">
                {C.registrosWeb}
                {C.leadsOnFacebook}
                <Col xs={6} md key="waOrig">
                  <KPICard
                    title="Conv. WhatsApp"
                    value={formatNumberFull(kpis.whatsappConversations || 0)}
                    subtitle={kpis.costPerWhatsappConversation > 0 ? `CP-Conv: ${formatCurrency(kpis.costPerWhatsappConversation)}` : 'Meta · Mensajes iniciados'}
                    color="#25d366"
                  />
                </Col>
                {C.ctr}
              </Row>
              {illuminSection}
            </>
          );
        }

        // ----- Layouts dinámicos para Claro -----
        if (activeProduct === 'BRAND') {
          return (
            <>
              <Row className="g-2 mb-2">
                {C.impresiones}{C.reachSinFrecuencia}{C.clicsEnlace}{C.cpm}
              </Row>
              <Row className="g-2 mb-3">
                {C.cpc}{C.linkCpc}{C.inversion}{C.ctrSolo}
              </Row>
              {illuminSection}
            </>
          );
        }

        if (activeProduct === 'PREPAGO') {
          return (
            <>
              <Row className="g-2 mb-2">
                {C.impresiones}{C.reach}{C.leads}{C.whatsapp}
              </Row>
              <Row className="g-2 mb-3">
                {C.cpm}{C.cpc}{C.inversion}{C.clicsEnlace}
              </Row>
              {illuminSection}
            </>
          );
        }

        if (activeProduct === 'POSTPAGO') {
          return (
            <>
              <Row className="g-2 mb-2">
                {C.impresiones}{C.reach}{C.leads}{C.whatsapp}
              </Row>
              <Row className="g-2 mb-3">
                {C.cpm}{C.cpc}{C.inversion}{C.clicsEnlace}
              </Row>
              {illuminSection}
            </>
          );
        }

        if (activeProduct === 'EQUIPOS') {
          return (
            <>
              <Row className="g-2 mb-2">
                {C.impresiones}{C.reach}{C.leads}{C.whatsapp}
              </Row>
              <Row className="g-2 mb-3">
                {C.cpm}{C.cpc}{C.inversion}{C.clicsEnlace}
              </Row>
              {illuminSection}
            </>
          );
        }

        // Claro sin filtro de producto específico (todos / múltiples)
        return (
          <>
            <Row className="g-2 mb-2">
              {C.impresiones}{C.reach}{C.leads}{C.whatsapp}
            </Row>
            <Row className="g-2 mb-3">
              {C.cpm}{C.cpc}{C.inversion}{C.clicsEnlace}
            </Row>
            {illuminSection}
          </>
        );
      })()}

      {/* Sección de Cumplimiento Real vs Objetivo/Proyectado */}
      {/*}{(complianceData || investmentCompliance) && (() => {
        const allObjectives = complianceData?.objectives || [];
        const hasAccountFilter = !!selectedAccount;
        const hasPlatformFilter = selectedPlatform?.length > 0;
        const platformValues = (selectedPlatform || []).map(p => (p.value || p).toLowerCase());

        const filtered = allObjectives.filter(o => {
          if (hasAccountFilter && String(o.accountId) !== String(selectedAccount)) return false;
          if (hasPlatformFilter && !platformValues.some(pv => (o.medium || '').toLowerCase().includes(pv))) return false;
          return true;
        });

        const agg = {};
        filtered.forEach(o => {
          const u = o.unit || 'Unknown';
          if (!agg[u]) agg[u] = { objective: 0, real: 0, isCostMetric: false };
          agg[u].objective += o.nominalValue || 0;
          agg[u].real += o.realValue || 0;
          if (o.isCostMetric) agg[u].isCostMetric = true;
        });

        const getColor = (pct) => {
          if (pct >= 90 && pct <= 110) return '#22b8cf';
          if (pct > 110) return '#51cf66';
          if (pct >= 70) return '#fcc419';
          return '#ff6b6b';
        };

        const fmtSmall = (v, isCost) => {
          if (isCost) return formatCurrency(v);
          if (v >= 1000000) return (v / 1000000).toFixed(1) + 'M';
          if (v >= 1000) return (v / 1000).toFixed(1) + 'K';
          return Math.round(v).toLocaleString();
        };

        const makeGaugeOption = (pct, label, realStr, objStr, color) => ({
          series: [{
            type: 'gauge',
            startAngle: 200,
            endAngle: -20,
            min: 0,
            max: 100,
            radius: '100%',
            center: ['50%', '60%'],
            pointer: { show: false },
            progress: {
              show: true,
              overlap: false,
              roundCap: true,
              clip: false,
              itemStyle: { color },
              width: 10,
            },
            axisLine: { lineStyle: { width: 10, color: [[1, '#e9ecef']] } },
            splitLine: { show: false },
            axisTick: { show: false },
            axisLabel: { show: false },
            data: [{ value: Math.min(pct, 100) }],
            title: { show: false },
            detail: {
              fontSize: 22,
              fontWeight: 700,
              fontFamily: 'inherit',
              color: '#2c3e50',
              offsetCenter: [0, '-5%'],
              formatter: () => pct.toFixed(0) + '%',
            },
          }],
        });

        const showInvestment = investmentCompliance && investmentCompliance.totalBudgeted > 0;
        const hasKpis = Object.keys(agg).length > 0;
        if (!showInvestment && !hasKpis) return null;

        const items = [];
        if (showInvestment) {
          const p = investmentCompliance.compliancePct;
          items.push({
            key: 'inv', label: 'Inversión', pct: p, color: getColor(p),
            realStr: formatCurrency(investmentCompliance.totalReal),
            objStr: formatCurrency(investmentCompliance.totalBudgeted),
          });
        }
        Object.entries(agg).forEach(([unit, vals]) => {
          const p = vals.isCostMetric
            ? (vals.objective > 0 && vals.real > 0 ? (vals.objective / vals.real) * 100 : 0)
            : (vals.objective > 0 ? (vals.real / vals.objective) * 100 : 0);
          items.push({
            key: unit, label: unit, pct: p, color: getColor(p),
            realStr: fmtSmall(vals.real, vals.isCostMetric),
            objStr: fmtSmall(vals.objective, vals.isCostMetric),
          });
        });

        return (
          <Card className="shadow-sm border-0 mb-3">
            <Card.Header className="bg-white border-bottom py-2 d-flex justify-content-between align-items-center">
              <h6 className="mb-0 fw-bold" style={{ fontSize: 13, color: '#1B2A4A' }}>
                Cumplimiento Real vs Objetivo
              </h6>
              <div className="d-flex align-items-center gap-2">
                {(hasAccountFilter || hasPlatformFilter) && (
                  <Badge bg="info" className="fw-normal" style={{ fontSize: 10 }}>
                    Filtrado por {hasAccountFilter ? 'cuenta' : ''}{hasAccountFilter && hasPlatformFilter ? ' + ' : ''}{hasPlatformFilter ? 'plataforma' : ''}
                  </Badge>
                )}
                <Badge bg="light" text="dark" className="border" style={{ fontSize: 10 }}>
                  {filtered.length} objetivo{filtered.length !== 1 ? 's' : ''}
                </Badge>
              </div>
            </Card.Header>
            <Card.Body className="py-2">
              <Row className="g-2 justify-content-center">
                {items.map(item => (
                  <Col xs={6} sm={4} md={3} lg key={item.key}>
                    <div className="text-center">
                      <ReactEChartsCore
                        echarts={echarts}
                        option={makeGaugeOption(item.pct, item.label, item.realStr, item.objStr, item.color)}
                        style={{ height: 130 }}
                        opts={{ renderer: 'canvas' }}
                      />
                      <div style={{ marginTop: -10 }}>
                        <div className="fw-semibold" style={{ fontSize: 12, color: '#1B2A4A' }}>{item.label}</div>
                        <div style={{ fontSize: 10, color: '#6c757d' }}>
                          <span style={{ color: item.color, fontWeight: 600 }}>{item.realStr}</span>
                          {' / '}
                          <span>{item.objStr}</span>
                        </div>
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            </Card.Body>
          </Card>
        );
      })()}{*/}

      {/* Tabs de navegación */}
      <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
        <Card className="shadow-sm border-0 mb-3">
          <Card.Body className="py-0">
            <Nav variant="pills" className="nav-pills-falcon py-2">
              <Nav.Item>
                <Nav.Link 
                  eventKey="overview" 
                  className={`px-4 py-2 me-2 rounded-pill ${activeTab === 'overview' ? 'bg-primary text-white' : 'bg-light text-dark'}`}
                  style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                >
                  Overview
                </Nav.Link>
              </Nav.Item>
              {/* Tab Por Producto - Solo para Claro */}
              {clientId === 16 && (
                <Nav.Item>
                  <Nav.Link 
                    eventKey="products" 
                    className={`px-4 py-2 me-2 rounded-pill ${activeTab === 'products' ? 'bg-primary text-white' : 'bg-light text-dark'}`}
                    style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                  >
                    Por Producto
                  </Nav.Link>
                </Nav.Item>
              )}
              <Nav.Item>
                <Nav.Link 
                  eventKey="campaigns" 
                  className={`px-4 py-2 me-2 rounded-pill ${activeTab === 'campaigns' ? 'bg-primary text-white' : 'bg-light text-dark'}`}
                  style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                >
                  Campañas {sortedCampaigns?.length ? `(${sortedCampaigns.length})` : ''}
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link
                  eventKey="adgroups-platform"
                  className={`px-4 py-2 me-2 rounded-pill ${activeTab === 'adgroups-platform' ? 'bg-primary text-white' : 'bg-light text-dark'}`}
                  style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                >
                  Ad Groups × Plataforma
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link
                  eventKey="demographics"
                  className={`px-4 py-2 me-2 rounded-pill ${activeTab === 'demographics' ? 'bg-primary text-white' : 'bg-light text-dark'}`}
                  style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                >
                  Demografía
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link 
                  eventKey="platforms" 
                  className={`px-4 py-2 me-2 rounded-pill ${activeTab === 'platforms' ? 'bg-primary text-white' : 'bg-light text-dark'}`}
                  style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                >
                  Plataformas
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link 
                  eventKey="geography" 
                  className={`px-4 py-2 me-2 rounded-pill ${activeTab === 'geography' ? 'bg-primary text-white' : 'bg-light text-dark'}`}
                  style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                >
                  Geografía
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link 
                  eventKey="objectives" 
                  className={`px-4 py-2 rounded-pill ${activeTab === 'objectives' ? 'bg-success text-white' : 'bg-light text-dark'}`}
                  style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                >
                  Objetivos
                </Nav.Link>
              </Nav.Item>
            </Nav>
          </Card.Body>
        </Card>

        <Tab.Content>
          {/* Tab Overview - Estilo Power BI/Looker */}
          <Tab.Pane eventKey="overview">
            {/* Barra de filtro de campaña del Overview — selecciona las campañas que
                quieras y pulsa Buscar (una sola búsqueda). Cascadea a las gráficas + el
                embudo JIT vía el mismo estado selectedCampaign del filtro grande. */}
            <Card className="shadow-sm border-0 mb-3" style={{ background: 'linear-gradient(180deg,#f8fafc 0%,#ffffff 100%)' }}>
              <Card.Body className="py-2 px-3">
                <Row className="align-items-center g-2">
                  <Col xs={12} md="auto" className="d-flex align-items-center">
                    <span className="fw-semibold text-nowrap" style={{ fontSize: 12.5, color: '#1B2A4A' }}>
                      🎯 Filtrar por campaña
                    </span>
                    {selectedCampaign.length > 0 && (
                      <Badge bg="primary" className="ms-2" style={{ fontSize: 10 }}>
                        {selectedCampaign.length}
                      </Badge>
                    )}
                  </Col>
                  <Col xs={12} md>
                    <Select
                      isMulti
                      value={selectedCampaign}
                      onChange={(opts) => { setSelectedCampaign(opts || []); setSelectedAdGroup([]); setSelectedAd([]); }}
                      closeMenuOnSelect={false}
                      hideSelectedOptions={false}
                      blurInputOnSelect={false}
                      options={crossFilteredOptions.campaigns.map(c => ({ value: c.campaignKey, label: c.campaignName }))}
                      placeholder="Elige una o varias campañas y pulsa Buscar…"
                      isClearable={false}
                      isSearchable
                      classNamePrefix="react-select"
                      components={{ MultiValue: CampaignMultiValue }}
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                      menuPosition="fixed"
                      styles={{
                        container: (b) => ({ ...b, fontSize: 12 }),
                        control: (b) => ({ ...b, minHeight: 38, maxHeight: 38, flexWrap: 'nowrap', borderColor: '#cbd5e1' }),
                        // UNA sola línea: los chips extra se colapsan en "+N más" (CampaignMultiValue),
                        // así el selector nunca crece ni se desborda hacia las tarjetas de abajo.
                        valueContainer: (b) => ({ ...b, flexWrap: 'nowrap', overflow: 'hidden', gap: 2, padding: '2px 6px' }),
                        multiValue: (b) => ({ ...b, backgroundColor: '#e0e7ff', borderRadius: 6, maxWidth: 220, flexShrink: 0 }),
                        multiValueLabel: (b) => ({ ...b, fontSize: 11, color: '#3730a3', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }),
                        multiValueRemove: (b) => ({ ...b, color: '#4f46e5', ':hover': { backgroundColor: '#c7d2fe', color: '#312e81' } }),
                        menuPortal: (b) => ({ ...b, zIndex: 9999 }),
                        menu: (b) => ({ ...b, fontSize: 12 }),
                        option: (b) => ({ ...b, fontSize: 12 }),
                      }}
                    />
                  </Col>
                  <Col xs="auto" className="d-flex gap-2">
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() => setCampaignApplyNonce((n) => n + 1)}
                      disabled={isRefreshing || loading}
                      style={{ fontWeight: 600, fontSize: 12 }}
                      title="Aplica las campañas seleccionadas a las gráficas y al embudo JIT"
                    >
                      {isRefreshing ? (
                        <><Spinner animation="border" size="sm" className="me-1" /> Buscando…</>
                      ) : (
                        <>🔍 Buscar</>
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={selectedCampaign.length === 0 || isRefreshing || loading}
                      onClick={() => { setSelectedCampaign([]); setSelectedAdGroup([]); setSelectedAd([]); setCampaignApplyNonce((n) => n + 1); }}
                      style={{ fontSize: 11 }}
                    >
                      Limpiar
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
            {/* Fila 0: Real vs Objetivo + Evolutivo — Solo Claro */}
            {clientId === 16 && (
              <>
                <Row className="g-3 mb-3">
                  {/* Semáforo de KPIs: Real vs Objetivo — agrupados por unidad */}
                  <Col lg={4}>
                    <Card className="shadow-sm border-0 h-100">
                      <Card.Header className="bg-white border-bottom py-2 d-flex justify-content-between align-items-center">
                        <h6 className="mb-0 fw-bold" style={{ fontSize: 13, color: '#1B2A4A' }}>
                          Real vs Objetivo
                          <small className="text-muted fw-normal ms-2" style={{ fontSize: 10 }}>
                            {selectedPlatform.length > 0 && `· ${selectedPlatform.map(p => p.label).join(', ')}`}
                            {selectedAccount.length > 0 && ` · ${selectedAccount.length} cuenta(s)`}
                          </small>
                        </h6>
                        <Badge bg="light" text="dark" className="border" style={{ fontSize: 10 }}>
                          {complianceData?.totalObjectives || 0} objetivos
                        </Badge>
                      </Card.Header>
                      <Card.Body className="py-2 px-2" style={{ overflow: 'visible' }}>
                        {(() => {
                          const allObjectives = complianceData?.objectives || [];
                          const platValues = (selectedPlatform || []).map(p => (p.value || p).toLowerCase());
                          const acctValues = (selectedAccount || []).map(a => String(a.value));
                          const productValues = (selectedProductType || []).map(p => (p.value || p).toUpperCase());
                          const filtered = allObjectives.filter(o => {
                            if (platValues.length > 0 && !platValues.some(pv => (o.medium || '').toLowerCase().includes(pv))) return false;
                            if (acctValues.length > 0 && !acctValues.includes(String(o.accountId))) return false;
                            // Filtrar por producto en accountName (ej: "Claro Equipos" → EQUIPOS)
                            if (productValues.length > 0) {
                              const acctUpper = (o.accountName || '').toUpperCase();
                              const matches = productValues.some(pv => acctUpper.includes(pv));
                              if (!matches) return false;
                            }
                            return true;
                          });

                          if (filtered.length === 0 && !(investmentCompliance?.totalBudgeted > 0)) {
                            return (
                              <div className="text-center text-muted py-4">
                                <small>Sin objetivos configurados para este filtro</small>
                                <br/><small style={{ fontSize: 10 }}>Configura objetivos en el módulo de Objetivos</small>
                              </div>
                            );
                          }

                          const inv = investmentCompliance;
                          const fmtN = (v, isCost) => {
                            if (isCost) return '$' + (v || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
                            if (v >= 1e6) return (v / 1e6).toFixed(1) + 'M';
                            if (v >= 1000) return (v / 1000).toFixed(1) + 'K';
                            return Math.round(v || 0).toLocaleString();
                          };
                          const semaforo = (pct) => {
                            if (pct >= 110) return { color: '#0f5132', soft: '#d1e7dd', label: 'Superado' };
                            if (pct >= 90)  return { color: '#146c43', soft: '#d1e7dd', label: 'En meta' };
                            if (pct >= 70)  return { color: '#664d03', soft: '#fff3cd', label: 'En riesgo' };
                            if (pct > 0)    return { color: '#842029', soft: '#f8d7da', label: 'Crítico' };
                            return { color: '#6c757d', soft: '#e9ecef', label: 'Sin datos' };
                          };
                          const calcPct = (real, objective, isCost) =>
                            isCost ? (real > 0 && objective > 0 ? (objective / real) * 100 : 0)
                                   : (objective > 0 ? (real / objective) * 100 : 0);

                          const MiniBar = ({ pct, sem, h = 8 }) => {
                            const p = Math.max(0, Number(pct) || 0);
                            const w = Math.min(100, p);
                            return (
                              <div style={{ height: h, borderRadius: h / 2, background: '#e9ecef', overflow: 'hidden' }}>
                                <div style={{ height: '100%', width: `${w}%`, background: sem.color, borderRadius: h / 2, transition: 'width 0.4s' }} />
                              </div>
                            );
                          };

                          const toggleExpand = (key) => setTreeExpanded(prev => ({ ...prev, [key]: !prev[key] }));

                          // Agrupar: Unit → [{ accountName, medium, objectives[] }]
                          const tree = {};
                          filtered.forEach(o => {
                            const u = o.unit || 'Sin unidad';
                            if (!tree[u]) tree[u] = { objectives: [], real: 0, expected: 0, objective: 0, isCostMetric: false };
                            tree[u].objectives.push(o);
                            tree[u].real += o.realValue || 0;
                            tree[u].expected += o.expectedValue || 0;
                            tree[u].objective += o.nominalValue || 0;
                            if (o.isCostMetric) tree[u].isCostMetric = true;
                          });

                          const unitItems = Object.entries(tree).map(([unit, g]) => ({
                            unit,
                            ...g,
                            pct: calcPct(g.real, g.objective, g.isCostMetric),
                            expectedPct: calcPct(g.real, g.expected, g.isCostMetric),
                          }));

                          return (
                            <div style={{ maxHeight: 450, overflowY: 'auto', fontSize: 11 }}>
                              {/* Inversión */}
                              {inv && inv.totalBudgeted > 0 && (() => {
                                const p = inv.compliancePct || 0;
                                const sem = semaforo(p);
                                return (
                                  <div className="mb-2 pb-2 border-bottom">
                                    <div className="d-flex align-items-center gap-2 mb-1">
                                      <span className="fw-bold" style={{ color: '#1B2A4A' }}>Inversión</span>
                                      <span className="ms-auto rounded-pill px-2" style={{ fontSize: 9, fontWeight: 600, color: sem.color, background: sem.soft }}>{sem.label}</span>
                                      <span className="fw-bold tabular-nums" style={{ color: sem.color }}>{p.toFixed(0)}%</span>
                                    </div>
                                    <MiniBar pct={p} sem={sem} h={10} />
                                    <div className="d-flex justify-content-between mt-1" style={{ fontSize: 9, color: '#6c757d' }}>
                                      <span>Real <strong className="text-dark">{fmtN(inv.totalReal, true)}</strong></span>
                                      <span>Obj <strong className="text-dark">{fmtN(inv.totalBudgeted, true)}</strong></span>
                                    </div>
                                  </div>
                                );
                              })()}

                              {/* Árbol por KPI/Unit */}
                              {unitItems.map((item) => {
                                const sem = semaforo(item.pct);
                                const key = `unit_${item.unit}`;
                                const isOpen = !!treeExpanded[key];
                                const childCount = item.objectives.length;

                                // Agrupar hijos por cuenta
                                const byAccount = {};
                                item.objectives.forEach(o => {
                                  const acctLabel = o.accountName || 'Sin cuenta';
                                  if (!byAccount[acctLabel]) byAccount[acctLabel] = [];
                                  byAccount[acctLabel].push(o);
                                });

                                return (
                                  <div key={item.unit} className="mb-1">
                                    {/* Cabecera expandible del KPI */}
                                    <div
                                      className="d-flex align-items-center gap-2 py-1 px-1 rounded"
                                      style={{ cursor: 'pointer', background: isOpen ? '#f8f9fa' : 'transparent', transition: 'background 0.2s' }}
                                      onClick={() => toggleExpand(key)}
                                    >
                                      <span style={{ width: 14, textAlign: 'center', fontSize: 10, color: '#6c757d', flexShrink: 0 }}>
                                        {isOpen ? '▾' : '▸'}
                                      </span>
                                      <span className="fw-semibold" style={{ color: '#1B2A4A', minWidth: 0, flex: 1 }}>{item.unit}</span>
                                      <span className="text-muted" style={{ fontSize: 9 }}>({childCount})</span>
                                      <span className="rounded-pill px-2" style={{ fontSize: 9, fontWeight: 600, color: sem.color, background: sem.soft, flexShrink: 0 }}>{sem.label}</span>
                                      <span className="fw-bold tabular-nums" style={{ color: sem.color, minWidth: 42, textAlign: 'right', flexShrink: 0 }}>{item.pct.toFixed(0)}%</span>
                                    </div>
                                    <div className="px-1 mb-1"><MiniBar pct={item.pct} sem={sem} /></div>
                                    <div className="d-flex justify-content-between px-1 mb-1" style={{ fontSize: 9, color: '#6c757d' }}>
                                      <span>Real <strong className="text-dark">{fmtN(item.real, item.isCostMetric)}</strong></span>
                                      <span>Obj <strong className="text-dark">{fmtN(item.objective, item.isCostMetric)}</strong></span>
                                    </div>

                                    {/* Hijos: por cuenta */}
                                    {isOpen && (
                                      <div className="ms-3 ps-2" style={{ borderLeft: '2px solid #dee2e6' }}>
                                        {Object.entries(byAccount).map(([acctName, objs]) => {
                                          const acctKey = `${key}_${acctName}`;
                                          const acctOpen = !!treeExpanded[acctKey];
                                          const acctReal = objs.reduce((s, o) => s + (o.realValue || 0), 0);
                                          const acctObj = objs.reduce((s, o) => s + (o.nominalValue || 0), 0);
                                          const acctPct = calcPct(acctReal, acctObj, item.isCostMetric);
                                          const acctSem = semaforo(acctPct);

                                          return (
                                            <div key={acctName} className="mb-1">
                                              <div
                                                className="d-flex align-items-center gap-1 py-1 rounded"
                                                style={{ cursor: objs.length > 1 ? 'pointer' : 'default', fontSize: 10 }}
                                                onClick={() => objs.length > 1 && toggleExpand(acctKey)}
                                              >
                                                {objs.length > 1 && (
                                                  <span style={{ width: 12, textAlign: 'center', color: '#adb5bd', flexShrink: 0 }}>
                                                    {acctOpen ? '▾' : '▸'}
                                                  </span>
                                                )}
                                                {objs.length === 1 && <span style={{ width: 12, flexShrink: 0 }} />}
                                                <span className="text-truncate" style={{ flex: 1, color: '#495057' }} title={acctName}>{acctName}</span>
                                                <span className="text-muted" style={{ fontSize: 9 }}>{objs[0]?.medium || ''}</span>
                                                <span className="fw-bold tabular-nums" style={{ color: acctSem.color, fontSize: 10, minWidth: 36, textAlign: 'right' }}>{acctPct.toFixed(0)}%</span>
                                              </div>
                                              <div className="mb-1"><MiniBar pct={acctPct} sem={acctSem} h={4} /></div>
                                              <div className="d-flex justify-content-between mb-1" style={{ fontSize: 9, color: '#adb5bd' }}>
                                                <span>Real <strong style={{ color: '#495057' }}>{fmtN(acctReal, item.isCostMetric)}</strong></span>
                                                <span>Obj <strong style={{ color: '#495057' }}>{fmtN(acctObj, item.isCostMetric)}</strong></span>
                                              </div>

                                              {/* Nivel 3: objetivos individuales */}
                                              {acctOpen && objs.length > 1 && (
                                                <div className="ms-3 ps-2" style={{ borderLeft: '1px dashed #dee2e6' }}>
                                                  {objs.map((o) => {
                                                    const oPct = calcPct(o.realValue || 0, o.nominalValue || 0, o.isCostMetric);
                                                    const oSem = semaforo(oPct);
                                                    return (
                                                      <div key={o.id} className="mb-1" style={{ fontSize: 9 }}>
                                                        <div className="d-flex align-items-center gap-1">
                                                          <span className="text-truncate" style={{ flex: 1, color: '#6c757d' }} title={`${o.campaignType || ''} · ${o.medium || ''}`}>
                                                            {o.campaignType || o.medium || `#${o.id}`}
                                                          </span>
                                                          <span className="fw-bold tabular-nums" style={{ color: oSem.color, minWidth: 30, textAlign: 'right' }}>{oPct.toFixed(0)}%</span>
                                                        </div>
                                                        <MiniBar pct={oPct} sem={oSem} h={3} />
                                                        <div className="d-flex justify-content-between" style={{ color: '#adb5bd' }}>
                                                          <span>{fmtN(o.realValue || 0, o.isCostMetric)}</span>
                                                          <span>{fmtN(o.nominalValue || 0, o.isCostMetric)}</span>
                                                        </div>
                                                      </div>
                                                    );
                                                  })}
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}

                              {/* Leyenda */}
                              <div className="border-top pt-2 mt-1 d-flex flex-wrap gap-2 justify-content-center" style={{ fontSize: 9, color: '#495057' }}>
                                <span><span style={{ color: '#0f5132' }}>●</span> &gt;110%</span>
                                <span><span style={{ color: '#146c43' }}>●</span> 90–110%</span>
                                <span><span style={{ color: '#664d03' }}>●</span> 70–90%</span>
                                <span><span style={{ color: '#842029' }}>●</span> &lt;70%</span>
                              </div>
                            </div>
                          );
                        })()}
                      </Card.Body>
                    </Card>
                  </Col>
                  {/* Costo por Día (al lado del árbol — alerta planeado vs consumido) */}
                  <Col lg={8}>
                    <Card className="shadow-sm border-0 h-100">
                      <Card.Header className="bg-white border-bottom py-2 d-flex justify-content-between align-items-center">
                        <h6 className="mb-0">Costo por Día</h6>
                        <ChartExportMenu data={data?.dailyTrend} filename="costo_por_dia" columns={[
                          { key: 'dateKey', header: 'Fecha' }, { key: 'cost', header: 'Costo' },
                        ]} />
                      </Card.Header>
                      <Card.Body>
                        <CostByDayChart data={data?.dailyTrend} />
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </>
            )}

   

            {/* Evolutivo principal (full width) */}
            <Row className="g-3 mb-3">
              <Col lg={12}>
                <EvolutiveChart data={data?.dailyTrend} />
              </Col>
            </Row>

            {/* Top Anuncios — todos los clientes con datos de ads */}
            <Row className="g-3 mb-3">
              <Col lg={12}>
                <TopAdsSection ads={data?.ads} campaigns={data?.campaigns} />
              </Col>
            </Row>

            {/* Tendencia general + Edad */}
            <Row className="g-3 mb-3">
              <Col lg={8}>
                <Card className="shadow-sm border-0 h-100">
                  <Card.Header className="bg-white border-bottom py-2 d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">Tendencia General - Impresiones/Clicks/Gasto</h6>
                    <ChartExportMenu data={data?.dailyTrend} filename="tendencia_general" columns={[
                      { key: 'dateKey', header: 'Fecha' }, { key: 'impressions', header: 'Impresiones' },
                      { key: 'clicks', header: 'Clicks' }, { key: 'cost', header: 'Gasto' },
                      { key: 'reach', header: 'Alcance' }, { key: 'leads', header: 'Leads' },
                    ]} />
                  </Card.Header>
                  <Card.Body>
                    <TrendChart data={data?.dailyTrend} />
                  </Card.Body>
                </Card>
              </Col>
              <Col lg={4}>
                <Card className="shadow-sm border-0 h-100">
                  <Card.Header className="bg-white border-bottom py-2 d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="mb-0">Distribución por Edad</h6>
                      <small className="text-muted" style={{ fontSize: 10 }} title="Solo Meta reporta este desglose. TikTok y Google Ads no exponen breakdowns por edad.">
                        Solo Meta · ⓘ
                      </small>
                    </div>
                    <ChartExportMenu data={data?.byAge} filename="distribucion_edad" columns={[
                      { key: 'ageRange', header: 'Rango Edad' }, { key: 'impressions', header: 'Impresiones' },
                      { key: 'clicks', header: 'Clicks' }, { key: 'cost', header: 'Inversión' }, { key: 'percentage', header: '% del Total' },
                    ]} />
                  </Card.Header>
                  <Card.Body>
                    <AgeChart data={data?.byAge} />
                  </Card.Body>
                </Card>
              </Col>
            </Row>

  {/* Fila 2: Leads vs CPL + Funnels (Claro) | Leads vs CPL + Costo (resto) */}
  <Row className="g-3 mb-3">
              <Col lg={4}>
                <Card className="shadow-sm border-0 h-100">
                  <Card.Header className="bg-white border-bottom py-2 d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">Leads vs CPL</h6>
                    <ChartExportMenu data={data?.dailyTrend} filename="leads_cpl" columns={[
                      { key: 'dateKey', header: 'Fecha' }, { key: 'leads', header: 'Leads' }, { key: 'cpl', header: 'CPL' },
                    ]} />
                  </Card.Header>
                  <Card.Body>
                    <LeadsVsCplChart data={data?.dailyTrend} />
                  </Card.Body>
                </Card>
              </Col>
              {clientId === 16 ? (
                <>
                  <Col lg={4}>
                    <LeadsFunnelChart jitData={jitData} digitalLeads={kpisByObjective?.LEADS?.leads ?? kpis.leads ?? 0} />
                  </Col>
                  <Col lg={4}>
                    <Card className="shadow-sm border-0 h-100">
                      <Card.Header className="bg-white border-bottom py-2">
                        <h6 className="mb-0 fw-bold" style={{ fontSize: 12, color: '#25d366' }}>
                          Funnel WhatsApp SAC
                        </h6>
                        {waData?.dateRange && (
                          <small className="text-muted" style={{ fontSize: 9 }}>
                            {waData.dateRange.from} → {waData.dateRange.to}
                          </small>
                        )}
                      </Card.Header>
                      <Card.Body className="py-2">
                        {waData?.kpis ? (() => {
                          const k = waData.kpis;
                          const total = parseInt(k.totalConversaciones) || 0;
                          const contactados = parseInt(k.totalContactados) || 0;
                          const acepta = parseInt(k.totalAcepta) || 0;
                          const gestion = parseInt(k.totalGestionFinalizada) || 0;
                          if (total === 0) return <div className="text-center text-muted py-3"><small>Sin datos WhatsApp para este período</small></div>;
                          const funnelItems = [
                            { label: 'Conversaciones', value: total, color: '#B8D4E3' },
                            { label: 'Contactados', value: contactados, color: '#6BAED6' },
                            { label: 'Gestión Fin.', value: gestion, color: '#2171B5' },
                            { label: 'Acepta', value: acepta, color: '#25d366' },
                          ];
                          return (
                            <div>
                              {waData._fallbackRange && (
                                <div className="alert alert-info py-1 px-2 mb-2" style={{ fontSize: 9, borderRadius: 6 }}>
                                  Datos fuera del rango seleccionado — mostrando rango disponible
                                </div>
                              )}
                              {funnelItems.map((item, idx) => (
                                <div key={idx} className="mb-2">
                                  <div className="d-flex justify-content-between mb-1">
                                    <small className="fw-semibold" style={{ fontSize: 11 }}>{item.label}</small>
                                    <small className="fw-bold" style={{ fontSize: 11, color: item.color }}>{item.value.toLocaleString()}</small>
                                  </div>
                                  <div style={{ height: 14, borderRadius: 7, background: '#e9ecef', overflow: 'hidden' }}>
                                    <div style={{ height: '100%', width: `${Math.max((item.value / total) * 100, 2)}%`, background: item.color, borderRadius: 7 }} />
                                  </div>
                                </div>
                              ))}
                              <div className="border-top pt-2 mt-2" style={{ fontSize: 10 }}>
                                <div className="d-flex justify-content-between text-muted">
                                  <span>Tasa Contacto</span>
                                  <span className="fw-bold">{total > 0 ? ((contactados/total)*100).toFixed(1) : 0}%</span>
                                </div>
                                <div className="d-flex justify-content-between text-muted">
                                  <span>Tasa Aceptación</span>
                                  <span className="fw-bold text-success">{contactados > 0 ? ((acepta/contactados)*100).toFixed(1) : 0}%</span>
                                </div>
                              </div>
                            </div>
                          );
                        })() : <div className="text-center text-muted py-3"><small>Sin datos WhatsApp</small></div>}
                      </Card.Body>
                    </Card>
                  </Col>
                </>
              ) : (
                <Col lg={8}>
                  <Card className="shadow-sm border-0 h-100">
                    <Card.Header className="bg-white border-bottom py-2 d-flex justify-content-between align-items-center">
                      <h6 className="mb-0">Costo por Día</h6>
                      <ChartExportMenu data={data?.dailyTrend} filename="costo_por_dia" columns={[
                        { key: 'dateKey', header: 'Fecha' }, { key: 'cost', header: 'Costo' },
                      ]} />
                    </Card.Header>
                    <Card.Body>
                      <CostByDayChart data={data?.dailyTrend} />
                    </Card.Body>
                  </Card>
                </Col>
              )}
            </Row>

            {/* Señales de Decisión · IA */}
            <Row className="g-3 mb-3">
              <Col lg={12}>
                <AISignals
                  kpis={data?.summary}
                  campaigns={data?.campaigns}
                  clientName={clients?.find(c => c.id === clientId)?.name || `Cliente ${clientId}`}
                  dateRange={`${toLocalDateString(dateFrom)} a ${toLocalDateString(dateTo)}`}
                  clientId={clientId}
                  dateFrom={dateFrom}
                  dateTo={dateTo}
                  filters={{
                    platform: selectedPlatform.map(p => p.value),
                    campaign: selectedCampaign.map(c => c.value),
                    account: selectedAccount.map(a => a.value),
                  }}
                />
              </Col>
            </Row>

          </Tab.Pane>

          {/* Tab Por Producto - Solo para Claro (ClientID 16) */}
          {clientId === 16 && (
            <Tab.Pane eventKey="products">
              {/* Resumen visual por tipo de producto */}
              <Row className="g-3 mb-3">
                <Col lg={8}>
                  <Card className="shadow-sm border-0 h-100">
                    <Card.Header className="bg-white border-bottom py-2 d-flex justify-content-between align-items-center">
                      <h6 className="mb-0">Métricas por Tipo de Producto</h6>
                      <ChartExportMenu data={data?.byProductType} filename="metricas_producto" columns={[
                        { key: 'productType', header: 'Producto' }, { key: 'impressions', header: 'Impresiones' },
                        { key: 'clicks', header: 'Clicks' }, { key: 'ctr', header: 'CTR' },
                        { key: 'leads', header: 'Leads' }, { key: 'cost', header: 'Inversión' }, { key: 'percentage', header: '%' },
                      ]} />
                    </Card.Header>
                    <Card.Body>
                      <ProductTypeChart data={data?.byProductType} />
                    </Card.Body>
                  </Card>
                </Col>
                <Col lg={4}>
                  <Card className="shadow-sm border-0 h-100">
                    <Card.Header className="bg-white border-bottom py-2 d-flex justify-content-between align-items-center">
                      <h6 className="mb-0">Distribución por Plataforma</h6>
                      <ChartExportMenu data={data?.byProductTypePlatform} filename="distribucion_plataforma" columns={[
                        { key: 'platform', header: 'Plataforma' }, { key: 'productType', header: 'Producto' },
                        { key: 'cost', header: 'Inversión' }, { key: 'impressions', header: 'Impresiones' },
                      ]} />
                    </Card.Header>
                    <Card.Body>
                      <PlatformDistributionChart data={data?.byProductTypePlatform} metric="cost" />
                      <div className="text-center mt-2">
                        <small className="text-muted">Inversión por plataforma (Meta vs Google)</small>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Tabla detallada por tipo de producto */}
              <Card className="shadow-sm border-0">
                <Card.Header className="bg-white border-bottom py-2 d-flex justify-content-between align-items-center">
                  <h6 className="mb-0">Detalle por Tipo de Producto</h6>
                  <ChartExportMenu data={data?.byProductType} filename="detalle_producto" columns={[
                    { key: 'productType', header: 'Producto' }, { key: 'impressions', header: 'Impresiones' },
                    { key: 'clicks', header: 'Clicks' }, { key: 'videoViews', header: 'Views' },
                    { key: 'ctr', header: 'CTR' }, { key: 'cpm', header: 'CPM' },
                    { key: 'cpc', header: 'CPC' }, { key: 'leads', header: 'Leads' },
                    { key: 'cpl', header: 'CPL' }, { key: 'cost', header: 'Inversión' },
                  ]} />
                </Card.Header>
                <Card.Body className="p-0">
                  <ProductTypeTable 
                    data={data?.byProductType} 
                    byPlatform={data?.byProductTypePlatform}
                    sortBy={sortByProducts}
                    onSort={(f) => setSortByProducts((p) => ({ field: f, direction: p.field === f && p.direction === 'desc' ? 'asc' : 'desc' }))}
                    filters={filterProducts}
                    onFilterChange={setFilterProducts}
                  />
                </Card.Body>
              </Card>

              {/* Cards de KPIs por tipo de producto */}
              <Row className="g-2 mt-3">
                {data?.byProductType?.filter(p => p.productType !== 'OTRO').map((product, idx) => (
                  <Col key={idx} xs={6} md={4} lg={2}>
                    <Card 
                      className="shadow-sm border-0 h-100 text-center"
                      style={{ borderTop: `4px solid ${productTypeColors[product.productType]}` }}
                    >
                      <Card.Body className="py-2 px-2">
                        <p className="small text-uppercase text-muted mb-1" style={{ fontSize: '10px' }}>
                          {product.productType}
                        </p>
                        <h5 className="mb-0 fw-bold" style={{ color: productTypeColors[product.productType], fontSize: '14px' }}>
                          {formatCurrency(product.cost)}
                        </h5>
                        <small className="text-muted d-block" style={{ fontSize: '10px' }}>
                          {formatNumber(product.impressions)} impr · {formatNumber(product.leads)} leads
                        </small>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Tab.Pane>
          )}

          {/* Tab Campañas (Árbol: Campaña → Ad Group → Ad) */}
          <Tab.Pane eventKey="campaigns">
            <Card className="shadow-sm border-0" style={{ overflow: 'visible' }}>
              <Card.Header className="bg-white border-bottom py-2 d-flex justify-content-between align-items-center" style={{ overflow: 'visible' }}>
                <h6 className="mb-0">Campañas · Ad Groups · Anuncios</h6>
                <div className="d-flex align-items-center gap-2">
                  <small className="text-muted">Clic en una fila para expandir</small>
                  <ChartExportMenu data={sortedCampaigns} filename="campanas_arbol"
                    treeData={buildTreeExportRows(sortedCampaigns, filteredAdGroupsForDisplay, filteredAdsForDisplay)}
                    columns={[
                    { key: 'campaignName', header: 'Campaña' }, { key: 'productType', header: 'Producto' },
                    { key: 'platform', header: 'Plataforma' }, { key: 'impressions', header: 'Impresiones' },
                    { key: 'clicks', header: 'Clicks' }, { key: 'ctr', header: 'CTR' },
                    { key: 'leads', header: 'Leads' }, { key: 'cpl', header: 'CPL' }, { key: 'cost', header: 'Inversión' },
                  ]} />
                </div>
              </Card.Header>
              <Card.Body className="p-0">
                <CampaignTreeTable
                  campaigns={sortedCampaigns}
                  adGroups={filteredAdGroupsForDisplay}
                  ads={filteredAdsForDisplay}
                  sortBy={sortBy}
                  onSort={handleSort}
                  filters={filterCampaigns}
                  onFilterChange={setFilterCampaigns}
                  isClaro={isClaro}
                />
              </Card.Body>
            </Card>
          </Tab.Pane>

          {/* Tab Ad Groups por Plataforma */}
          <Tab.Pane eventKey="adgroups-platform">
            <Card className="shadow-sm border-0" style={{ overflow: 'visible' }}>
              <Card.Header className="bg-white border-bottom py-2 d-flex justify-content-between align-items-center">
                <h6 className="mb-0">Ad Groups / Conjuntos de anuncio · agrupados por plataforma</h6>
                <ChartExportMenu
                  data={filteredAdGroupsForDisplay}
                  filename="adgroups_por_plataforma"
                  columns={[
                    { key: 'sourcePlatform', header: 'Plataforma' },
                    { key: 'adGroupName', header: 'Ad Group' },
                    { key: 'campaignName', header: 'Campaña' },
                    { key: 'impressions', header: 'Impresiones' },
                    { key: 'reach', header: 'Alcance' },
                    { key: 'clicks', header: 'Clicks' },
                    { key: 'ctr', header: 'CTR' },
                    { key: 'cpc', header: 'CPC' },
                    { key: 'leads', header: 'Leads' },
                    { key: 'cpl', header: 'CPL' },
                    { key: 'cost', header: 'Inversión' },
                  ]}
                />
              </Card.Header>
              <Card.Body className="p-0">
                <AdGroupsByPlatformTable adGroups={filteredAdGroupsForDisplay} />
              </Card.Body>
            </Card>
          </Tab.Pane>

          {/* Tab Demografía */}
          <Tab.Pane eventKey="demographics">
            <Row className="g-3 mb-2">
              <Col lg={12}>
                <div className="alert alert-info py-2 mb-0" style={{ fontSize: 12 }}>
                  <strong>ⓘ Solo Meta:</strong> El desglose por edad y publisher platform solo está disponible para Meta Ads. TikTok y Google Ads no exponen estos breakdowns en su API.
                </div>
              </Col>
            </Row>
            <Row className="g-3">
              <Col lg={6}>
                <Card className="shadow-sm border-0">
                  <Card.Header className="bg-white border-bottom py-2 d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">Distribución por Edad <small className="text-muted">· solo Meta</small></h6>
                    <ChartExportMenu data={data?.byAge} filename="demografia_edad" columns={[
                      { key: 'ageRange', header: 'Rango Edad' }, { key: 'impressions', header: 'Impresiones' },
                      { key: 'clicks', header: 'Clicks' }, { key: 'ctr', header: 'CTR' },
                      { key: 'cost', header: 'Inversión' }, { key: 'percentage', header: '%' },
                    ]} />
                  </Card.Header>
                  <Card.Body>
                    <AgeChart data={data?.byAge} />
                    {data?.byAge && (() => {
                      const filteredAge = (data.byAge || []).filter((a) => !filterDemographics.ageRange || (a.ageRange || '').toLowerCase().includes(filterDemographics.ageRange.toLowerCase()));
                      const sortedAge = [...filteredAge].sort((a, b) => {
                        const aVal = a[sortByDemographics.field] ?? 0;
                        const bVal = b[sortByDemographics.field] ?? 0;
                        return sortByDemographics.direction === 'asc' ? aVal - bVal : bVal - aVal;
                      });
                      return (
                      <Table size="sm" className="mt-3" style={{ fontSize: '12px' }}>
                        <thead className="bg-light">
                          <tr>
                            <th>Rango</th>
                            <SortHeader field="impressions" label="Impresiones" sortBy={sortByDemographics} onSort={(f) => setSortByDemographics((p) => ({ field: f, direction: p.field === f && p.direction === 'desc' ? 'asc' : 'desc' }))} />
                            <SortHeader field="clicks" label="Clicks" sortBy={sortByDemographics} onSort={(f) => setSortByDemographics((p) => ({ field: f, direction: p.field === f && p.direction === 'desc' ? 'asc' : 'desc' }))} />
                            <SortHeader field="cost" label="Inversión" sortBy={sortByDemographics} onSort={(f) => setSortByDemographics((p) => ({ field: f, direction: p.field === f && p.direction === 'desc' ? 'asc' : 'desc' }))} />
                          </tr>
                          <tr className="bg-white border-bottom">
                            <th style={{ padding: '4px 6px' }}><Form.Control size="sm" placeholder="Filtrar rango..." value={filterDemographics.ageRange || ''} onChange={(e) => setFilterDemographics((p) => ({ ...p, ageRange: e.target.value }))} style={{ fontSize: '11px' }} /></th>
                            <th colSpan={2} className="text-muted small" style={{ padding: '4px 8px', verticalAlign: 'middle' }}>{sortedAge.length} resultado(s)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedAge.map((a, i) => (
                            <tr key={i}>
                              <td>{a.ageRange}</td>
                              <td className="text-end">{formatNumber(a.impressions)}</td>
                              <td className="text-end">{formatNumber(a.clicks)}</td>
                              <td className="text-end">{formatCurrency(a.cost)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-light fw-bold">
                          <tr>
                            <td>TOTAL</td>
                            <td className="text-end">{formatNumber(sortedAge.reduce((s, a) => s + (a.impressions || 0), 0))}</td>
                            <td className="text-end">{formatNumber(sortedAge.reduce((s, a) => s + (a.clicks || 0), 0))}</td>
                            <td className="text-end">{formatCurrency(sortedAge.reduce((s, a) => s + (a.cost || 0), 0))}</td>
                          </tr>
                        </tfoot>
                      </Table>
                      );
                    })()}
                  </Card.Body>
                </Card>
              </Col>
              <Col lg={6}>
                <Card className="shadow-sm border-0">
                  <Card.Header className="bg-white border-bottom py-2 d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">Top Regiones/Provincias</h6>
                    <ChartExportMenu data={data?.byRegion} filename="top_regiones" columns={[
                      { key: 'region', header: 'Región' }, { key: 'impressions', header: 'Impresiones' },
                      { key: 'clicks', header: 'Clicks' }, { key: 'cost', header: 'Inversión' },
                      { key: 'leads', header: 'Leads' },
                    ]} />
                  </Card.Header>
                  <Card.Body>
                    <LocationChart data={data?.byLocation} />
                    {data?.byLocation && (
                      <Table size="sm" className="mt-3" style={{ fontSize: '12px' }}>
                        <thead>
                          <tr>
                            <th>Región</th>
                            <th className="text-end">Impresiones</th>
                            <th className="text-end">Clicks</th>
                            <th className="text-end">Inversión</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.byLocation.slice(0, 8).map((l, i) => (
                            <tr key={i}>
                              <td>{l.region}</td>
                              <td className="text-end">{formatNumber(l.impressions)}</td>
                              <td className="text-end">{formatNumber(l.clicks)}</td>
                              <td className="text-end">{formatCurrency(l.cost)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-light fw-bold">
                          <tr>
                            <td>TOTAL (Top 8)</td>
                            <td className="text-end">{formatNumber(data.byLocation.slice(0, 8).reduce((s, l) => s + (l.impressions || 0), 0))}</td>
                            <td className="text-end">{formatNumber(data.byLocation.slice(0, 8).reduce((s, l) => s + (l.clicks || 0), 0))}</td>
                            <td className="text-end">{formatCurrency(data.byLocation.slice(0, 8).reduce((s, l) => s + (l.cost || 0), 0))}</td>
                          </tr>
                        </tfoot>
                      </Table>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab.Pane>

          {/* Tab Plataformas */}
          <Tab.Pane eventKey="platforms">
            <Row className="g-3">
              <Col lg={8}>
                <Card className="shadow-sm border-0">
                  <Card.Header className="bg-white border-bottom py-2 d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">Rendimiento por Plataforma</h6>
                    <ChartExportMenu data={data?.byPlatform} filename="plataformas" columns={[
                      { key: 'platform', header: 'Plataforma' }, { key: 'impressions', header: 'Impresiones' },
                      { key: 'clicks', header: 'Clicks' }, { key: 'ctr', header: 'CTR' },
                      { key: 'videoViews', header: 'Video Views' }, { key: 'leads', header: 'Leads' },
                      { key: 'cost', header: 'Inversión' }, { key: 'percentage', header: '%' },
                    ]} />
                  </Card.Header>
                  <Card.Body>
                    <PlatformChart data={data?.byPlatform} />
                  </Card.Body>
                </Card>
              </Col>
              <Col lg={4}>
                <Card className="shadow-sm border-0">
                  <Card.Header className="bg-white border-bottom py-2 d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">Detalle por Plataforma</h6>
                    <ChartExportMenu data={data?.byPlatform} filename="detalle_plataforma" columns={[
                      { key: 'platform', header: 'Plataforma' }, { key: 'impressions', header: 'Impresiones' },
                      { key: 'clicks', header: 'Clicks' }, { key: 'cost', header: 'Inversión' },
                      { key: 'leads', header: 'Leads' }, { key: 'percentage', header: '%' },
                    ]} />
                  </Card.Header>
                  <Card.Body className="p-0">
                    {data?.byPlatform && (() => {
                      const filteredPlat = (data.byPlatform || []).filter((p) => !filterPlatforms.platform || (p.platform || '').toLowerCase().includes(filterPlatforms.platform.toLowerCase()));
                      const sortedPlat = [...filteredPlat].sort((a, b) => {
                        const aVal = a[sortByPlatforms.field] ?? 0;
                        const bVal = b[sortByPlatforms.field] ?? 0;
                        return sortByPlatforms.direction === 'asc' ? aVal - bVal : bVal - aVal;
                      });
                      return (
                      <Table size="sm" className="mb-0" style={{ fontSize: '12px' }}>
                        <thead className="bg-light">
                          <tr>
                            <th>Plataforma</th>
                            <th>Dispositivo</th>
                            <SortHeader field="impressions" label="Impr." sortBy={sortByPlatforms} onSort={(f) => setSortByPlatforms((p) => ({ field: f, direction: p.field === f && p.direction === 'desc' ? 'asc' : 'desc' }))} />
                            <SortHeader field="cost" label="Gasto" sortBy={sortByPlatforms} onSort={(f) => setSortByPlatforms((p) => ({ field: f, direction: p.field === f && p.direction === 'desc' ? 'asc' : 'desc' }))} />
                          </tr>
                          <tr className="bg-white border-bottom">
                            <th style={{ padding: '4px 6px' }}><Form.Control size="sm" placeholder="Filtrar..." value={filterPlatforms.platform || ''} onChange={(e) => setFilterPlatforms((p) => ({ ...p, platform: e.target.value }))} style={{ fontSize: '11px' }} /></th>
                            <th colSpan={2} className="text-muted small" style={{ padding: '4px 8px', verticalAlign: 'middle' }}>{sortedPlat.length} resultado(s)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedPlat.map((p, i) => (
                            <tr key={i}>
                              <td>
                                <Badge bg={p.platform === 'facebook' ? 'primary' : p.platform === 'instagram' ? 'danger' : 'secondary'}>
                                  {p.platform}
                                </Badge>
                              </td>
                              <td className="text-muted">{p.device}</td>
                              <td className="text-end">{formatNumber(p.impressions)}</td>
                              <td className="text-end">{formatCurrency(p.cost)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-light fw-bold">
                          <tr>
                            <td>TOTAL</td><td></td>
                            <td className="text-end">{formatNumber(sortedPlat.reduce((s, p) => s + (p.impressions || 0), 0))}</td>
                            <td className="text-end">{formatCurrency(sortedPlat.reduce((s, p) => s + (p.cost || 0), 0))}</td>
                          </tr>
                        </tfoot>
                      </Table>
                      );
                    })()}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab.Pane>

          {/* Tab Geografía */}
          <Tab.Pane eventKey="geography">
            <Row className="g-3 mb-2">
              <Col lg={12}>
                <div className="alert alert-info py-2 mb-0" style={{ fontSize: 12 }}>
                  <strong>ⓘ Solo Meta:</strong> El desglose por región solo está disponible para Meta Ads. TikTok y Google Ads no exponen breakdowns geográficos en su API. Por eso los totales aquí pueden ser menores al total general (que sí incluye todas las plataformas).
                </div>
              </Col>
            </Row>
            <Row className="g-3">
              <Col lg={8}>
                <Card className="shadow-sm border-0 h-100">
                  <Card.Header className="bg-white border-bottom py-2 d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">Métricas por Región/Provincia <small className="text-muted">· solo Meta</small></h6>
                    <div className="d-flex align-items-center gap-2">
                    <ChartExportMenu data={data?.byRegion} filename="geografia" columns={[
                      { key: 'region', header: 'Región' }, { key: 'impressions', header: 'Impresiones' },
                      { key: 'clicks', header: 'Clicks' }, { key: 'cost', header: 'Inversión' },
                      { key: 'leads', header: 'Leads' }, { key: 'ctr', header: 'CTR' },
                    ]} />
                    <div className="btn-group btn-group-sm">
                      {[
                        { key: 'impressions', label: 'Impresiones' },
                        { key: 'clicks', label: 'Clicks' },
                        { key: 'cost', label: 'Inversión' },
                        { key: 'leads', label: 'Leads' },
                      ].map(m => (
                        <Button 
                          key={m.key}
                          variant={geoMetric === m.key ? 'primary' : 'outline-secondary'}
                          onClick={() => setGeoMetric(m.key)}
                          size="sm"
                        >
                          {m.label}
                        </Button>
                      ))}
                    </div>
                    </div>
                  </Card.Header>
                  <Card.Body>
                    <RegionMapChart data={data?.byLocation} selectedMetric={geoMetric} />
                  </Card.Body>
                </Card>
              </Col>
              <Col lg={4}>
                <Card className="shadow-sm border-0 h-100">
                  <Card.Header className="bg-white border-bottom py-2 d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">Top 10 Regiones</h6>
                    <ChartExportMenu data={data?.byRegion?.slice(0, 10)} filename="top10_regiones" columns={[
                      { key: 'region', header: 'Región' }, { key: 'impressions', header: 'Impresiones' },
                      { key: 'clicks', header: 'Clicks' }, { key: 'cost', header: 'Inversión' }, { key: 'leads', header: 'Leads' },
                    ]} />
                  </Card.Header>
                  <Card.Body className="p-0">
                    <Table hover size="sm" className="mb-0" style={{ fontSize: '12px' }}>
                      <thead className="bg-light">
                        <tr>
                          <th>#</th>
                          <th>Región</th>
                          <th className="text-end">
                            {geoMetric === 'impressions' && 'Impr.'}
                            {geoMetric === 'clicks' && 'Clicks'}
                            {geoMetric === 'cost' && 'Inversión'}
                            {geoMetric === 'leads' && 'Leads'}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {data?.byLocation
                          ?.sort((a, b) => (b[geoMetric] || 0) - (a[geoMetric] || 0))
                          .slice(0, 10)
                          .map((l, i) => (
                            <tr key={i}>
                              <td><Badge bg="secondary" pill>{i + 1}</Badge></td>
                              <td className="text-truncate" style={{ maxWidth: '150px' }}>{l.region || 'Desc.'}</td>
                              <td className="text-end fw-bold">
                                {geoMetric === 'cost' ? formatCurrency(l[geoMetric]) : formatNumber(l[geoMetric] || 0)}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
            <Row className="g-3 mt-3">
              <Col lg={12}>
                <Card className="shadow-sm border-0">
                  <Card.Header className="bg-white border-bottom py-2 d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">Detalle Completo por Ubicación</h6>
                    <ChartExportMenu data={data?.byRegion} filename="detalle_ubicacion" columns={[
                      { key: 'region', header: 'Ubicación' }, { key: 'impressions', header: 'Impresiones' },
                      { key: 'clicks', header: 'Clicks' }, { key: 'ctr', header: 'CTR' },
                      { key: 'cost', header: 'Inversión' }, { key: 'leads', header: 'Leads' },
                    ]} />
                  </Card.Header>
                  <Card.Body className="p-0">
                    {(() => {
                      const filteredGeo = (data?.byLocation || []).filter((l) => !filterGeo.region || (l.region || '').toLowerCase().includes(filterGeo.region.toLowerCase()));
                      const sortedGeo = [...filteredGeo].sort((a, b) => {
                        const aVal = a[sortByGeo.field] ?? 0;
                        const bVal = b[sortByGeo.field] ?? 0;
                        return sortByGeo.direction === 'asc' ? aVal - bVal : bVal - aVal;
                      });
                      return (
                    <Table hover responsive className="mb-0" style={{ fontSize: '12px' }}>
                      <thead className="bg-light">
                        <tr>
                          <th style={{ width: '30%' }}>Región/Provincia</th>
                          <SortHeader field="impressions" label="Impresiones" sortBy={sortByGeo} onSort={(f) => setSortByGeo((p) => ({ field: f, direction: p.field === f && p.direction === 'desc' ? 'asc' : 'desc' }))} />
                          <SortHeader field="clicks" label="Clicks" sortBy={sortByGeo} onSort={(f) => setSortByGeo((p) => ({ field: f, direction: p.field === f && p.direction === 'desc' ? 'asc' : 'desc' }))} />
                          <th className="text-end">CTR</th>
                          <SortHeader field="leads" label="Leads" sortBy={sortByGeo} onSort={(f) => setSortByGeo((p) => ({ field: f, direction: p.field === f && p.direction === 'desc' ? 'asc' : 'desc' }))} />
                          <SortHeader field="cost" label="Inversión" sortBy={sortByGeo} onSort={(f) => setSortByGeo((p) => ({ field: f, direction: p.field === f && p.direction === 'desc' ? 'asc' : 'desc' }))} />
                          <th className="text-end">CPM</th>
                          <th className="text-end">CPC</th>
                        </tr>
                        <tr className="bg-white border-bottom">
                          <th style={{ padding: '4px 6px' }}><Form.Control size="sm" placeholder="Filtrar región..." value={filterGeo.region || ''} onChange={(e) => setFilterGeo((p) => ({ ...p, region: e.target.value }))} style={{ fontSize: '11px' }} /></th>
                          <th colSpan={7} className="text-muted small" style={{ padding: '4px 8px', verticalAlign: 'middle' }}>Filtros · {sortedGeo.length} resultado(s)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedGeo.map((l, i) => {
                          const ctr = l.impressions > 0 ? (l.clicks / l.impressions * 100) : 0;
                          const cpm = l.impressions > 0 ? (l.cost / l.impressions * 1000) : 0;
                          const cpc = l.clicks > 0 ? (l.cost / l.clicks) : 0;
                          return (
                            <tr key={i}>
                              <td>
                                <div className="d-flex align-items-center">
                                  <div 
                                    className="me-2" 
                                    style={{ 
                                      width: `${Math.min(80, (l[geoMetric] / (sortedGeo[0]?.[geoMetric] || 1)) * 80)}px`,
                                      height: '6px',
                                      backgroundColor: geoMetric === 'cost' ? '#e63757' : geoMetric === 'clicks' ? '#00d97e' : '#2c7be5',
                                      borderRadius: '3px',
                                      minWidth: '3px'
                                    }} 
                                  />
                                  <span>{l.region || 'Desconocido'}</span>
                                </div>
                              </td>
                              <td className="text-end">{formatNumber(l.impressions)}</td>
                              <td className="text-end">{formatNumber(l.clicks)}</td>
                              <td className="text-end">
                                <Badge bg={ctr > 1 ? 'success' : ctr > 0.5 ? 'warning' : 'secondary'} className="px-2">
                                  {formatPercent(ctr)}
                                </Badge>
                              </td>
                              <td className="text-end">{formatNumber(l.leads || 0)}</td>
                              <td className="text-end fw-bold">{formatCurrency(l.cost)}</td>
                              <td className="text-end">{formatCurrency(cpm)}</td>
                              <td className="text-end">{formatCurrency(cpc)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot className="bg-light fw-bold">
                        {(() => {
                          const tImpr = sortedGeo.reduce((s, l) => s + (l.impressions || 0), 0);
                          const tClicks = sortedGeo.reduce((s, l) => s + (l.clicks || 0), 0);
                          const tLeads = sortedGeo.reduce((s, l) => s + (l.leads || 0), 0);
                          const tCost = sortedGeo.reduce((s, l) => s + (l.cost || 0), 0);
                          return (
                            <tr>
                              <td>TOTAL ({sortedGeo.length} regiones)</td>
                              <td className="text-end">{formatNumber(tImpr)}</td>
                              <td className="text-end">{formatNumber(tClicks)}</td>
                              <td className="text-end"><Badge bg={tImpr > 0 && (tClicks/tImpr*100) > 1 ? 'success' : 'warning'}>{formatPercent(tImpr > 0 ? tClicks/tImpr*100 : 0)}</Badge></td>
                              <td className="text-end">{formatNumber(tLeads)}</td>
                              <td className="text-end">{formatCurrency(tCost)}</td>
                              <td className="text-end">{formatCurrency(tImpr > 0 ? tCost / (tImpr / 1000) : 0)}</td>
                              <td className="text-end">{formatCurrency(tClicks > 0 ? tCost / tClicks : 0)}</td>
                            </tr>
                          );
                        })()}
                      </tfoot>
                    </Table>
                      );
                    })()}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab.Pane>

          {/* Tab Objetivos */}
          <Tab.Pane eventKey="objectives">
            <ObjectivesSummaryWidget
              clientId={clientId}
              dateFrom={toLocalDateString(dateFrom)}
              dateTo={toLocalDateString(dateTo)}
              showTrend={true}
              maxCards={6}
            />
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>

      {/* Footer */}
      <div className="text-center mt-3">
        <small className="text-muted">
          ⏱️ Procesado en {data?.meta?.processingTimeMs || 0}ms · {data?.summary?.totalCampaigns || 0} campañas · {data?.summary?.totalAdGroups || 0} ad groups · {data?.summary?.totalAds || 0} anuncios · {data?.summary?.totalAccounts || 0} cuentas
        </small>
      </div>
    </Container>
  );
};

export default CampaignDashboard;
